import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Check, ChevronLeft, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { backendPalette } from '../../components/backend/BackendCardSystem';
import PortalAvatar from '../../components/profile/PortalAvatar';
import { apiFetch } from '../../lib/api';
import GlassPicklist, { type GlassPicklistOption } from '../../components/ui/GlassPicklist';
import {
  computeEffectiveOfferingSla,
  normalizeOfferingCode,
  normalizeOfferingSlaPolicy,
  normalizeServiceSlaPolicy,
  normalizeServiceOfferingCatalog,
  normalizeServiceOfferingType,
  normalizeTierOfferingSlaOverrides,
  parseNonNegativeIntOrNull,
  toInputValue,
} from '../../lib/services/offeringSla';
import {
  type EscalationScope,
  type ServiceAddOnCatalogEntry,
  type ServiceAddOnVariationCatalogEntry,
  type ServiceOfferingSubcategoryCatalogEntry,
  type ServiceSlaPolicy,
  type ServiceOfferingType,
  type TierOfferingSlaOverride,
} from '../../lib/services/serviceCatalogTypes';

type CatalogStatus = 'draft' | 'published' | 'archived';

type ServiceOffering = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  status: CatalogStatus;
  metadata: Record<string, unknown>;
};

type ServiceTier = {
  id: string;
  skuId: string;
  name: string;
  description: string;
  billingType?: string | null;
  billingInterval?: string | null;
  amountCents: number;
  currency?: string | null;
  isPrimaryService?: boolean;
  sortOrder?: number | null;
  status: CatalogStatus;
  metadata?: Record<string, unknown>;
  offerings: ServiceOffering[];
};

type ServiceCatalog = {
  id: string;
  code: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  teaser: string | null;
  checkoutEnabled: boolean;
  status: CatalogStatus;
  metadata?: Record<string, unknown>;
  tiers: ServiceTier[];
};

type AdminCatalogResponse = {
  services: ServiceCatalog[];
};

type WorkCadence = 'one_time' | 'recurring';

type AdditionalTierTeam = {
  id: string;
  designationPathIds: string[];
  designationType: string;
  brainNodeId: string;
  owner: string;
  cadence: WorkCadence;
  monthlyHoursAllocated: string;
  billableHours: string;
  notes: string;
};

type OrganizationTreeNodeResponse = {
  id: string;
  parentId: string | null;
  title: string;
  status: 'active' | 'archived';
  meta?: Record<string, unknown>;
  rules?: Record<string, unknown>;
};

type OrganizationTreeResponse = {
  orgId: string;
  nodes: OrganizationTreeNodeResponse[];
};

type BrainNodeOption = {
  id: string;
  parentId: string | null;
  title: string;
  designationType: string;
  teamLabel: string;
  rules?: Record<string, unknown>;
  appointedAgent: string;
  appointedAgentUid: string;
  appointedAgentEmail: string;
  appointedAgentAvatarUrl: string;
  status: 'active' | 'archived';
};

type BrainRoutingAssignment = {
  nodeId: string;
  serviceCode: string;
  tierSkuId: string | null;
  nodeTitle: string;
  designationType: string;
  teamLabel: string;
  owner: string;
  ownerUid: string;
  ownerEmail: string;
  ownerAvatarUrl: string;
};

type ServiceOfferingSubcategoryTemplate = ServiceOfferingSubcategoryCatalogEntry;

type ServiceOfferingTemplate = {
  code: string;
  name: string;
  description: string;
  status: CatalogStatus;
  sortOrder: number;
  type: ServiceOfferingType;
  addOnEnabled: boolean;
  unitPriceCents: number | null;
  subcategories: ServiceOfferingSubcategoryTemplate[];
  sla: {
    responseTargetMinutes: number | null;
    resolutionTargetMinutes: number | null;
    targetCompletionMinutes: number | null;
    escalationAfterMinutes: number | null;
    escalationScope: EscalationScope | null;
    queueWeight: number | null;
    businessHoursOnly: boolean | null;
  };
};

type ServiceExclusionTemplate = {
  code: string;
  name: string;
  description: string;
  status: CatalogStatus;
  sortOrder: number;
};

type ServiceAddOnVariationTemplate = ServiceAddOnVariationCatalogEntry;

type ServiceAddOnTemplate = ServiceAddOnCatalogEntry;

type TierOfferingOverride = {
  incentive: string;
};

type TierAddOnPreset =
  | 'additional_account_management_meeting'
  | 'additional_core_lane'
  | 'additional_advanced_lane'
  | 'additional_specialty_lane';

type TierAddOnConfig = {
  enabled: boolean;
  unitPriceCents: number | null;
  maxQuantity: number | null;
  unitsPerAddOn: number | null;
  laneOfferingCodes: string[];
};

type TierCustomAddOn = {
  code: string;
  name: string;
  description: string;
  unitPriceCents: number | null;
  sortOrder: number;
};

type TierAddOnSettings = {
  maxAddOnsPerTier: number | null;
  presets: Record<TierAddOnPreset, TierAddOnConfig>;
  customAddOns: TierCustomAddOn[];
};

type ServiceAddOnVariationDraft = {
  parentCode: string | null;
  code: string | null;
  name: string;
  description: string;
  price: string;
};

type DiscountMode = 'amount' | 'percent' | 'free' | 'duration';
type DiscountDurationValueMode = 'amount' | 'percent' | 'free';

type DiscountFormItem = {
  id: string;
  code: string;
  name: string;
  mode: DiscountMode;
  amountUsd: string;
  percentOff: string;
  durationValueMode: DiscountDurationValueMode;
  durationMonths: string;
  durationAmountUsd: string;
  durationPercentOff: string;
  expiresAt: string;
  noExpiration: boolean;
  inventoryLimit: string;
  unlimitedInventory: boolean;
  usageCount: number;
};

type SavedDiscountMetadata = {
  id: string;
  code: string;
  name: string;
  mode: DiscountMode;
  amountOffCents: number | null;
  percentOff: number | null;
  durationValueMode: DiscountDurationValueMode | null;
  durationMonths: number | null;
  expiresAt: string | null;
  inventoryLimit: number | null;
  usageCount: number;
};

type TierOfferingSlaFormState = {
  responseTargetMinutes: string;
  resolutionTargetMinutes: string;
  targetCompletionMinutes: string;
  escalationAfterMinutes: string;
  escalationScope: EscalationScope;
  queueWeight: string;
};

type TierSlaBlockKey = 'offerings' | 'add_ons' | 'ticketing';

type TierSlaBlockDrafts = Record<TierSlaBlockKey, TierOfferingSlaFormState>;

type ServiceSlaPoliciesByBlock = Record<TierSlaBlockKey, ServiceSlaPolicy>;

type ClientSuccessCadenceType = 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

const STATUS_OPTIONS: CatalogStatus[] = ['draft', 'published', 'archived'];
const OFFERING_TYPE_OPTIONS: readonly ServiceOfferingType[] = ['core', 'advanced', 'specialty'];
const OFFERING_TYPE_PICKLIST_OPTIONS: ReadonlyArray<GlassPicklistOption<ServiceOfferingType>> =
  OFFERING_TYPE_OPTIONS.map((type) => ({
    value: type,
    label: `${type.charAt(0).toUpperCase()}${type.slice(1)}`,
  }));
const CLIENT_SUCCESS_CADENCE_OPTIONS: readonly ClientSuccessCadenceType[] = [
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
];
const ESCALATION_SCOPE_OPTIONS: ReadonlyArray<GlassPicklistOption<EscalationScope>> = [
  { value: 'response', label: 'First response SLA' },
  { value: 'resolution', label: 'Resolution SLA' },
  { value: 'both', label: 'Both response and resolution SLA' },
];
const QUEUE_WEIGHT_OPTIONS: ReadonlyArray<GlassPicklistOption<string>> = [
  { value: '', label: 'Select priority' },
  { value: '100', label: 'Critical (P1)' },
  { value: '75', label: 'High (P2)' },
  { value: '50', label: 'Standard (P3)' },
  { value: '25', label: 'Low (P4)' },
  { value: '10', label: 'Backlog (P5)' },
];
const DISCOUNT_MODE_PICKLIST_OPTIONS: ReadonlyArray<GlassPicklistOption<DiscountMode>> = [
  { value: 'amount', label: 'Dollar amount' },
  { value: 'percent', label: 'Percentage' },
  { value: 'free', label: 'Free' },
  { value: 'duration', label: 'Duration (first months)' },
];
const DISCOUNT_DURATION_VALUE_PICKLIST_OPTIONS: ReadonlyArray<
  GlassPicklistOption<DiscountDurationValueMode>
> = [
  { value: 'free', label: 'Free for duration' },
  { value: 'amount', label: 'Dollar off for duration' },
  { value: 'percent', label: 'Percent off for duration' },
];

const TIER_ADD_ON_PRESETS: readonly TierAddOnPreset[] = [
  'additional_account_management_meeting',
  'additional_core_lane',
  'additional_advanced_lane',
  'additional_specialty_lane',
];

function normalizeDiscountCode(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function moneyInputToCents(value: string): number | null {
  const trimmed = value.trim().replace(/[$,\s]/g, '');
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100);
}

function centsToMoneyInput(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '';
  return (value / 100).toFixed(2);
}

function parseNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function createNewDiscountFormItem(index: number): DiscountFormItem {
  const code = `DISCOUNT_${index + 1}`;
  return {
    id: `discount-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code,
    name: `Discount ${index + 1}`,
    mode: 'amount',
    amountUsd: '',
    percentOff: '',
    durationValueMode: 'free',
    durationMonths: '1',
    durationAmountUsd: '',
    durationPercentOff: '',
    expiresAt: '',
    noExpiration: true,
    inventoryLimit: '',
    unlimitedInventory: true,
    usageCount: 0,
  };
}

function readDiscountFormItems(metadata?: Record<string, unknown>): DiscountFormItem[] {
  const raw = metadata?.discounts;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
      const item = entry as Record<string, unknown>;
      const rawMode = String(item.mode ?? item.valueType ?? '').trim().toLowerCase();
      const freeFlag = item.free === true || rawMode === 'free';
      const mode: DiscountMode = freeFlag
        ? 'free'
        : rawMode === 'duration'
          ? 'duration'
        : rawMode === 'percent' || rawMode === 'percentage'
          ? 'percent'
          : 'amount';
      const rawDurationValueMode = String(item.durationValueMode ?? item.durationType ?? '')
        .trim()
        .toLowerCase();
      const durationValueMode: DiscountDurationValueMode =
        rawDurationValueMode === 'amount' || rawDurationValueMode === 'dollar'
          ? 'amount'
          : rawDurationValueMode === 'percent' || rawDurationValueMode === 'percentage'
            ? 'percent'
            : 'free';
      const durationMonthsRaw =
        typeof item.durationMonths === 'number'
          ? item.durationMonths
          : typeof item.durationInMonths === 'number'
            ? item.durationInMonths
            : typeof item.months === 'number'
              ? item.months
              : Number.parseInt(String(item.durationMonths ?? item.durationInMonths ?? item.months ?? ''), 10);
      const durationMonths =
        Number.isFinite(durationMonthsRaw) && durationMonthsRaw > 0
          ? String(Math.floor(durationMonthsRaw))
          : '1';
      const code = normalizeDiscountCode(String(item.code ?? item.name ?? `DISCOUNT_${index + 1}`));
      const name = String(item.name ?? code).trim() || `Discount ${index + 1}`;
      const amountOffCents =
        typeof item.amountOffCents === 'number'
          ? item.amountOffCents
          : typeof item.amountCents === 'number'
            ? item.amountCents
            : typeof item.valueCents === 'number'
              ? item.valueCents
              : null;
      const percentOff =
        typeof item.percentOff === 'number'
          ? item.percentOff
          : typeof item.valuePercent === 'number'
            ? item.valuePercent
            : typeof item.percentage === 'number'
              ? item.percentage
              : typeof item.value === 'number' && mode === 'percent'
                ? item.value
                : null;
      const expiresAt =
        typeof item.expiresAt === 'string' && item.expiresAt.trim()
          ? item.expiresAt.trim().slice(0, 10)
          : '';
      const inventoryLimit =
        typeof item.inventoryLimit === 'number'
          ? item.inventoryLimit
          : typeof item.inventoryLevel === 'number'
            ? item.inventoryLevel
            : null;
      const usageCount =
        typeof item.usageCount === 'number' && Number.isFinite(item.usageCount)
          ? Math.max(0, Math.floor(item.usageCount))
          : 0;
      return {
        id: String(item.id ?? `discount-${index}`),
        code,
        name,
        mode,
        amountUsd: mode === 'amount' ? centsToMoneyInput(amountOffCents) : '',
        percentOff:
          mode === 'percent' && typeof percentOff === 'number' && Number.isFinite(percentOff)
            ? String(percentOff)
            : '',
        durationValueMode,
        durationMonths,
        durationAmountUsd:
          mode === 'duration' && durationValueMode === 'amount'
            ? centsToMoneyInput(amountOffCents)
            : '',
        durationPercentOff:
          mode === 'duration' && durationValueMode === 'percent' && typeof percentOff === 'number'
            ? String(percentOff)
            : '',
        expiresAt,
        noExpiration: !expiresAt,
        inventoryLimit:
          typeof inventoryLimit === 'number' && Number.isFinite(inventoryLimit)
            ? String(Math.max(0, Math.floor(inventoryLimit)))
            : '',
        unlimitedInventory: !(typeof inventoryLimit === 'number' && Number.isFinite(inventoryLimit)),
        usageCount,
      } satisfies DiscountFormItem;
    })
    .filter((item): item is DiscountFormItem => Boolean(item));
}

function serializeDiscountFormItems(items: DiscountFormItem[]): SavedDiscountMetadata[] {
  return items
    .map((item) => {
      const code = normalizeDiscountCode(item.code || item.name);
      const name = item.name.trim();
      if (!code || !name) return null;
      const mode = item.mode;
      const durationValueMode: DiscountDurationValueMode =
        item.durationValueMode === 'amount' || item.durationValueMode === 'percent'
          ? item.durationValueMode
          : 'free';
      const durationMonthsRaw = Number.parseInt(item.durationMonths.trim(), 10);
      const durationMonths =
        mode === 'duration' && Number.isFinite(durationMonthsRaw) && durationMonthsRaw > 0
          ? Math.max(1, Math.floor(durationMonthsRaw))
          : null;
      let amountOffCents: number | null = null;
      let percentOff: number | null = null;
      if (mode === 'amount') {
        amountOffCents = moneyInputToCents(item.amountUsd);
      } else if (mode === 'percent') {
        const percentRaw = Number.parseFloat(item.percentOff.trim());
        percentOff =
          Number.isFinite(percentRaw) && percentRaw > 0
            ? Math.min(100, percentRaw)
            : null;
      } else if (mode === 'duration') {
        if (durationValueMode === 'amount') {
          amountOffCents = moneyInputToCents(item.durationAmountUsd);
        } else if (durationValueMode === 'percent') {
          const percentRaw = Number.parseFloat(item.durationPercentOff.trim());
          percentOff =
            Number.isFinite(percentRaw) && percentRaw > 0
              ? Math.min(100, percentRaw)
              : null;
        }
      }
      if (mode === 'amount' && !amountOffCents) return null;
      if (mode === 'percent' && !percentOff) return null;
      if (mode === 'duration' && !durationMonths) return null;
      if (mode === 'duration' && durationValueMode === 'amount' && !amountOffCents) return null;
      if (mode === 'duration' && durationValueMode === 'percent' && !percentOff) return null;
      const inventoryLimit = item.unlimitedInventory ? null : parseNumberOrNull(item.inventoryLimit);
      return {
        id: item.id,
        code,
        name,
        mode,
        amountOffCents:
          mode === 'amount' || (mode === 'duration' && durationValueMode === 'amount')
            ? amountOffCents
            : null,
        percentOff:
          mode === 'percent' || (mode === 'duration' && durationValueMode === 'percent')
            ? percentOff
            : null,
        durationValueMode: mode === 'duration' ? durationValueMode : null,
        durationMonths,
        expiresAt: item.noExpiration ? null : item.expiresAt.trim() || null,
        inventoryLimit,
        usageCount: Math.max(0, Math.floor(item.usageCount || 0)),
      } satisfies SavedDiscountMetadata;
    })
    .filter((item): item is SavedDiscountMetadata => Boolean(item));
}

function minutesToHoursInput(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const hours = value / 60;
  return Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(2)));
}

function hoursInputToMinutes(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 60);
}

function queueWeightLabel(value: string): string {
  const matched = QUEUE_WEIGHT_OPTIONS.find((option) => option.value === value);
  return matched?.label ?? '--';
}

function createDefaultTierSlaFormState(): TierOfferingSlaFormState {
  return {
    responseTargetMinutes: '',
    resolutionTargetMinutes: '',
    targetCompletionMinutes: '',
    escalationAfterMinutes: '',
    escalationScope: 'both',
    queueWeight: '',
  };
}

function createDefaultTierSlaBlockDrafts(): TierSlaBlockDrafts {
  return {
    offerings: createDefaultTierSlaFormState(),
    add_ons: createDefaultTierSlaFormState(),
    ticketing: createDefaultTierSlaFormState(),
  };
}

function toTierSlaOverrideFromDraft(draft: TierOfferingSlaFormState): TierOfferingSlaOverride {
  const nextOverride: TierOfferingSlaOverride = {
    responseTargetMinutes: hoursInputToMinutes(draft.responseTargetMinutes),
    resolutionTargetMinutes: hoursInputToMinutes(draft.resolutionTargetMinutes),
    targetCompletionMinutes: hoursInputToMinutes(draft.targetCompletionMinutes),
    escalationAfterMinutes: hoursInputToMinutes(draft.escalationAfterMinutes),
    escalationScope: draft.escalationScope,
    queueWeight: parseNonNegativeIntOrNull(draft.queueWeight),
  };
  return nextOverride;
}

function hasTierSlaOverrideValues(override: TierOfferingSlaOverride): boolean {
  return (
    typeof override.responseTargetMinutes === 'number' ||
    typeof override.resolutionTargetMinutes === 'number' ||
    typeof override.targetCompletionMinutes === 'number' ||
    typeof override.escalationAfterMinutes === 'number' ||
    typeof override.escalationScope === 'string' ||
    typeof override.queueWeight === 'number'
  );
}

function readTierTicketingSlaOverride(metadata?: Record<string, unknown>): TierOfferingSlaOverride {
  return normalizeOfferingSlaPolicy(metadata?.ticketingSla);
}

const slideCardLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(120px) scale(1.02);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Page = styled.section`
  display: grid;
  gap: 12px;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  height: calc(100dvh - 2.25rem);
  min-height: calc(100dvh - 2.25rem);
  overflow: hidden;
  position: relative;
`;

const ShellHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 2px 2px 0;
`;

const HeaderText = styled.div`
  display: grid;
  gap: 5px;
`;

const Title = styled.h1`
  margin: 0;
  color: ${backendPalette.yellow};
  font-size: clamp(2rem, 4vw, 2.8rem);
  line-height: 1;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.74);
  font-size: 0.9rem;
  font-weight: 700;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $ghost?: boolean }>`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: ${({ $ghost }) => ($ghost ? 'transparent' : 'rgba(224, 161, 94, 0.2)')};
  color: rgba(245, 247, 252, 0.92);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  padding: 0.48rem 0.72rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToastStack = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 12;
  display: grid;
  gap: 8px;
  width: min(520px, calc(100% - 20px));
  pointer-events: none;
`;

const Toast = styled.div<{ $error?: boolean }>`
  border-radius: 10px;
  border: 1px solid ${({ $error }) => ($error ? 'rgba(255, 126, 126, 0.62)' : 'rgba(224, 161, 94, 0.56)')};
  background: ${({ $error }) => ($error ? 'rgba(166, 76, 68, 0.9)' : 'rgba(36, 29, 24, 0.96)')};
  color: rgba(245, 247, 252, 0.96);
  padding: 10px 12px;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.35;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
`;

const Shell = styled.section`
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  min-height: 0;
  display: grid;
  gap: 12px;
  grid-template-rows: auto minmax(0, 1fr);
`;

const InitialStage = styled.section`
  min-height: 0;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 12px;
`;

const CardsGrid = styled.section<{ $centered?: boolean }>`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, 220px);
  justify-content: ${({ $centered }) => ($centered ? 'center' : 'start')};
  align-content: ${({ $centered }) => ($centered ? 'center' : 'start')};
  width: 100%;

  @media (max-width: 1550px) {
    grid-template-columns: repeat(auto-fit, 210px);
  }

  @media (max-width: 680px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
`;

const ServiceCard = styled.button<{ $active?: boolean; $fill?: boolean }>`
  border: none;
  border-radius: 14px;
  background: ${backendPalette.yellow};
  color: ${backendPalette.black};
  width: ${({ $fill }) => ($fill ? '100%' : '220px')};
  aspect-ratio: ${({ $fill }) => ($fill ? 'auto' : '5 / 7')};
  min-height: ${({ $fill }) => ($fill ? '100%' : '308px')};
  height: ${({ $fill }) => ($fill ? '100%' : 'auto')};
  text-align: left;
  padding: 12px;
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  gap: 7px;
  box-shadow: ${({ $active }) =>
    $active ? '0 0 0 2px rgba(255, 255, 255, 0.42)' : '0 10px 22px rgba(0, 0, 0, 0.24)'};
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const AddServiceCard = styled.button`
  border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.5);
  background: transparent;
  color: rgba(245, 247, 252, 0.92);
  width: 220px;
  aspect-ratio: 5 / 7;
  min-height: 308px;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const AddCircle = styled.span`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px dashed rgba(255, 255, 255, 0.56);
  display: grid;
  place-items: center;
  color: ${backendPalette.green};
  font-size: 1.4rem;
  line-height: 1;
`;

const AddLabel = styled.span`
  font-size: 0.92rem;
  font-weight: 900;
  letter-spacing: 0.02em;
`;

const AddSub = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(219, 227, 247, 0.76);
`;

const ServiceCode = styled.span`
  justify-self: start;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.24);
  padding: 0.15rem 0.44rem;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const ServiceTitle = styled.h3`
  margin: 0;
  font-size: 1.06rem;
  font-weight: 900;
  line-height: 1.14;
`;

const ServiceTeaser = styled.p`
  margin: 0;
  font-size: 0.79rem;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(14, 14, 14, 0.84);
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const MetaChip = styled.span`
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.24);
  padding: 0.12rem 0.44rem;
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Workspace = styled.section`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 12px;
  height: 100%;
  min-height: 0;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const LeftRail = styled.aside`
  border-radius: 14px;
  // border: 1px solid rgba(255, 255, 255, 0.2);
  // background: rgba(10, 14, 32, 0.58);
  // padding: 10px;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  align-content: stretch;
  min-height: 0;
  gap: 10px;
  animation: ${slideCardLeft} 240ms ease both;
`;

const BackButton = styled.button`
  border-radius: 10px;
  background: #969696;
  color: #1a1a1a;
  height: 36px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  font-weight: 800;
  cursor: pointer;
`;

const SelectedCardShell = styled.div`
  border-radius: 12px;
  overflow: auto;
  min-height: 0;
  height: 100%;
  animation: ${slideCardLeft} 280ms ease both;

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.24);
    border-radius: 999px;
  }
`;

const SummaryPanel = styled.section`
  min-height: 100%;
  border-radius: 12px;
  background: #e0a15e;
  padding: 12px;
  display: grid;
  align-content: start;
  gap: 14px;
`;

const SummaryHeader = styled.div`
  display: grid;
  gap: 4px;
`;

const SummaryEyebrow = styled.span`
  color: rgba(34, 21, 10, 0.66);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const SummaryHeading = styled.h3`
  margin: 0;
  color: rgba(28, 18, 9, 0.95);
  font-size: 1.02rem;
  font-weight: 900;
  line-height: 1.16;
`;

const SummarySub = styled.p`
  margin: 0;
  color: rgba(43, 28, 12, 0.8);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.35;
`;

const SummarySection = styled.section`
  display: grid;
  gap: 8px;
`;

const SummarySectionTitle = styled.h4`
  margin: 0;
  color: rgba(38, 24, 10, 0.88);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SummaryRow = styled.div`
  display: grid;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  color: rgba(52, 34, 15, 0.72);
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const SummaryValue = styled.span`
  color: rgba(33, 21, 10, 0.94);
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.25;
`;

const SummaryList = styled.ul`
  margin: 0;
  padding-left: 16px;
  display: grid;
  gap: 4px;
`;

const SummaryListItem = styled.li`
  color: rgba(40, 25, 10, 0.88);
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.28;
`;

const MainPane = styled.section`
  border-radius: 14px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: grid;
  animation: ${fadeInUp} 220ms ease both;
`;

const SplitColumns = styled.div`
  height: 100%;
  min-height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 1fr);
  gap: 10px;
  overflow: hidden;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border-radius: 12px;
  // border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.02);
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`;

const PanelHeader = styled.header`
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  padding: 10px;
  display: grid;
  gap: 2px;
`;

const PanelTitle = styled.h3`
  margin: 0;
  color: rgba(245, 247, 252, 0.96);
  font-size: 0.84rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PanelTitleSentence = styled(PanelTitle)`
  text-transform: none;
  font-size: 0.92rem;
  letter-spacing: 0.01em;
`;

const PanelTabs = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const PanelTabButton = styled.button<{ $active?: boolean }>`
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${({ $active }) =>
    $active ? backendPalette.yellow : 'rgba(245, 247, 252, 0.82)'};
  height: auto;
  padding: 0 0 7px;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? backendPalette.yellow : 'transparent')};
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  cursor: pointer;

  &:hover {
    color: ${({ $active }) =>
      $active ? backendPalette.yellow : 'rgba(245, 247, 252, 0.95)'};
  }
`;

const PanelBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 10px;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.26);
    border-radius: 999px;
  }
`;

const OfferingList = styled.div`
  display: grid;
  gap: 8px;
`;

const NestedOfferingList = styled.div`
  display: grid;
  gap: 8px;
  margin-left: 14px;
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
`;

const OfferingCard = styled.div<{
  $selectable?: boolean;
  $selected?: boolean;
  $dragging?: boolean;
  $dropTarget?: boolean;
}>`
  border-radius: 10px;
  border: 1px solid
    ${({ $selectable, $selected }) =>
      $selectable && $selected ? 'rgba(224, 161, 94, 0.75)' : 'rgba(255, 255, 255, 0.18)'};
  ${({ $dropTarget }) => ($dropTarget ? 'border-color: rgba(224, 161, 94, 0.9);' : '')}
  background: rgba(255, 255, 255, 0.02);
  padding: 8px 10px;
  display: grid;
  gap: 8px;
  color: rgba(245, 247, 252, 0.95);
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};
  opacity: ${({ $dragging }) => ($dragging ? 0.55 : 1)};

  ${({ $selectable, $selected }) =>
    $selectable
      ? css`
          &:hover {
            border-color: rgba(224, 161, 94, 0.72);
            background: ${$selected ? 'rgba(224, 161, 94, 0.14)' : 'rgba(224, 161, 94, 0.08)'};
          }
        `
      : ''}
`;

const NestedOfferingCard = styled(OfferingCard)`
  background: rgba(255, 255, 255, 0.035);
  border-color: rgba(255, 255, 255, 0.14);
`;

const ReadOnlySubcategoryCard = styled(NestedOfferingCard)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  column-gap: 12px;
  row-gap: 0;
`;

const OfferingTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SubcategoryReadOnlyHeader = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const OfferingTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
`;

const DragHandleSlot = styled.div`
  width: 0;
  overflow: hidden;
  transition: width 140ms ease;
  flex: 0 0 auto;

  ${OfferingCard}:hover &,
  ${OfferingCard}:focus-within & {
    width: 22px;
  }
`;

const DragHandleButton = styled.button`
  width: 22px;
  height: 22px;
  border: 0;
  background: transparent;
  color: rgba(245, 247, 252, 0.72);
  display: inline-grid;
  place-items: center;
  cursor: grab;
  padding: 0;

  &:active {
    cursor: grabbing;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const OfferingTitle = styled.span`
  color: rgba(245, 247, 252, 0.95);
  font-size: 0.96rem;
  font-weight: 800;
  line-height: 1.25;
`;

const OfferingMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const OfferingMetaChip = styled.span`
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  padding: 0.12rem 0.44rem;
  color: rgba(245, 247, 252, 0.86);
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const OfferingDescription = styled.span`
  color: rgba(245, 247, 252, 0.72);
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.3;
`;

const SubcategoryEstimateSummary = styled.div`
  display: grid;
  justify-items: end;
  align-content: start;
  gap: 2px;
  flex: 0 0 auto;
  text-align: right;
`;

const SubcategoryEstimateLabel = styled.span`
  color: rgba(245, 247, 252, 0.82);
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: 0;
  text-transform: none;
  white-space: nowrap;
`;

const SubcategoryEstimateMeta = styled.span`
  display: block;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.3;
  text-align: right;
  white-space: nowrap;
`;

const SubcategorySection = styled.div`
  display: grid;
  gap: 8px;
`;

const SubcategoryToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const SubcategoryList = styled.div`
  display: grid;
  gap: 8px;
`;

const SubcategoryFields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr) 108px auto;
  gap: 8px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SubcategoryEmptyState = styled.span`
  color: rgba(245, 247, 252, 0.56);
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.3;
`;

const OfferingTypeReadOnly = styled.span`
  color: rgba(245, 247, 252, 0.78);
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.2;
`;

const OfferingComposerRow = styled.div`
  border-radius: 10px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyState = styled.div`
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.34);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(245, 247, 252, 0.78);
  padding: 12px;
  font-size: 0.82rem;
  font-weight: 700;
`;

const TierDetails = styled.div`
  display: grid;
  gap: 8px;
`;

const DetailRow = styled.div`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.02);
  padding: 8px 10px;
  display: grid;
  gap: 2px;
`;

const DetailLabel = styled.span`
  color: rgba(255, 255, 255, 0.64);
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const DetailValue = styled.span`
  color: rgba(245, 247, 252, 0.95);
  font-size: 0.83rem;
  font-weight: 800;
  line-height: 1.2;
`;

const TierCardRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 10px;
  margin-bottom: 6px;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.24);
    border-radius: 999px;
  }
`;

const TierSelectCard = styled.button<{ $active?: boolean }>`
  flex: 0 0 auto;
  min-width: 168px;
  min-height: 52px;
  border-radius: 12px;
  border: ${({ $active }) => ($active ? 'none' : '1px solid rgba(152, 160, 172, 0.72)')};
  background: ${({ $active }) =>
    $active ? backendPalette.yellow : 'transparent'};
  color: ${({ $active }) => ($active ? 'rgba(14, 14, 14, 0.92)' : 'rgba(245, 247, 252, 0.94)')};
  padding: 8px 10px;
  display: grid;
  align-content: center;
  justify-items: start;
  gap: 2px;
  cursor: pointer;
  box-shadow: none;
`;

const TierCardTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 900;
  line-height: 1.2;
`;

const TierCardMeta = styled.span`
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  opacity: 0.86;
`;

const TierAddCard = styled.button`
  flex: 0 0 auto;
  min-width: 128px;
  min-height: 52px;
  border-radius: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.4);
  background: transparent;
  color: rgba(245, 247, 252, 0.84);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  cursor: pointer;
`;

const Composer = styled.form`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(10, 14, 32, 0.62);
  padding: 10px;
  display: grid;
  gap: 8px;
  animation: ${fadeInUp} 180ms ease both;
`;

const TierModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: rgba(5, 8, 20, 0.72);
  backdrop-filter: blur(6px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 94;
`;

const TierModal = styled.section<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.98)});
  width: min(720px, calc(100vw - 28px));
  max-height: min(760px, calc(100vh - 42px));
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background:
    linear-gradient(145deg, rgba(18, 24, 52, 0.92), rgba(9, 13, 32, 0.96));
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease, transform 180ms ease;
  z-index: 95;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const TierModalHeader = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TierModalTitle = styled.div`
  color: rgba(245, 247, 252, 0.95);
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.2;
`;

const TierModalSub = styled.div`
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.8rem;
  font-weight: 700;
`;

const TierModalBody = styled.div`
  padding: 10px 12px;
  overflow: auto;
  display: grid;
  gap: 10px;
  align-content: start;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 950px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: grid;
  gap: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Input = styled.input`
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  background: transparent;
  color: rgba(245, 247, 252, 0.95);
  padding: 0 10px;
  font-size: 0.86rem;
  font-weight: 700;
`;

const Select = styled.select`
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  background: transparent;
  color: rgba(245, 247, 252, 0.95);
  padding: 0 10px;
  font-size: 0.86rem;
  font-weight: 700;
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TypeQuickPick = styled.div`
  width: 122px;

  button {
    min-height: 28px;
    border-radius: 999px;
    padding: 0 26px 0 10px;
    font-size: 0.64rem;
    font-weight: 900;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.28);
  }

  span {
    right: 9px;
    font-size: 0.62rem;
  }
`;

const IconActionButton = styled.button<{ $danger?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid ${({ $danger }) => ($danger ? 'rgba(255, 98, 98, 0.56)' : 'rgba(255, 255, 255, 0.26)')};
  background: ${({ $danger }) => ($danger ? 'rgba(140, 38, 38, 0.22)' : 'transparent')};
  color: ${({ $danger }) => ($danger ? 'rgba(255, 164, 164, 0.94)' : 'rgba(245, 247, 252, 0.9)')};
  display: inline-grid;
  place-items: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const OfferingInput = styled.input`
  width: 100%;
  min-height: 34px;
  border-radius: 9px;
  border: 1px dashed rgba(255, 255, 255, 0.34);
  background: transparent;
  color: rgba(245, 247, 252, 0.95);
  padding: 0 10px;
  font-size: 0.8rem;
  font-weight: 700;

  &::placeholder {
    color: rgba(245, 247, 252, 0.55);
  }
`;

const SubcategoryEstimateInput = styled(OfferingInput)`
  width: 84px;
  min-height: 28px;
  padding: 0 8px;
  text-align: right;
  font-size: 0.72rem;
`;

const MetaForm = styled.form`
  display: grid;
  gap: 10px;
`;

const MetaActionRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const MetaGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MetaField = styled.label`
  display: grid;
  gap: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const MetaInput = styled.input`
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: rgba(245, 247, 252, 0.95);
  padding: 0 10px;
  font-size: 0.82rem;
  font-weight: 700;
`;

const MetaFieldInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MetaQuickSetButton = styled.button`
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: rgba(245, 247, 252, 0.92);
  padding: 0 10px;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const MetaSelect = styled.select`
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: rgba(245, 247, 252, 0.95);
  padding: 0 10px;
  font-size: 0.82rem;
  font-weight: 700;
`;

const MetaPicklistWrap = styled.div`
  width: 100%;

  button {
    min-height: 36px;
    border-radius: 10px;
    padding: 0 28px 0 10px;
    font-size: 0.82rem;
    font-weight: 700;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  span {
    right: 10px;
    font-size: 0.66rem;
  }
`;

const MetaTextarea = styled.textarea`
  min-height: 86px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: rgba(245, 247, 252, 0.95);
  padding: 8px 10px;
  font-size: 0.82rem;
  font-weight: 700;
  resize: vertical;
`;

const OwnerInline = styled.div`
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const OwnerAvatar = styled(PortalAvatar)`
  flex: 0 0 auto;
`;

const OwnerName = styled.span`
  color: rgba(245, 247, 252, 0.95);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TeamSummaryLine = styled.div`
  color: rgba(245, 247, 252, 0.95);
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.25;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(245, 247, 252, 0.9);
  font-size: 0.8rem;
  font-weight: 800;
`;

const DeleteTierZone = styled.div`
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  justify-content: flex-end;
`;

const DeleteTierButton = styled.button`
  min-height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255, 98, 98, 0.56);
  background: rgba(140, 38, 38, 0.22);
  color: rgba(255, 164, 164, 0.96);
  padding: 0 12px;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubsectionTitle = styled.h5`
  margin: 2px 0 6px;
  color: rgba(245, 247, 252, 0.95);
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const SubsectionHint = styled.p`
  margin: 0 0 8px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.72rem;
  font-weight: 700;
`;

const AuditWarning = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 191, 122, 0.42);
  background: rgba(120, 72, 24, 0.24);
  color: rgba(255, 235, 209, 0.94);
  padding: 10px 12px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.45;
`;

const FieldHint = styled.span`
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.3;
`;

function toOfferingCode(name: string): string {
  const base = normalizeOfferingCode(name).slice(0, 40);
  if (base) return base;
  return `OFFERING_${Date.now()}`;
}

function normalizeOfferingName(value: string): string {
  const collapsed = value.trim().replace(/\s+/g, ' ');
  if (!collapsed) return '';
  return collapsed.replace(/\b([a-z])/g, (_, char: string) => char.toUpperCase());
}

function normalizeServiceOfferingSubcategoryName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function normalizeServiceOfferingSubcategoryDescription(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function createServiceOfferingSubcategoryId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `subcategory_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeServiceOfferingSubcategories(
  subcategories: ServiceOfferingSubcategoryTemplate[],
): ServiceOfferingSubcategoryTemplate[] {
  const seen = new Set<string>();
  return subcategories.reduce<ServiceOfferingSubcategoryTemplate[]>((list, entry) => {
    const id = entry.id?.trim() || createServiceOfferingSubcategoryId();
    const name = normalizeServiceOfferingSubcategoryName(entry.name);
    if (!name || seen.has(id)) return list;
    seen.add(id);
    list.push({
      id,
      name,
      description: normalizeServiceOfferingSubcategoryDescription(entry.description),
      estimatedMinutesOverride: parseNonNegativeIntOrNull(entry.estimatedMinutesOverride),
    });
    return list;
  }, []);
}

function hasIncompleteServiceOfferingSubcategories(
  subcategories: ServiceOfferingSubcategoryTemplate[],
): boolean {
  return subcategories.some((entry) => {
    const name = normalizeServiceOfferingSubcategoryName(entry.name);
    const description = normalizeServiceOfferingSubcategoryDescription(entry.description);
    return !name && Boolean(description);
  });
}

function readServiceOfferingSubcategoriesFromMetadata(
  metadata: Record<string, unknown> | undefined,
): ServiceOfferingSubcategoryTemplate[] {
  if (!metadata) return [];
  const rawSubcategories = metadata.subcategories;
  if (!Array.isArray(rawSubcategories)) return [];
  return rawSubcategories.reduce<ServiceOfferingSubcategoryTemplate[]>((list, entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return list;
    const raw = entry as Record<string, unknown>;
    const name = normalizeServiceOfferingSubcategoryName(safeString(raw.name));
    if (!name) return list;
    list.push({
      id: safeString(raw.id).trim() || `subcategory_${index + 1}`,
      name,
      description: normalizeServiceOfferingSubcategoryDescription(safeString(raw.description)),
      estimatedMinutes: parseNonNegativeIntOrNull(raw.estimatedMinutes),
      estimatedMinutesOverride: parseNonNegativeIntOrNull(raw.estimatedMinutesOverride),
      estimateSampleCount: parseNonNegativeIntOrNull(raw.estimateSampleCount) ?? 0,
      estimateUpdatedAt:
        typeof raw.estimateUpdatedAt === 'string' ? raw.estimateUpdatedAt.trim() || null : null,
      estimateSource:
        raw.estimateSource === 'seed' ||
        raw.estimateSource === 'aggregate' ||
        raw.estimateSource === 'none'
          ? raw.estimateSource
          : 'none',
    });
    return list;
  }, []);
}

function deriveServiceCards(services: ServiceCatalog[]): ServiceCatalog[] {
  return services.filter((service) => service.status !== 'archived');
}

function isTierVisible(tier: ServiceTier): boolean {
  return tier.status !== 'archived';
}

function isPrimaryRecurringTier(tier: ServiceTier): boolean {
  if (typeof tier.isPrimaryService === 'boolean') {
    return tier.isPrimaryService;
  }
  return true;
}

function countVisibleOfferings(service: ServiceCatalog): number {
  const fromMetadata = service.metadata?.offeringsCatalog;
  if (Array.isArray(fromMetadata)) {
    return fromMetadata
      .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
      .filter((entry) => safeString(entry.status) !== 'archived')
      .length;
  }
  return service.tiers.reduce(
    (sum, tier) => sum + tier.offerings.filter((offering) => offering.status !== 'archived').length,
    0,
  );
}

function countVisibleTiers(service: ServiceCatalog): number {
  return service.tiers.filter((tier) => isTierVisible(tier) && isPrimaryRecurringTier(tier)).length;
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function safeNumberString(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') return value;
  return '';
}

function formatUsdFromCents(cents: number | null | undefined): string {
  const value = typeof cents === 'number' && Number.isFinite(cents) ? Math.max(0, cents) : 0;
  return `$${(value / 100).toFixed(2)}`;
}

function formatTokenLabel(value: string | null | undefined): string {
  const normalized = safeString(value).trim();
  if (!normalized) return '--';
  return normalized
    .split('_')
    .join(' ')
    .split(' ')
    .filter(Boolean)
    .map((token) => `${token[0]?.toUpperCase() ?? ''}${token.slice(1)}`)
    .join(' ');
}

function formatEstimatedTimeLabel(minutes: number | null | undefined): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes < 0) {
    return '--';
  }
  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainderMinutes = totalMinutes % 60;
  if (hours <= 0) return `${remainderMinutes}m`;
  if (remainderMinutes <= 0) return `${hours}h`;
  return `${hours}h ${remainderMinutes}m`;
}

function parseUsdToCents(value: string): number | null {
  const cleaned = value.replace(/[^0-9.]/g, '').trim();
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function normalizeUsdInput(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '');
  if (!cleaned) return '$';
  const [wholeRaw, decimalRaw] = cleaned.split('.');
  const whole = wholeRaw.replace(/^0+(?=\d)/, '') || '0';
  const decimal = (decimalRaw ?? '').slice(0, 2);
  return decimal.length ? `$${whole}.${decimal}` : `$${whole}`;
}

function normalizeClientSuccessCadenceType(value: unknown): ClientSuccessCadenceType {
  if (value === 'weekly' || value === 'biweekly' || value === 'monthly' || value === 'quarterly') {
    return value;
  }
  const legacy = safeString(value).toLowerCase();
  if (legacy.includes('biweek')) return 'biweekly';
  if (legacy.includes('week')) return 'weekly';
  if (legacy.includes('quarter')) return 'quarterly';
  return 'monthly';
}

type TierMetaFormState = {
  name: string;
  skuId: string;
  status: CatalogStatus;
  amountUsd: string;
  billingType: string;
  billingInterval: string;
  currency: string;
  isPrimaryService: boolean;
  sortOrder: string;
  description: string;
  minTermMonths: string;
  deliveryHoursMin: string;
  deliveryHoursMax: string;
  activeLanesIncluded: string;
  clientSuccessCadenceType: ClientSuccessCadenceType;
  clientSuccessMeetingsPerMonth: string;
  advancedLanesAccess: string;
  specialtyLanesAccess: string;
};

function createEmptyTierMetaForm(): TierMetaFormState {
  return {
    name: '',
    skuId: '',
    status: 'draft',
    amountUsd: '$0.00',
    billingType: '',
    billingInterval: '',
    currency: 'usd',
    isPrimaryService: true,
    sortOrder: '0',
    description: '',
    minTermMonths: '',
    deliveryHoursMin: '',
    deliveryHoursMax: '',
    activeLanesIncluded: '',
    clientSuccessCadenceType: 'monthly',
    clientSuccessMeetingsPerMonth: '',
    advancedLanesAccess: '',
    specialtyLanesAccess: '',
  };
}

function readTierMetaForm(tier: ServiceTier | null): TierMetaFormState {
  if (!tier) return createEmptyTierMetaForm();
  const metadata = tier.metadata ?? {};
  const deliveryHoursRaw =
    metadata.deliveryHours && typeof metadata.deliveryHours === 'object' && !Array.isArray(metadata.deliveryHours)
      ? (metadata.deliveryHours as Record<string, unknown>)
      : {};
  return {
    name: tier.name ?? '',
    skuId: tier.skuId ?? '',
    status: tier.status ?? 'draft',
    amountUsd: formatUsdFromCents(tier.amountCents ?? 0),
    billingType: tier.billingType ?? '',
    billingInterval: tier.billingInterval ?? '',
    currency: (tier.currency ?? 'usd').toLowerCase(),
    isPrimaryService: Boolean(tier.isPrimaryService),
    sortOrder: String(tier.sortOrder ?? 0),
    description: tier.description ?? '',
    minTermMonths: safeNumberString(metadata.minTermMonths),
    deliveryHoursMin: safeNumberString(deliveryHoursRaw.min),
    deliveryHoursMax: safeNumberString(deliveryHoursRaw.max),
    activeLanesIncluded: safeNumberString(metadata.activeLanesIncluded),
    clientSuccessCadenceType: normalizeClientSuccessCadenceType(
      metadata.clientSuccessCadenceType ?? metadata.clientSuccessCadence,
    ),
    clientSuccessMeetingsPerMonth: safeNumberString(metadata.clientSuccessMeetingsPerMonth),
    advancedLanesAccess: safeString(metadata.advancedLanesAccess),
    specialtyLanesAccess: safeString(metadata.specialtyLanesAccess),
  };
}

function readServiceSharedFeaturesText(metadata?: Record<string, unknown>): string {
  const value = metadata?.sharedFeatures;
  if (Array.isArray(value)) {
    return value
      .map((entry) => safeString(entry).trim())
      .filter(Boolean)
      .join('\n');
  }
  return safeString(value);
}

function parseSharedFeatures(text: string): string[] {
  return text
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function normalizeExclusionName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function toExclusionCode(name: string): string {
  return normalizeOfferingCode(name);
}

function readServiceExclusionsCatalog(service: ServiceCatalog | null): ServiceExclusionTemplate[] {
  if (!service) return [];
  const metadataValue = service.metadata?.exclusionsCatalog;
  if (Array.isArray(metadataValue)) {
    const seen = new Set<string>();
    return metadataValue
      .flatMap((entry, index) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
        const raw = entry as Record<string, unknown>;
        const code = safeString(raw.code).trim().toUpperCase() || toExclusionCode(safeString(raw.name));
        if (!code || seen.has(code)) return [];
        seen.add(code);
        return [
          {
            code,
            name: normalizeExclusionName(safeString(raw.name)) || code,
            description: safeString(raw.description),
            status: isCatalogStatus(raw.status) ? raw.status : 'draft',
            sortOrder: parseNonNegativeIntOrNull(raw.sortOrder) ?? index,
          },
        ];
      })
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  const legacy = service.metadata?.exclusions;
  if (!Array.isArray(legacy)) return [];
  return legacy
    .map((entry, index) => {
      const name = normalizeExclusionName(safeString(entry));
      if (!name) return null;
      return {
        code: toExclusionCode(name),
        name,
        description: '',
        status: 'published' as CatalogStatus,
        sortOrder: index,
      };
    })
    .filter((entry): entry is ServiceExclusionTemplate => Boolean(entry));
}

function readTierIncludedExclusionCodes(
  tier: ServiceTier | null,
  catalog: ServiceExclusionTemplate[],
): string[] {
  if (!tier) return [];
  const validCodes = new Set(catalog.map((entry) => entry.code));
  const rawCodes = tier.metadata?.includedExclusionCodes;
  if (Array.isArray(rawCodes)) {
    return rawCodes
      .map((entry) => safeString(entry).trim().toUpperCase())
      .filter((code) => code && validCodes.has(code));
  }
  const legacy = tier.metadata?.exclusions;
  if (!Array.isArray(legacy)) return [];
  const namesToCodes = new Map(catalog.map((entry) => [entry.name.trim().toLowerCase(), entry.code]));
  return legacy
    .map((entry) => namesToCodes.get(safeString(entry).trim().toLowerCase()) ?? '')
    .filter(Boolean);
}

function normalizeRoutingKey(value: unknown): string {
  return safeString(value).trim().toLowerCase();
}

function readNodeRoutingRule(
  rules: Record<string, unknown> | undefined,
): { serviceCode: string; tierSkuId: string | null } | null {
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return null;
  const routingRaw = rules.routing;
  if (!routingRaw || typeof routingRaw !== 'object' || Array.isArray(routingRaw)) return null;
  const routing = routingRaw as Record<string, unknown>;
  const serviceCode = safeString(routing.serviceCode).trim();
  if (!serviceCode) return null;
  const tierSkuId = safeString(routing.tierSkuId).trim();
  return {
    serviceCode,
    tierSkuId: tierSkuId || null,
  };
}

function toServiceSlaPolicyFromDraft(draft: TierOfferingSlaFormState): ServiceSlaPolicy {
  return {
    responseTargetMinutes: hoursInputToMinutes(draft.responseTargetMinutes),
    resolutionTargetMinutes: hoursInputToMinutes(draft.resolutionTargetMinutes),
    targetCompletionMinutes: hoursInputToMinutes(draft.targetCompletionMinutes),
    escalationAfterMinutes: hoursInputToMinutes(draft.escalationAfterMinutes),
    escalationScope: draft.escalationScope,
    monthlyHoursCommitment: null,
    queueWeight: parseNonNegativeIntOrNull(draft.queueWeight),
    businessHoursOnly: false,
  };
}

function readServiceSlaPoliciesByBlock(metadata?: Record<string, unknown>): ServiceSlaPoliciesByBlock {
  const legacy = normalizeServiceSlaPolicy(metadata?.serviceSla);
  const defaultFromLegacy = (fallbackScope: EscalationScope = 'both'): ServiceSlaPolicy => ({
    responseTargetMinutes: legacy.responseTargetMinutes,
    resolutionTargetMinutes: legacy.resolutionTargetMinutes,
    targetCompletionMinutes: legacy.targetCompletionMinutes,
    escalationAfterMinutes: legacy.escalationAfterMinutes,
    escalationScope: legacy.escalationScope ?? fallbackScope,
    monthlyHoursCommitment: legacy.monthlyHoursCommitment,
    queueWeight: legacy.queueWeight,
    businessHoursOnly: legacy.businessHoursOnly,
  });

  const rawBlocks = metadata?.serviceSlaBlocks;
  if (!rawBlocks || typeof rawBlocks !== 'object' || Array.isArray(rawBlocks)) {
    const fallback = defaultFromLegacy();
    return {
      offerings: fallback,
      add_ons: fallback,
      ticketing: fallback,
    };
  }

  const blockRecord = rawBlocks as Record<string, unknown>;
  const readBlock = (key: TierSlaBlockKey, fallbackScope: EscalationScope = 'both'): ServiceSlaPolicy => {
    const rawValue = blockRecord[key];
    const fallback = defaultFromLegacy(fallbackScope);
    if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
      return fallback;
    }

    const normalized = normalizeServiceSlaPolicy(rawValue);
    return {
      responseTargetMinutes:
        normalized.responseTargetMinutes ?? fallback.responseTargetMinutes,
      resolutionTargetMinutes:
        normalized.resolutionTargetMinutes ?? fallback.resolutionTargetMinutes,
      targetCompletionMinutes:
        normalized.targetCompletionMinutes ?? fallback.targetCompletionMinutes,
      escalationAfterMinutes:
        normalized.escalationAfterMinutes ?? fallback.escalationAfterMinutes,
      escalationScope: normalized.escalationScope ?? fallback.escalationScope,
      monthlyHoursCommitment:
        normalized.monthlyHoursCommitment ?? fallback.monthlyHoursCommitment,
      queueWeight: normalized.queueWeight ?? fallback.queueWeight,
      businessHoursOnly:
        typeof (rawValue as Record<string, unknown>).businessHoursOnly === 'boolean'
          ? Boolean((rawValue as Record<string, unknown>).businessHoursOnly)
          : fallback.businessHoursOnly,
    };
  };

  return {
    offerings: readBlock('offerings'),
    add_ons: readBlock('add_ons'),
    ticketing: readBlock('ticketing'),
  };
}

function toSlaFormStateFromServicePolicy(policy: ServiceSlaPolicy): TierOfferingSlaFormState {
  return {
    responseTargetMinutes: minutesToHoursInput(policy.responseTargetMinutes),
    resolutionTargetMinutes: minutesToHoursInput(policy.resolutionTargetMinutes),
    targetCompletionMinutes: minutesToHoursInput(policy.targetCompletionMinutes),
    escalationAfterMinutes: minutesToHoursInput(policy.escalationAfterMinutes),
    escalationScope: policy.escalationScope ?? 'both',
    queueWeight: toInputValue(policy.queueWeight),
  };
}

function readServiceSlaBlockDrafts(metadata?: Record<string, unknown>): TierSlaBlockDrafts {
  const policies = readServiceSlaPoliciesByBlock(metadata);
  return {
    offerings: toSlaFormStateFromServicePolicy(policies.offerings),
    add_ons: toSlaFormStateFromServicePolicy(policies.add_ons),
    ticketing: toSlaFormStateFromServicePolicy(policies.ticketing),
  };
}

function isCatalogStatus(value: unknown): value is CatalogStatus {
  return value === 'draft' || value === 'published' || value === 'archived';
}

function readServiceOfferingsCatalog(service: ServiceCatalog | null): ServiceOfferingTemplate[] {
  if (!service) return [];
  const metadataValue = service.metadata?.offeringsCatalog;
  if (Array.isArray(metadataValue)) {
    return normalizeServiceOfferingCatalog(metadataValue).map((entry) => ({
      ...entry,
      status: isCatalogStatus(entry.status) ? entry.status : 'draft',
    }));
  }

  const map = new Map<string, ServiceOfferingTemplate>();
  for (const tier of service.tiers ?? []) {
    for (const offering of tier.offerings ?? []) {
      if (!offering?.code || map.has(offering.code)) continue;
      map.set(offering.code, {
        code: offering.code,
        name: offering.name,
        description: offering.description ?? '',
        status: offering.status,
        sortOrder: offering.sortOrder ?? map.size,
        type: normalizeServiceOfferingType((offering.metadata as Record<string, unknown> | undefined)?.type),
        addOnEnabled: Boolean((offering.metadata as Record<string, unknown> | undefined)?.addOnEnabled),
        unitPriceCents: parseNonNegativeIntOrNull(
          (offering.metadata as Record<string, unknown> | undefined)?.unitPriceCents,
        ),
        subcategories: readServiceOfferingSubcategoriesFromMetadata(
          offering.metadata as Record<string, unknown> | undefined,
        ),
        sla: normalizeOfferingSlaPolicy((offering.metadata as Record<string, unknown> | undefined)?.sla),
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function normalizeServiceAddOnVariation(
  value: unknown,
  index: number,
): ServiceAddOnVariationTemplate | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const name = normalizeOfferingName(safeString(raw.name));
  if (!name) return null;
  const rawCode = safeString(raw.code).trim().toUpperCase();
  return {
    code: rawCode || toOfferingCode(name),
    name,
    description: safeString(raw.description).trim(),
    status: isCatalogStatus(raw.status) ? raw.status : 'published',
    sortOrder: parseNonNegativeIntOrNull(raw.sortOrder) ?? index,
    unitPriceCents: parseNonNegativeIntOrNull(raw.unitPriceCents),
  };
}

function normalizeServiceAddOn(
  value: unknown,
  index: number,
): ServiceAddOnTemplate | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const name = normalizeOfferingName(safeString(raw.name));
  if (!name) return null;
  const rawCode = safeString(raw.code).trim().toUpperCase();
  const variations = Array.isArray(raw.variations)
    ? raw.variations
        .map((entry, variationIndex) => normalizeServiceAddOnVariation(entry, variationIndex))
        .filter((entry): entry is ServiceAddOnVariationTemplate => Boolean(entry))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    : [];
  return {
    code: rawCode || toOfferingCode(name),
    name,
    description: safeString(raw.description).trim(),
    status: isCatalogStatus(raw.status) ? raw.status : 'published',
    sortOrder: parseNonNegativeIntOrNull(raw.sortOrder) ?? index,
    variations,
  };
}

function readServiceAddOnsCatalog(service: ServiceCatalog | null): ServiceAddOnTemplate[] {
  if (!service) return [];
  const metadataValue = service.metadata?.addOnsCatalog;
  if (!Array.isArray(metadataValue)) return [];
  return metadataValue
    .map((entry, index) => normalizeServiceAddOn(entry, index))
    .filter((entry): entry is ServiceAddOnTemplate => Boolean(entry))
    .filter((entry) => entry.status !== 'archived')
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function createServiceAddOnCode(existingCodes: Set<string>, name: string): string {
  const base = toOfferingCode(name);
  let code = base;
  let suffix = 2;
  while (existingCodes.has(code)) {
    code = `${base}_${suffix}`;
    suffix += 1;
  }
  return code;
}

function createServiceAddOnVariationCode(existingCodes: Set<string>, name: string): string {
  return createServiceAddOnCode(existingCodes, name);
}

function toServiceAddOnsCatalogMetadata(catalog: ServiceAddOnTemplate[]): Record<string, unknown> {
  return {
    addOnsCatalogVersion: 1,
    addOnsCatalog: catalog.map((entry, index) => ({
      code: entry.code,
      name: normalizeOfferingName(entry.name),
      description: entry.description.trim(),
      status: 'published' as CatalogStatus,
      sortOrder: index,
      variations: entry.variations.map((variation, variationIndex) => ({
        code: variation.code,
        name: normalizeOfferingName(variation.name),
        description: variation.description.trim(),
        status: 'published' as CatalogStatus,
        sortOrder: variationIndex,
        unitPriceCents: variation.unitPriceCents ?? null,
      })),
    })),
  };
}

function readTierIncludedOfferingCodes(tier: ServiceTier | null, catalog: ServiceOfferingTemplate[]): string[] {
  if (!tier) return [];
  const catalogCodes = new Set(catalog.map((entry) => entry.code));
  const metadataValue = tier.metadata?.includedServiceOfferingCodes;
  if (Array.isArray(metadataValue)) {
    return metadataValue
      .map((entry) => safeString(entry))
      .filter((code) => code && catalogCodes.has(code));
  }
  return (tier.offerings ?? [])
    .map((offering) => offering.code)
    .filter((code) => code && catalogCodes.has(code));
}

function readTierOfferingOverrides(
  tier: ServiceTier | null,
  catalog: ServiceOfferingTemplate[],
): Record<string, TierOfferingOverride> {
  if (!tier) return {};
  const catalogCodes = new Set(catalog.map((entry) => entry.code));
  const raw = tier.metadata?.tierOfferingOverrides;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const parsed: Record<string, TierOfferingOverride> = {};
  for (const [code, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!catalogCodes.has(code)) continue;
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const entry = value as Record<string, unknown>;
    parsed[code] = {
      incentive: safeString(entry.incentive),
    };
  }
  return parsed;
}

function createDefaultTierAddOnConfig(): TierAddOnConfig {
  return {
    enabled: false,
    unitPriceCents: null,
    maxQuantity: null,
    unitsPerAddOn: null,
    laneOfferingCodes: [],
  };
}

function getLaneTypeForTierAddOnPreset(
  preset: TierAddOnPreset,
): ServiceOfferingType | null {
  if (preset === 'additional_core_lane') return 'core';
  if (preset === 'additional_advanced_lane') return 'advanced';
  if (preset === 'additional_specialty_lane') return 'specialty';
  return null;
}

function normalizeTierAddOnLaneOfferingCodes(
  value: unknown,
  catalog: ServiceOfferingTemplate[],
  laneType: ServiceOfferingType | null,
): string[] {
  if (!Array.isArray(value)) return [];
  const catalogMap = new Map(
    catalog
      .filter((offering) => offering.status !== 'archived')
      .map((offering) => [offering.code, offering]),
  );
  const next: string[] = [];
  for (const entry of value) {
    const code = safeString(entry);
    if (!code || next.includes(code)) continue;
    const offering = catalogMap.get(code);
    if (!offering) continue;
    if (laneType && normalizeServiceOfferingType(offering.type) !== laneType) continue;
    next.push(code);
  }
  return next;
}

function normalizeTierAddOnConfig(
  value: unknown,
  catalog: ServiceOfferingTemplate[],
  laneType: ServiceOfferingType | null,
): TierAddOnConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return createDefaultTierAddOnConfig();
  }
  const raw = value as Record<string, unknown>;
  return {
    enabled: Boolean(raw.enabled),
    unitPriceCents: parseNonNegativeIntOrNull(raw.unitPriceCents),
    maxQuantity: parseNonNegativeIntOrNull(raw.maxQuantity),
    unitsPerAddOn: parseNonNegativeIntOrNull(raw.unitsPerAddOn),
    laneOfferingCodes: normalizeTierAddOnLaneOfferingCodes(raw.laneOfferingCodes, catalog, laneType),
  };
}

function normalizeTierCustomAddOn(
  value: unknown,
  index: number,
): TierCustomAddOn | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  const name = normalizeOfferingName(safeString(raw.name));
  if (!name) return null;
  const rawCode = safeString(raw.code).trim().toUpperCase();
  return {
    code: rawCode || toOfferingCode(name),
    name,
    description: safeString(raw.description).trim(),
    unitPriceCents: parseNonNegativeIntOrNull(raw.unitPriceCents),
    sortOrder: parseNonNegativeIntOrNull(raw.sortOrder) ?? index,
  };
}

function createTierCustomAddOnCode(existingCodes: Set<string>, name: string): string {
  const base = toOfferingCode(name);
  let code = base;
  let suffix = 2;
  while (existingCodes.has(code)) {
    code = `${base}_${suffix}`;
    suffix += 1;
  }
  return code;
}

function readTierAddOnSettings(
  tier: ServiceTier | null,
  catalog: ServiceOfferingTemplate[],
): TierAddOnSettings {
  const defaultPresets = Object.fromEntries(
    TIER_ADD_ON_PRESETS.map((preset) => [preset, createDefaultTierAddOnConfig()]),
  ) as Record<TierAddOnPreset, TierAddOnConfig>;
  const defaultSettings: TierAddOnSettings = {
    maxAddOnsPerTier: null,
    presets: defaultPresets,
    customAddOns: [],
  };
  if (!tier) return defaultSettings;

  const rawSettings = tier.metadata?.tierAddOnSettings;
  if (!rawSettings || typeof rawSettings !== 'object' || Array.isArray(rawSettings)) {
    return defaultSettings;
  }

  const parsedRaw = rawSettings as Record<string, unknown>;
  const rawPresets = parsedRaw.presets;
  const presets: Record<TierAddOnPreset, TierAddOnConfig> = {
    ...defaultPresets,
  };

  if (rawPresets && typeof rawPresets === 'object' && !Array.isArray(rawPresets)) {
    TIER_ADD_ON_PRESETS.forEach((preset) => {
      const laneType = getLaneTypeForTierAddOnPreset(preset);
      presets[preset] = normalizeTierAddOnConfig(
        (rawPresets as Record<string, unknown>)[preset],
        catalog,
        laneType,
      );
    });
  }

  const customAddOns = Array.isArray(parsedRaw.customAddOns)
    ? parsedRaw.customAddOns
        .map((entry, index) => normalizeTierCustomAddOn(entry, index))
        .filter((entry): entry is TierCustomAddOn => Boolean(entry))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    : [];

  return {
    maxAddOnsPerTier: parseNonNegativeIntOrNull(parsedRaw.maxAddOnsPerTier),
    presets,
    customAddOns,
  };
}

function toTierAddOnSettingsMetadata(settings: TierAddOnSettings): Record<string, unknown> {
  const presets = Object.fromEntries(
    TIER_ADD_ON_PRESETS.map((preset) => [
      preset,
      {
        enabled: Boolean(settings.presets[preset]?.enabled),
        unitPriceCents: settings.presets[preset]?.unitPriceCents ?? null,
        maxQuantity: settings.presets[preset]?.maxQuantity ?? null,
        unitsPerAddOn: settings.presets[preset]?.unitsPerAddOn ?? null,
        laneOfferingCodes: settings.presets[preset]?.laneOfferingCodes ?? [],
      },
    ]),
  );

  return {
    maxAddOnsPerTier: settings.maxAddOnsPerTier,
    presets,
    customAddOns: settings.customAddOns.map((entry, index) => ({
      code: entry.code,
      name: normalizeOfferingName(entry.name),
      description: entry.description.trim(),
      unitPriceCents: entry.unitPriceCents ?? null,
      sortOrder: index,
    })),
  };
}

function readTierOfferingSlaOverrides(
  tier: ServiceTier | null,
  catalog: ServiceOfferingTemplate[],
): Record<string, TierOfferingSlaOverride> {
  if (!tier) return {};
  const validCodes = new Set(catalog.map((entry) => entry.code));
  return normalizeTierOfferingSlaOverrides(tier.metadata?.tierOfferingSlaOverrides, validCodes);
}

function toTierOfferingSlaFormState(
  effective: ReturnType<typeof computeEffectiveOfferingSla>,
  override?: TierOfferingSlaOverride,
): TierOfferingSlaFormState {
  return {
    responseTargetMinutes: minutesToHoursInput(
      typeof override?.responseTargetMinutes === 'number'
        ? override.responseTargetMinutes
        : effective.responseTargetMinutes,
    ),
    resolutionTargetMinutes: minutesToHoursInput(
      typeof override?.resolutionTargetMinutes === 'number'
        ? override.resolutionTargetMinutes
        : effective.resolutionTargetMinutes,
    ),
    targetCompletionMinutes: minutesToHoursInput(
      typeof override?.targetCompletionMinutes === 'number'
        ? override.targetCompletionMinutes
        : effective.targetCompletionMinutes,
    ),
    escalationAfterMinutes: minutesToHoursInput(
      typeof override?.escalationAfterMinutes === 'number'
        ? override.escalationAfterMinutes
        : effective.escalationAfterMinutes,
    ),
    escalationScope:
      override?.escalationScope ??
      effective.escalationScope ??
      'both',
    queueWeight: toInputValue(
      typeof override?.queueWeight === 'number' ? override.queueWeight : effective.queueWeight,
    ),
  };
}

export default function SlaSetupPage() {
  const [services, setServices] = useState<ServiceCatalog[]>([]);
  const [activeServiceId, setActiveServiceId] = useState<string | null>(null);
  const [activeTierId, setActiveTierId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showCreateComposer, setShowCreateComposer] = useState(false);
  const [newServiceOfferingName, setNewServiceOfferingName] = useState('');
  const [newServiceAddOnName, setNewServiceAddOnName] = useState('');
  const [newServiceAddOnDescription, setNewServiceAddOnDescription] = useState('');
  const [newServiceAddOnVariationDraft, setNewServiceAddOnVariationDraft] = useState<ServiceAddOnVariationDraft>({
    parentCode: null,
    code: null,
    name: '',
    description: '',
    price: '',
  });
  const [newServiceExclusionName, setNewServiceExclusionName] = useState('');
  const [newServiceExclusionDescription, setNewServiceExclusionDescription] = useState('');
  const [editingServiceOfferingCode, setEditingServiceOfferingCode] = useState<string | null>(null);
  const [editingServiceOfferingName, setEditingServiceOfferingName] = useState('');
  const [editingServiceOfferingDescription, setEditingServiceOfferingDescription] = useState('');
  const [editingServiceOfferingType, setEditingServiceOfferingType] = useState<ServiceOfferingType>('core');
  const [editingServiceOfferingSubcategories, setEditingServiceOfferingSubcategories] = useState<
    ServiceOfferingSubcategoryTemplate[]
  >([]);
  const [expandedServiceOfferingCodes, setExpandedServiceOfferingCodes] = useState<string[]>([]);
  const [editingServiceAddOnCode, setEditingServiceAddOnCode] = useState<string | null>(null);
  const [editingServiceAddOnName, setEditingServiceAddOnName] = useState('');
  const [editingServiceAddOnDescription, setEditingServiceAddOnDescription] = useState('');
  const [editingServiceAddOnVariationDraft, setEditingServiceAddOnVariationDraft] =
    useState<ServiceAddOnVariationDraft>({
      parentCode: null,
      code: null,
      name: '',
      description: '',
      price: '',
    });
  const [editingServiceExclusionCode, setEditingServiceExclusionCode] = useState<string | null>(null);
  const [editingServiceExclusionName, setEditingServiceExclusionName] = useState('');
  const [editingServiceExclusionDescription, setEditingServiceExclusionDescription] = useState('');
  const [serviceOfferingBusyCode, setServiceOfferingBusyCode] = useState<string | 'new' | null>(null);
  const [serviceExclusionBusyCode, setServiceExclusionBusyCode] = useState<string | 'new' | null>(null);
  const [serviceOfferingReordering, setServiceOfferingReordering] = useState(false);
  const [draggingOfferingCode, setDraggingOfferingCode] = useState<string | null>(null);
  const [dropTargetOfferingCode, setDropTargetOfferingCode] = useState<string | null>(null);
  const [tierOfferingBusyCode, setTierOfferingBusyCode] = useState<string | null>(null);
  const [editingTierOfferingCode, setEditingTierOfferingCode] = useState<string | null>(null);
  const [tierSlaEditing, setTierSlaEditing] = useState(false);
  const [tierSlaBusyCode, setTierSlaBusyCode] = useState<string | null>(null);
  const [tierSlaBlockDrafts, setTierSlaBlockDrafts] = useState<TierSlaBlockDrafts>(
    createDefaultTierSlaBlockDrafts(),
  );
  const [servicePanelTab, setServicePanelTab] = useState<
    'meta' | 'offerings' | 'add_ons' | 'exclusions' | 'discounts' | 'sla' | 'service_team' | 'settings'
  >('meta');
  const [tierPanelTab, setTierPanelTab] = useState<
    'meta' | 'offerings' | 'add_ons' | 'exclusions' | 'discounts' | 'sla' | 'service_team' | 'settings'
  >('meta');
  const [serviceMetaEditing, setServiceMetaEditing] = useState(false);
  const [serviceMetaBusy, setServiceMetaBusy] = useState(false);
  const [serviceSlaEditing, setServiceSlaEditing] = useState(false);
  const [serviceSlaBusy, setServiceSlaBusy] = useState(false);
  const [serviceSlaBlockDrafts, setServiceSlaBlockDrafts] = useState<TierSlaBlockDrafts>(
    createDefaultTierSlaBlockDrafts(),
  );
  const [serviceDeleteBusy, setServiceDeleteBusy] = useState(false);
  const [brainLoading, setBrainLoading] = useState(false);
  const [brainNodeOptions, setBrainNodeOptions] = useState<BrainNodeOption[]>([]);
  const [serviceMetaForm, setServiceMetaForm] = useState({
    title: '',
    slug: '',
    status: 'draft' as CatalogStatus,
    checkoutEnabled: true,
    teaser: '',
    subtitle: '',
    description: '',
    sharedFeatures: '',
  });
  const [tierMetaEditing, setTierMetaEditing] = useState(false);
  const [tierMetaBusy, setTierMetaBusy] = useState(false);
  const [tierDeleteBusy, setTierDeleteBusy] = useState(false);
  const [tierExclusionBusyCode, setTierExclusionBusyCode] = useState<string | null>(null);
  const [serviceDiscountsForm, setServiceDiscountsForm] = useState<DiscountFormItem[]>([]);
  const [tierDiscountsForm, setTierDiscountsForm] = useState<DiscountFormItem[]>([]);
  const [serviceDiscountsBusy, setServiceDiscountsBusy] = useState(false);
  const [tierDiscountsBusy, setTierDiscountsBusy] = useState(false);
  const [newTierCustomAddOnName, setNewTierCustomAddOnName] = useState('');
  const [newTierCustomAddOnDescription, setNewTierCustomAddOnDescription] = useState('');
  const [newTierCustomAddOnPrice, setNewTierCustomAddOnPrice] = useState('');
  const [editingTierCustomAddOnCode, setEditingTierCustomAddOnCode] = useState<string | null>(null);
  const [editingTierCustomAddOnName, setEditingTierCustomAddOnName] = useState('');
  const [editingTierCustomAddOnDescription, setEditingTierCustomAddOnDescription] = useState('');
  const [editingTierCustomAddOnPrice, setEditingTierCustomAddOnPrice] = useState('');

  useEffect(() => {
    if (!message) return;
    const timeout = window.setTimeout(() => {
      setMessage('');
    }, 2400);
    return () => window.clearTimeout(timeout);
  }, [message]);

  useEffect(() => {
    setEditingTierOfferingCode(null);
  }, [activeTierId]);

  useEffect(() => {
    setNewTierCustomAddOnName('');
    setNewTierCustomAddOnDescription('');
    setNewTierCustomAddOnPrice('');
    setEditingTierCustomAddOnCode(null);
    setEditingTierCustomAddOnName('');
    setEditingTierCustomAddOnDescription('');
    setEditingTierCustomAddOnPrice('');
  }, [activeTierId]);
  useEffect(() => {
    setNewServiceAddOnName('');
    setNewServiceAddOnDescription('');
    setEditingServiceAddOnCode(null);
    setEditingServiceAddOnName('');
    setEditingServiceAddOnDescription('');
    setNewServiceAddOnVariationDraft({
      parentCode: null,
      code: null,
      name: '',
      description: '',
      price: '',
    });
    setEditingServiceAddOnVariationDraft({
      parentCode: null,
      code: null,
      name: '',
      description: '',
      price: '',
    });
  }, [activeServiceId]);
  const [tierMetaForm, setTierMetaForm] = useState<TierMetaFormState>(createEmptyTierMetaForm());

  const [createServiceForm, setCreateServiceForm] = useState({
    code: '',
    slug: '',
    title: '',
    teaser: '',
    status: 'draft' as CatalogStatus,
  });
  const [showCreateTierComposer, setShowCreateTierComposer] = useState(false);
  const [createTierForm, setCreateTierForm] = useState({
    name: '',
    skuId: '',
    status: 'draft' as CatalogStatus,
    billingType: 'recurring' as 'recurring' | 'one_time',
  });

  const openCreateTierModal = () => {
    setCreateTierForm({
      name: '',
      skuId: '',
      status: 'draft',
      billingType: 'recurring',
    });
    setShowCreateTierComposer(true);
  };

  const closeCreateTierModal = () => {
    if (saving) return;
    setShowCreateTierComposer(false);
  };

  const serviceCards = useMemo(() => deriveServiceCards(services), [services]);

  const activeService = useMemo(
    () => (activeServiceId ? services.find((service) => service.id === activeServiceId) ?? null : null),
    [activeServiceId, services],
  );

  const visibleTiers = useMemo(
    () => (activeService?.tiers ?? []).filter((tier) => isTierVisible(tier) && isPrimaryRecurringTier(tier)),
    [activeService],
  );

  const activeTier = useMemo(() => {
    if (!activeTierId) return null;
    return visibleTiers.find((tier) => tier.id === activeTierId) ?? null;
  }, [activeTierId, visibleTiers]);

  const activeServiceSharedFeatures = useMemo(
    () =>
      parseSharedFeatures(readServiceSharedFeaturesText(activeService?.metadata)).slice(0, 8),
    [activeService],
  );

  const activeTierSummary = useMemo(() => readTierMetaForm(activeTier), [activeTier]);

  const brainNodeById = useMemo(() => {
    const map = new Map<string, BrainNodeOption>();
    for (let index = 0; index < brainNodeOptions.length; index += 1) {
      const option = brainNodeOptions[index];
      map.set(option.id, option);
    }
    return map;
  }, [brainNodeOptions]);

  const buildNodePathFromNodeId = (nodeId: string): string[] => {
    if (!nodeId || !brainNodeById.has(nodeId)) return [];
    const path: string[] = [];
    const visited = new Set<string>();
    let current: string | null = nodeId;
    while (current) {
      if (visited.has(current)) break;
      visited.add(current);
      path.unshift(current);
      const node = brainNodeById.get(current);
      current = node?.parentId ?? null;
    }
    return path;
  };

  // Pending follow-up: this helper is retained for the service-team editor flow.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getTeamDesignationPath = (team: AdditionalTierTeam): string[] => {
    if (team.designationPathIds.length) {
      return team.designationPathIds.filter((nodeId) => brainNodeById.has(nodeId));
    }
    return buildNodePathFromNodeId(team.brainNodeId.trim());
  };

  // Pending follow-up: this helper is retained for the designation picklist flow.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mapNodesToPicklistOptions = (nodes: BrainNodeOption[]): Array<GlassPicklistOption<string>> =>
    nodes.map((node) => ({
      value: node.id,
      label: `${node.title}${node.designationType ? ` (${node.designationType})` : ''}${
        node.teamLabel ? ` • Team: ${node.teamLabel}` : ''
      }`,
    }));
  const brainRoutingAssignments = useMemo<BrainRoutingAssignment[]>(() => {
    const assignments: BrainRoutingAssignment[] = [];
    for (let index = 0; index < brainNodeOptions.length; index += 1) {
      const node = brainNodeOptions[index];
      const routingRule = readNodeRoutingRule(node.rules);
      if (!routingRule) continue;
      assignments.push({
        nodeId: node.id,
        serviceCode: routingRule.serviceCode,
        tierSkuId: routingRule.tierSkuId,
        nodeTitle: node.title,
        designationType: node.designationType,
        teamLabel: node.teamLabel,
        owner: node.appointedAgent,
        ownerUid: node.appointedAgentUid,
        ownerEmail: node.appointedAgentEmail,
        ownerAvatarUrl: node.appointedAgentAvatarUrl,
      });
    }
    return assignments;
  }, [brainNodeOptions]);

  const validBrainRoutingAssignments = useMemo(
    () => brainRoutingAssignments.filter((assignment) => Boolean(normalizeRoutingKey(assignment.teamLabel))),
    [brainRoutingAssignments],
  );

  const invalidBrainRoutingAssignments = useMemo(
    () => brainRoutingAssignments.filter((assignment) => !normalizeRoutingKey(assignment.teamLabel)),
    [brainRoutingAssignments],
  );

  const serviceRoutingTeams = useMemo(() => {
    if (!activeService) return [] as BrainRoutingAssignment[];
    const targetServiceCode = normalizeRoutingKey(activeService.code);
    if (!targetServiceCode) return [] as BrainRoutingAssignment[];
    const seen = new Set<string>();
    return validBrainRoutingAssignments
      .filter(
        (assignment) =>
          normalizeRoutingKey(assignment.serviceCode) === targetServiceCode &&
          !normalizeRoutingKey(assignment.tierSkuId),
      )
      .filter((assignment) => {
        if (seen.has(assignment.nodeId)) return false;
        seen.add(assignment.nodeId);
        return true;
      });
  }, [activeService, validBrainRoutingAssignments]);

  const invalidServiceRoutingTeams = useMemo(() => {
    if (!activeService) return [] as BrainRoutingAssignment[];
    const targetServiceCode = normalizeRoutingKey(activeService.code);
    if (!targetServiceCode) return [] as BrainRoutingAssignment[];
    return invalidBrainRoutingAssignments.filter(
      (assignment) =>
        normalizeRoutingKey(assignment.serviceCode) === targetServiceCode &&
        !normalizeRoutingKey(assignment.tierSkuId),
    );
  }, [activeService, invalidBrainRoutingAssignments]);

  const tierSpecificRoutingTeams = useMemo(() => {
    if (!activeService || !activeTier) return [] as BrainRoutingAssignment[];
    const targetServiceCode = normalizeRoutingKey(activeService.code);
    const targetTierSkuId = normalizeRoutingKey(activeTier.skuId);
    if (!targetServiceCode || !targetTierSkuId) return [] as BrainRoutingAssignment[];
    const seen = new Set<string>();
    return validBrainRoutingAssignments
      .filter(
        (assignment) =>
          normalizeRoutingKey(assignment.serviceCode) === targetServiceCode &&
          normalizeRoutingKey(assignment.tierSkuId) === targetTierSkuId,
      )
      .filter((assignment) => {
        if (seen.has(assignment.nodeId)) return false;
        seen.add(assignment.nodeId);
        return true;
      });
  }, [activeService, activeTier, validBrainRoutingAssignments]);

  const invalidTierRoutingTeams = useMemo(() => {
    if (!activeService || !activeTier) return [] as BrainRoutingAssignment[];
    const targetServiceCode = normalizeRoutingKey(activeService.code);
    const targetTierSkuId = normalizeRoutingKey(activeTier.skuId);
    if (!targetServiceCode || !targetTierSkuId) return [] as BrainRoutingAssignment[];
    return invalidBrainRoutingAssignments.filter(
      (assignment) =>
        normalizeRoutingKey(assignment.serviceCode) === targetServiceCode &&
        normalizeRoutingKey(assignment.tierSkuId) === targetTierSkuId,
    );
  }, [activeService, activeTier, invalidBrainRoutingAssignments]);

  const effectiveTierRoutingTeams = useMemo(
    () => (tierSpecificRoutingTeams.length ? tierSpecificRoutingTeams : serviceRoutingTeams),
    [serviceRoutingTeams, tierSpecificRoutingTeams],
  );

  useEffect(() => {
    if (!activeService) {
      setActiveTierId(null);
      setServiceMetaEditing(false);
      setServiceSlaEditing(false);
      setServiceMetaForm({
        title: '',
        slug: '',
        status: 'draft',
        checkoutEnabled: true,
        teaser: '',
        subtitle: '',
        description: '',
        sharedFeatures: '',
      });
      setServiceSlaBlockDrafts(createDefaultTierSlaBlockDrafts());
      setServiceDiscountsForm([]);
      return;
    }

    setServiceMetaEditing(false);
    setServiceSlaEditing(false);
    setServiceMetaForm({
      title: activeService.title ?? '',
      slug: activeService.slug ?? '',
      status: activeService.status ?? 'draft',
      checkoutEnabled: Boolean(activeService.checkoutEnabled),
      teaser: activeService.teaser ?? '',
      subtitle: activeService.subtitle ?? '',
      description: activeService.description ?? '',
      sharedFeatures: readServiceSharedFeaturesText(activeService.metadata),
    });
    setServiceSlaBlockDrafts(readServiceSlaBlockDrafts(activeService.metadata));
    setServiceDiscountsForm(readDiscountFormItems(activeService.metadata));

    if (!visibleTiers.length) {
      setActiveTierId(null);
      return;
    }
    const stillValid = activeTierId ? visibleTiers.some((tier) => tier.id === activeTierId) : true;
    if (!stillValid) setActiveTierId(null);
  }, [activeService, activeTierId, visibleTiers]);

  const loadCatalog = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch<AdminCatalogResponse>('/api/admin/services/config');
      const next = response.services ?? [];
      setServices(next);
      if (activeServiceId && !next.some((service) => service.id === activeServiceId)) {
        setActiveServiceId(null);
      }
    } catch (loadError: any) {
      setError(loadError?.message ?? 'Failed to load service catalog.');
    } finally {
      setLoading(false);
    }
  };

  const loadBrainNodes = async () => {
    setBrainLoading(true);
    try {
      const response = await apiFetch<OrganizationTreeResponse>('/api/admin/organization-tree');
      const normalized = (response.nodes ?? [])
        .map((node) => {
          const meta = node.meta && typeof node.meta === 'object' ? node.meta : {};
          const designationType = safeString((meta as Record<string, unknown>).designation).trim();
          const teamLabel = safeString((meta as Record<string, unknown>).team).trim();
          return {
            id: node.id,
            parentId: node.parentId,
            title: safeString(node.title).trim(),
            designationType,
            teamLabel,
            rules: node.rules && typeof node.rules === 'object' && !Array.isArray(node.rules)
              ? (node.rules as Record<string, unknown>)
              : undefined,
            appointedAgent: safeString((meta as Record<string, unknown>).appointedAgent).trim(),
            appointedAgentUid: safeString((meta as Record<string, unknown>).appointedAgentUid).trim(),
            appointedAgentEmail: safeString((meta as Record<string, unknown>).appointedAgentEmail).trim(),
            appointedAgentAvatarUrl: safeString(
              (meta as Record<string, unknown>).appointedAgentAvatarUrl,
            ).trim(),
            status: node.status,
          } as BrainNodeOption;
        })
        .filter((node) => node.status === 'active' && node.title.length > 0);
      setBrainNodeOptions(normalized);
    } catch (brainError) {
      console.error('Failed to load organization tree nodes for services mapping.', brainError);
      setBrainNodeOptions([]);
    } finally {
      setBrainLoading(false);
    }
  };

  useEffect(() => {
    void loadCatalog();
    void loadBrainNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeTier) {
      setTierMetaEditing(false);
      setTierMetaForm(createEmptyTierMetaForm());
      setTierDiscountsForm([]);
      return;
    }
    setTierMetaEditing(false);
    setTierMetaForm(readTierMetaForm(activeTier));
    setTierDiscountsForm(readDiscountFormItems(activeTier.metadata));
  }, [activeService, activeTier]);

  useEffect(() => {
    setShowCreateTierComposer(false);
  }, [activeServiceId]);

  useEffect(() => {
    if (!showCreateTierComposer) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeCreateTierModal();
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateTierComposer, saving]);

  const onCreateService = async (event: FormEvent) => {
    event.preventDefault();
    if (!createServiceForm.code.trim() || !createServiceForm.slug.trim() || !createServiceForm.title.trim()) {
      setError('Code, slug, and title are required.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await apiFetch<AdminCatalogResponse>('/api/admin/services', {
        method: 'POST',
        body: JSON.stringify({
          code: createServiceForm.code.trim(),
          slug: createServiceForm.slug.trim(),
          title: createServiceForm.title.trim(),
          subtitle: '',
          description: '',
          teaser: createServiceForm.teaser.trim(),
          checkoutEnabled: true,
          status: createServiceForm.status,
          metadata: {},
        }),
      });
      const next = response.services ?? [];
      setServices(next);
      const created =
        next.find((service) => service.slug === createServiceForm.slug.trim()) ?? null;
      setActiveServiceId(created?.id ?? null);
      setShowCreateComposer(false);
      setMessage('Service created.');
    } catch (createError: any) {
      setError(createError?.message ?? 'Failed to create service.');
    } finally {
      setSaving(false);
    }
  };

  const onCreateTier = async (event: FormEvent) => {
    event.preventDefault();
    if (!activeService) return;
    if (!createTierForm.name.trim() || !createTierForm.skuId.trim()) {
      setError('Tier name and SKU are required.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}/tiers`, {
        method: 'POST',
        body: JSON.stringify({
          name: createTierForm.name.trim(),
          skuId: createTierForm.skuId.trim(),
          status: createTierForm.status,
          billingType: createTierForm.billingType,
          billingInterval: createTierForm.billingType === 'recurring' ? 'month' : undefined,
          amountCents: 0,
          currency: 'usd',
          isPrimaryService: true,
          sortOrder: (activeService.tiers?.length ?? 0) + 1,
          description: '',
          metadata: {},
        }),
      });
      const next = response.services ?? [];
      setServices(next);

      const updatedService = next.find((service) => service.id === activeService.id) ?? null;
      const createdTier = updatedService?.tiers.find((tier) => tier.skuId === createTierForm.skuId.trim()) ?? null;
      if (createdTier?.id) {
        setActiveTierId(createdTier.id);
      }
      setTierPanelTab('meta');
      setShowCreateTierComposer(false);
      setCreateTierForm({
        name: '',
        skuId: '',
        status: 'draft',
        billingType: 'recurring',
      });
      setMessage('Tier created.');
    } catch (createError: any) {
      setError(createError?.message ?? 'Failed to create tier.');
    } finally {
      setSaving(false);
    }
  };

  const serviceOfferingsCatalog = useMemo(
    () => readServiceOfferingsCatalog(activeService),
    [activeService],
  );
  const serviceAddOnsCatalog = useMemo(
    () => readServiceAddOnsCatalog(activeService),
    [activeService],
  );
  const serviceAddOnVariationCount = useMemo(
    () =>
      serviceAddOnsCatalog.reduce((total, addOn) => total + addOn.variations.length, 0),
    [serviceAddOnsCatalog],
  );
  const serviceExclusionsCatalog = useMemo(
    () => readServiceExclusionsCatalog(activeService),
    [activeService],
  );
  const tierIncludedOfferingCodes = useMemo(
    () => readTierIncludedOfferingCodes(activeTier, serviceOfferingsCatalog),
    [activeTier, serviceOfferingsCatalog],
  );
  const tierIncludedExclusionCodes = useMemo(
    () => readTierIncludedExclusionCodes(activeTier, serviceExclusionsCatalog),
    [activeTier, serviceExclusionsCatalog],
  );
  const tierAddOnOfferingCodes = useMemo(
    () =>
      serviceOfferingsCatalog
        .filter((offering) => tierIncludedOfferingCodes.includes(offering.code) && offering.addOnEnabled)
        .map((offering) => offering.code),
    [serviceOfferingsCatalog, tierIncludedOfferingCodes],
  );
  const tierStandardOfferingCodes = useMemo(
    () => tierIncludedOfferingCodes.filter((code) => !tierAddOnOfferingCodes.includes(code)),
    [tierIncludedOfferingCodes, tierAddOnOfferingCodes],
  );
  const tierOfferingOverrides = useMemo(
    () => readTierOfferingOverrides(activeTier, serviceOfferingsCatalog),
    [activeTier, serviceOfferingsCatalog],
  );
  const tierAddOnSettings = useMemo(
    () => readTierAddOnSettings(activeTier, serviceOfferingsCatalog),
    [activeTier, serviceOfferingsCatalog],
  );
  const tierOfferingSlaOverrides = useMemo(
    () => readTierOfferingSlaOverrides(activeTier, serviceOfferingsCatalog),
    [activeTier, serviceOfferingsCatalog],
  );
  const serviceSlaPoliciesByBlock = useMemo(
    () => readServiceSlaPoliciesByBlock(activeService?.metadata),
    [activeService],
  );
  const effectiveTierOfferingSlaByCode = useMemo(() => {
    const map = new Map<string, ReturnType<typeof computeEffectiveOfferingSla>>();
    serviceOfferingsCatalog.forEach((offering) => {
      const defaultServicePolicy = offering.addOnEnabled
        ? serviceSlaPoliciesByBlock.add_ons
        : serviceSlaPoliciesByBlock.offerings;
      map.set(
        offering.code,
        computeEffectiveOfferingSla({
          serviceSla: defaultServicePolicy,
          offeringSla: null,
          tierOverride: tierOfferingSlaOverrides[offering.code],
        }),
      );
    });
    return map;
  }, [serviceOfferingsCatalog, serviceSlaPoliciesByBlock, tierOfferingSlaOverrides]);
  const showInitialCards = !activeService;

  const buildTierSlaBlockDrafts = useCallback((): TierSlaBlockDrafts => {
    const selectRepresentative = (codes: string[]) => {
      const code = codes[0];
      if (!code) return createDefaultTierSlaFormState();
      const effective = effectiveTierOfferingSlaByCode.get(code);
      if (!effective) return createDefaultTierSlaFormState();
      return toTierOfferingSlaFormState(effective, tierOfferingSlaOverrides[code]);
    };

    const ticketingOverride = readTierTicketingSlaOverride(activeTier?.metadata);
    const ticketingEffective = computeEffectiveOfferingSla({
      serviceSla: serviceSlaPoliciesByBlock.ticketing,
      offeringSla: null,
      tierOverride: ticketingOverride,
    });

    return {
      offerings: selectRepresentative(
        tierStandardOfferingCodes.length ? tierStandardOfferingCodes : tierIncludedOfferingCodes,
      ),
      add_ons: selectRepresentative(tierAddOnOfferingCodes),
      ticketing: toTierOfferingSlaFormState(ticketingEffective, ticketingOverride),
    };
  }, [
    activeTier?.metadata,
    effectiveTierOfferingSlaByCode,
    tierOfferingSlaOverrides,
    tierStandardOfferingCodes,
    tierIncludedOfferingCodes,
    tierAddOnOfferingCodes,
    serviceSlaPoliciesByBlock,
  ]);

  const updateTierSlaBlockDraft = (
    block: TierSlaBlockKey,
    patch: Partial<TierOfferingSlaFormState>,
  ) => {
    setTierSlaBlockDrafts((previous) => ({
      ...previous,
      [block]: {
        ...previous[block],
        ...patch,
      },
    }));
  };

  useEffect(() => {
    if (!activeTier) {
      setTierSlaBlockDrafts(createDefaultTierSlaBlockDrafts());
      setTierSlaEditing(false);
      return;
    }
    setTierSlaBlockDrafts(buildTierSlaBlockDrafts());
    setTierSlaEditing(false);
  }, [activeTier, buildTierSlaBlockDrafts]);

  const saveServiceMeta = async () => {
    if (!activeService) return;
    if (!serviceMetaForm.title.trim() || !serviceMetaForm.slug.trim()) {
      setError('Service title and slug are required.');
      return;
    }
    setServiceMetaBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: serviceMetaForm.title.trim(),
          slug: serviceMetaForm.slug.trim(),
          status: serviceMetaForm.status,
          checkoutEnabled: serviceMetaForm.checkoutEnabled,
          teaser: serviceMetaForm.teaser.trim(),
          subtitle: serviceMetaForm.subtitle.trim(),
          description: serviceMetaForm.description.trim(),
          metadata: {
            ...(activeService.metadata ?? {}),
            sharedFeatures: parseSharedFeatures(serviceMetaForm.sharedFeatures),
          },
        }),
      });
      setServices(response.services ?? []);
      setServiceMetaEditing(false);
      setMessage('Service metadata updated.');
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update service metadata.');
    } finally {
      setServiceMetaBusy(false);
    }
  };

  const saveServiceMetadata = async (
    metadataPatch: Record<string, unknown>,
    successMessage: string,
  ) => {
    if (!activeService) return;
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        metadata: {
          ...(activeService.metadata ?? {}),
          ...metadataPatch,
        },
      }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const saveServiceSla = async () => {
    if (!activeService) return;
    setServiceSlaBusy(true);
    setError('');
    setMessage('');
    try {
      const offeringsPolicy = toServiceSlaPolicyFromDraft(serviceSlaBlockDrafts.offerings);
      const addOnsPolicy = toServiceSlaPolicyFromDraft(serviceSlaBlockDrafts.add_ons);
      const ticketingPolicy = toServiceSlaPolicyFromDraft(serviceSlaBlockDrafts.ticketing);
      await saveServiceMetadata(
        {
          serviceSlaBlocks: {
            offerings: offeringsPolicy,
            add_ons: addOnsPolicy,
            ticketing: ticketingPolicy,
          },
          // Backward compatibility for existing readers.
          serviceSla: offeringsPolicy,
          ticketingSla: ticketingPolicy,
        },
        'Service SLA updated.',
      );
      setServiceSlaEditing(false);
    } catch (saveError: any) {
      setError(saveError?.message ?? 'Failed to update service SLA.');
    } finally {
      setServiceSlaBusy(false);
    }
  };

  const addServiceDiscount = () => {
    setServiceDiscountsForm((previous) => [...previous, createNewDiscountFormItem(previous.length)]);
  };

  const updateServiceDiscount = (
    id: string,
    updater: (previous: DiscountFormItem) => DiscountFormItem,
  ) => {
    setServiceDiscountsForm((previous) =>
      previous.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const removeServiceDiscount = (id: string) => {
    setServiceDiscountsForm((previous) => previous.filter((item) => item.id !== id));
  };

  const saveServiceDiscounts = async () => {
    if (!activeService) return;
    setServiceDiscountsBusy(true);
    setError('');
    setMessage('');
    try {
      await saveServiceMetadata(
        {
          discounts: serializeDiscountFormItems(serviceDiscountsForm),
        },
        'Service discounts updated.',
      );
      setServiceDiscountsForm(
        readDiscountFormItems({
          ...(activeService.metadata ?? {}),
          discounts: serializeDiscountFormItems(serviceDiscountsForm),
        }),
      );
    } catch (saveError: any) {
      setError(saveError?.message ?? 'Failed to update service discounts.');
    } finally {
      setServiceDiscountsBusy(false);
    }
  };

  const addTierDiscount = () => {
    setTierDiscountsForm((previous) => [...previous, createNewDiscountFormItem(previous.length)]);
  };

  const updateTierDiscount = (
    id: string,
    updater: (previous: DiscountFormItem) => DiscountFormItem,
  ) => {
    setTierDiscountsForm((previous) =>
      previous.map((item) => (item.id === id ? updater(item) : item)),
    );
  };

  const removeTierDiscount = (id: string) => {
    setTierDiscountsForm((previous) => previous.filter((item) => item.id !== id));
  };

  const saveTierDiscounts = async () => {
    if (!activeTier) return;
    setTierDiscountsBusy(true);
    setError('');
    setMessage('');
    try {
      const nextDiscounts = serializeDiscountFormItems(tierDiscountsForm);
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          metadata: {
            ...(activeTier.metadata ?? {}),
            discounts: nextDiscounts,
          },
        }),
      });
      setServices(response.services ?? []);
      setTierDiscountsForm(
        readDiscountFormItems({
          ...(activeTier.metadata ?? {}),
          discounts: nextDiscounts,
        }),
      );
      setMessage('Tier discounts updated.');
    } catch (saveError: any) {
      setError(saveError?.message ?? 'Failed to update tier discounts.');
    } finally {
      setTierDiscountsBusy(false);
    }
  };

  const saveTierMetadataPatch = async (
    metadataPatch: Record<string, unknown>,
    successMessage: string,
  ) => {
    if (!activeTier) return;
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        metadata: {
          ...(activeTier.metadata ?? {}),
          ...metadataPatch,
        },
      }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const saveTierMeta = async () => {
    if (!activeTier) return;
    if (!tierMetaForm.name.trim() || !tierMetaForm.skuId.trim()) {
      setError('Tier name and SKU ID are required.');
      return;
    }
    setTierMetaBusy(true);
    setError('');
    setMessage('');
    try {
      const toNumberOrNull = (value: string) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
      };
      const amountCents = parseUsdToCents(tierMetaForm.amountUsd);
      if (amountCents === null) {
        setError('Price must use currency format like $2500.00');
        setTierMetaBusy(false);
        return;
      }
      const sortOrder = Number.parseInt(tierMetaForm.sortOrder, 10);
      const deliveryHoursMin = toNumberOrNull(tierMetaForm.deliveryHoursMin);
      const deliveryHoursMax = toNumberOrNull(tierMetaForm.deliveryHoursMax);
      const activeLanesRaw = tierMetaForm.activeLanesIncluded.trim();
      const activeLanesParsed = Number.parseInt(activeLanesRaw, 10);
      const activeLanesIncluded =
        activeLanesRaw.length === 0
          ? null
          : Number.isFinite(activeLanesParsed)
            ? Math.max(0, activeLanesParsed)
            : activeLanesRaw;
      const nextMetadata: Record<string, unknown> = {
        ...(activeTier.metadata ?? {}),
        minTermMonths: toNumberOrNull(tierMetaForm.minTermMonths),
        deliveryHours: {
          min: deliveryHoursMin,
          max: deliveryHoursMax,
        },
        activeLanesIncluded,
        clientSuccessCadenceType: tierMetaForm.clientSuccessCadenceType,
        clientSuccessMeetingsPerMonth: toNumberOrNull(tierMetaForm.clientSuccessMeetingsPerMonth),
        advancedLanesAccess: tierMetaForm.advancedLanesAccess.trim(),
        specialtyLanesAccess: tierMetaForm.specialtyLanesAccess.trim(),
      };
      delete nextMetadata.clientSuccessCadence;
      // Onboarding is part of monthly billable hours; remove legacy separate fee field.
      delete nextMetadata.onboardingFeeCents;
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: tierMetaForm.name.trim(),
          skuId: tierMetaForm.skuId.trim(),
          status: tierMetaForm.status,
          amountCents,
          billingType: tierMetaForm.billingType || undefined,
          billingInterval: tierMetaForm.billingInterval || undefined,
          currency: (tierMetaForm.currency || 'usd').toLowerCase(),
          isPrimaryService: tierMetaForm.isPrimaryService,
          sortOrder: Number.isFinite(sortOrder) ? Math.max(0, sortOrder) : activeTier.sortOrder ?? 0,
          description: tierMetaForm.description,
          metadata: nextMetadata,
        }),
      });
      setServices(response.services ?? []);
      setTierMetaEditing(false);
      setMessage('Tier metadata updated.');
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update tier metadata.');
    } finally {
      setTierMetaBusy(false);
    }
  };

  const deleteTier = async () => {
    if (!activeTier) return;
    if (!window.confirm(`Delete tier "${activeTier.name}"? This will archive it.`)) return;
    setTierDeleteBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'archived',
        }),
      });
      setServices(response.services ?? []);
      setMessage('Tier deleted.');
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Failed to delete tier.');
    } finally {
      setTierDeleteBusy(false);
    }
  };

  const deleteService = async () => {
    if (!activeService) return;
    if (!window.confirm(`Delete service "${activeService.title}"? This will archive it.`)) return;
    setServiceDeleteBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'archived',
        }),
      });
      setServices(response.services ?? []);
      setActiveServiceId(null);
      setActiveTierId(null);
      setServicePanelTab('meta');
      setMessage('Service deleted.');
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Failed to delete service.');
    } finally {
      setServiceDeleteBusy(false);
    }
  };

  const saveServiceOfferingsCatalog = async (
    nextCatalog: ServiceOfferingTemplate[],
    successMessage: string,
  ) => {
    if (!activeService) return;
    const normalizedCatalog = nextCatalog.map((entry, index) => ({
      code: entry.code,
      name: normalizeOfferingName(entry.name),
      description: entry.description.trim(),
      status: 'published' as CatalogStatus,
      sortOrder: index,
      type: normalizeServiceOfferingType(entry.type),
      addOnEnabled: Boolean(entry.addOnEnabled),
      unitPriceCents: entry.unitPriceCents ?? null,
      subcategories: normalizeServiceOfferingSubcategories(entry.subcategories ?? []),
      sla: normalizeOfferingSlaPolicy(entry.sla),
    }));
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        metadata: {
          ...(activeService.metadata ?? {}),
          offeringsCatalogVersion: 2,
          offeringsCatalog: normalizedCatalog,
        },
      }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const saveServiceAddOnsCatalog = async (
    nextCatalog: ServiceAddOnTemplate[],
    successMessage: string,
  ) => {
    if (!activeService) return;
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        metadata: {
          ...(activeService.metadata ?? {}),
          ...toServiceAddOnsCatalogMetadata(nextCatalog),
        },
      }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const saveServiceExclusionsCatalog = async (
    nextCatalog: ServiceExclusionTemplate[],
    successMessage: string,
  ) => {
    if (!activeService) return;
    const normalizedCatalog = nextCatalog.map((entry, index) => ({
      code: entry.code,
      name: normalizeExclusionName(entry.name),
      description: entry.description.trim(),
      status: 'published' as CatalogStatus,
      sortOrder: index,
    }));
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/${activeService.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        metadata: {
          ...(activeService.metadata ?? {}),
          exclusionsCatalogVersion: 1,
          exclusionsCatalog: normalizedCatalog,
          exclusions: normalizedCatalog.map((entry) => entry.name),
        },
      }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const createServiceOffering = async () => {
    if (!activeService) return;
    const name = normalizeOfferingName(newServiceOfferingName);
    if (!name) return;
    setServiceOfferingBusyCode('new');
    setError('');
    setMessage('');
    try {
      const existingCodes = new Set(serviceOfferingsCatalog.map((entry) => entry.code));
      const base = toOfferingCode(name);
      let code = base;
      let suffix = 2;
      while (existingCodes.has(code)) {
        code = `${base}_${suffix}`;
        suffix += 1;
      }
      const nextCatalog = [
        ...serviceOfferingsCatalog,
        {
          code,
          name,
          description: '',
          status: 'published' as CatalogStatus,
          sortOrder: serviceOfferingsCatalog.length,
          type: 'core' as ServiceOfferingType,
          addOnEnabled: false,
          unitPriceCents: null,
          subcategories: [],
          sla: normalizeOfferingSlaPolicy(null),
        },
      ];
      await saveServiceOfferingsCatalog(nextCatalog, 'Offering created.');
      setNewServiceOfferingName('');
    } catch (createError: any) {
      setError(createError?.message ?? 'Failed to create offering.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const createServiceAddOn = async () => {
    if (!activeService) return;
    const name = normalizeOfferingName(newServiceAddOnName);
    if (!name) {
      setError('Add-on name is required.');
      return;
    }
    setServiceOfferingBusyCode('service-addon-new');
    setError('');
    setMessage('');
    try {
      const existingCodes = new Set(serviceAddOnsCatalog.map((entry) => entry.code));
      const code = createServiceAddOnCode(existingCodes, name);
      const nextCatalog = [
        ...serviceAddOnsCatalog,
        {
          code,
          name,
          description: newServiceAddOnDescription.trim(),
          status: 'published' as CatalogStatus,
          sortOrder: serviceAddOnsCatalog.length,
          variations: [],
        },
      ];
      await saveServiceAddOnsCatalog(nextCatalog, 'Add-on created.');
      setNewServiceAddOnName('');
      setNewServiceAddOnDescription('');
    } catch (createError: any) {
      setError(createError?.message ?? 'Failed to create add-on.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const startEditServiceAddOn = (addOn: ServiceAddOnTemplate) => {
    setEditingServiceAddOnCode(addOn.code);
    setEditingServiceAddOnName(addOn.name);
    setEditingServiceAddOnDescription(addOn.description ?? '');
  };

  const saveServiceAddOnEdit = async (addOnCode: string) => {
    const name = normalizeOfferingName(editingServiceAddOnName);
    if (!name) {
      setError('Add-on name is required.');
      return;
    }
    setServiceOfferingBusyCode(`service-addon-${addOnCode}`);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceAddOnsCatalog.map((entry) =>
        entry.code === addOnCode
          ? {
              ...entry,
              name,
              description: editingServiceAddOnDescription.trim(),
            }
          : entry,
      );
      await saveServiceAddOnsCatalog(nextCatalog, 'Add-on updated.');
      setEditingServiceAddOnCode(null);
      setEditingServiceAddOnName('');
      setEditingServiceAddOnDescription('');
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update add-on.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const deleteServiceAddOn = async (addOnCode: string) => {
    setServiceOfferingBusyCode(`service-addon-${addOnCode}`);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceAddOnsCatalog.filter((entry) => entry.code !== addOnCode);
      await saveServiceAddOnsCatalog(nextCatalog, 'Add-on removed.');
      if (editingServiceAddOnCode === addOnCode) {
        setEditingServiceAddOnCode(null);
        setEditingServiceAddOnName('');
        setEditingServiceAddOnDescription('');
      }
      if (newServiceAddOnVariationDraft.parentCode === addOnCode) {
        setNewServiceAddOnVariationDraft({
          parentCode: null,
          code: null,
          name: '',
          description: '',
          price: '',
        });
      }
      if (editingServiceAddOnVariationDraft.parentCode === addOnCode) {
        setEditingServiceAddOnVariationDraft({
          parentCode: null,
          code: null,
          name: '',
          description: '',
          price: '',
        });
      }
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Failed to remove add-on.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const beginCreateServiceAddOnVariation = (parentCode: string) => {
    setNewServiceAddOnVariationDraft({
      parentCode,
      code: null,
      name: '',
      description: '',
      price: '',
    });
  };

  const createServiceAddOnVariation = async (parentCode: string) => {
    const parent = serviceAddOnsCatalog.find((entry) => entry.code === parentCode);
    if (!parent) return;
    const name = normalizeOfferingName(newServiceAddOnVariationDraft.name);
    if (!name) {
      setError('Variation name is required.');
      return;
    }
    const unitPriceCents = parseUsdToCents(newServiceAddOnVariationDraft.price);
    if (unitPriceCents === null) {
      setError('Variation price must use currency format like $250.00');
      return;
    }
    setServiceOfferingBusyCode(`service-addon-variation-new-${parentCode}`);
    setError('');
    setMessage('');
    try {
      const existingCodes = new Set(parent.variations.map((entry) => entry.code));
      const code = createServiceAddOnVariationCode(existingCodes, name);
      const nextCatalog = serviceAddOnsCatalog.map((entry) =>
        entry.code === parentCode
          ? {
              ...entry,
              variations: [
                ...entry.variations,
                {
                  code,
                  name,
                  description: newServiceAddOnVariationDraft.description.trim(),
                  status: 'published' as CatalogStatus,
                  sortOrder: entry.variations.length,
                  unitPriceCents,
                },
              ],
            }
          : entry,
      );
      await saveServiceAddOnsCatalog(nextCatalog, 'Add-on variation created.');
      setNewServiceAddOnVariationDraft({
        parentCode: null,
        code: null,
        name: '',
        description: '',
        price: '',
      });
    } catch (createError: any) {
      setError(createError?.message ?? 'Failed to create add-on variation.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const startEditServiceAddOnVariation = (
    parentCode: string,
    variation: ServiceAddOnVariationTemplate,
  ) => {
    setEditingServiceAddOnVariationDraft({
      parentCode,
      code: variation.code,
      name: variation.name,
      description: variation.description ?? '',
      price: formatUsdFromCents(variation.unitPriceCents),
    });
  };

  const saveServiceAddOnVariationEdit = async (parentCode: string, variationCode: string) => {
    const name = normalizeOfferingName(editingServiceAddOnVariationDraft.name);
    if (!name) {
      setError('Variation name is required.');
      return;
    }
    const unitPriceCents = parseUsdToCents(editingServiceAddOnVariationDraft.price);
    if (unitPriceCents === null) {
      setError('Variation price must use currency format like $250.00');
      return;
    }
    setServiceOfferingBusyCode(`service-addon-variation-${variationCode}`);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceAddOnsCatalog.map((entry) =>
        entry.code === parentCode
          ? {
              ...entry,
              variations: entry.variations.map((variation) =>
                variation.code === variationCode
                  ? {
                      ...variation,
                      name,
                      description: editingServiceAddOnVariationDraft.description.trim(),
                      unitPriceCents,
                    }
                  : variation,
              ),
            }
          : entry,
      );
      await saveServiceAddOnsCatalog(nextCatalog, 'Add-on variation updated.');
      setEditingServiceAddOnVariationDraft({
        parentCode: null,
        code: null,
        name: '',
        description: '',
        price: '',
      });
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update add-on variation.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const deleteServiceAddOnVariation = async (parentCode: string, variationCode: string) => {
    setServiceOfferingBusyCode(`service-addon-variation-${variationCode}`);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceAddOnsCatalog.map((entry) =>
        entry.code === parentCode
          ? {
              ...entry,
              variations: entry.variations.filter((variation) => variation.code !== variationCode),
            }
          : entry,
      );
      await saveServiceAddOnsCatalog(nextCatalog, 'Add-on variation removed.');
      if (editingServiceAddOnVariationDraft.parentCode === parentCode &&
          editingServiceAddOnVariationDraft.code === variationCode) {
        setEditingServiceAddOnVariationDraft({
          parentCode: null,
          code: null,
          name: '',
          description: '',
          price: '',
        });
      }
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Failed to remove add-on variation.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const createServiceExclusion = async () => {
    if (!activeService) return;
    const name = normalizeExclusionName(newServiceExclusionName);
    if (!name) {
      setError('Exclusion name is required.');
      return;
    }
    setServiceExclusionBusyCode('new');
    setError('');
    setMessage('');
    try {
      const existingCodes = new Set(serviceExclusionsCatalog.map((entry) => entry.code));
      const base = toExclusionCode(name);
      let code = base;
      let suffix = 2;
      while (existingCodes.has(code)) {
        code = `${base}_${suffix}`;
        suffix += 1;
      }
      const nextCatalog = [
        ...serviceExclusionsCatalog,
        {
          code,
          name,
          description: newServiceExclusionDescription.trim(),
          status: 'published' as CatalogStatus,
          sortOrder: serviceExclusionsCatalog.length,
        },
      ];
      await saveServiceExclusionsCatalog(nextCatalog, 'Exclusion created.');
      setNewServiceExclusionName('');
      setNewServiceExclusionDescription('');
    } catch (createError: any) {
      setError(createError?.message ?? 'Failed to create exclusion.');
    } finally {
      setServiceExclusionBusyCode(null);
    }
  };

  const quickUpdateServiceOfferingType = async (
    offeringCode: string,
    nextType: ServiceOfferingType,
  ) => {
    if (!activeService) return;
    setServiceOfferingBusyCode(offeringCode);
    setError('');
    setMessage('');
    try {
      const normalizedType = normalizeServiceOfferingType(nextType);
      const nextCatalog = serviceOfferingsCatalog.map((entry) =>
        entry.code === offeringCode
          ? {
              ...entry,
              type: normalizedType,
            }
          : entry,
      );
      await saveServiceOfferingsCatalog(nextCatalog, 'Offering type updated.');
      if (editingServiceOfferingCode === offeringCode) {
        setEditingServiceOfferingType(normalizedType);
      }
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update offering type.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const startEditServiceOffering = (offering: ServiceOfferingTemplate) => {
    setEditingServiceOfferingCode(offering.code);
    setEditingServiceOfferingName(offering.name);
    setEditingServiceOfferingDescription(offering.description ?? '');
    setEditingServiceOfferingType(offering.type);
    setEditingServiceOfferingSubcategories(offering.subcategories ?? []);
    setExpandedServiceOfferingCodes((previous) =>
      previous.includes(offering.code) ? previous : [...previous, offering.code],
    );
  };

  const toggleServiceOfferingExpansion = (offeringCode: string) => {
    setExpandedServiceOfferingCodes((previous) =>
      previous.includes(offeringCode)
        ? previous.filter((code) => code !== offeringCode)
        : [...previous, offeringCode],
    );
  };

  const addEditingServiceOfferingSubcategory = () => {
    setEditingServiceOfferingSubcategories((previous) => [
      ...previous,
      {
        id: createServiceOfferingSubcategoryId(),
        name: '',
        description: '',
      },
    ]);
  };

  const updateEditingServiceOfferingSubcategory = (
    id: string,
    field: 'name' | 'description' | 'estimatedMinutesOverride',
    value: string,
  ) => {
    setEditingServiceOfferingSubcategories((previous) =>
      previous.map((subcategory) =>
        subcategory.id === id
          ? {
              ...subcategory,
              [field]:
                field === 'estimatedMinutesOverride'
                  ? parseNonNegativeIntOrNull(value)
                  : value,
            }
          : subcategory,
      ),
    );
  };

  const removeEditingServiceOfferingSubcategory = (id: string) => {
    setEditingServiceOfferingSubcategories((previous) =>
      previous.filter((subcategory) => subcategory.id !== id),
    );
  };


  const startEditServiceExclusion = (exclusion: ServiceExclusionTemplate) => {
    setEditingServiceExclusionCode(exclusion.code);
    setEditingServiceExclusionName(exclusion.name);
    setEditingServiceExclusionDescription(exclusion.description ?? '');
  };

  const saveServiceOfferingEdit = async (offeringCode: string) => {
    const name = normalizeOfferingName(editingServiceOfferingName);
    if (!name) {
      setError('Offering name is required.');
      return;
    }
    const existingEntry = serviceOfferingsCatalog.find((entry) => entry.code === offeringCode);
    if (hasIncompleteServiceOfferingSubcategories(editingServiceOfferingSubcategories)) {
      setError('Each subcategory needs a name before you save.');
      return;
    }
    const normalizedSubcategories = normalizeServiceOfferingSubcategories(editingServiceOfferingSubcategories);
    setServiceOfferingBusyCode(offeringCode);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceOfferingsCatalog.map((entry) =>
        entry.code === offeringCode
          ? {
              ...entry,
              name,
              description: editingServiceOfferingDescription.trim(),
              type: normalizeServiceOfferingType(editingServiceOfferingType),
              addOnEnabled: Boolean(existingEntry?.addOnEnabled),
              unitPriceCents: existingEntry?.unitPriceCents ?? null,
              subcategories: normalizedSubcategories,
              sla: normalizeOfferingSlaPolicy(entry.sla),
            }
          : entry,
      );
      await saveServiceOfferingsCatalog(nextCatalog, 'Offering updated.');
      setEditingServiceOfferingCode(null);
      setEditingServiceOfferingName('');
      setEditingServiceOfferingDescription('');
      setEditingServiceOfferingType('core');
      setEditingServiceOfferingSubcategories([]);
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update offering.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const deleteServiceOffering = async (offeringCode: string) => {
    setServiceOfferingBusyCode(offeringCode);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceOfferingsCatalog.filter((entry) => entry.code !== offeringCode);
      await saveServiceOfferingsCatalog(nextCatalog, 'Offering removed.');
      if (editingServiceOfferingCode === offeringCode) {
        setEditingServiceOfferingCode(null);
        setEditingServiceOfferingName('');
        setEditingServiceOfferingDescription('');
        setEditingServiceOfferingType('core');
        setEditingServiceOfferingSubcategories([]);
      }
      setExpandedServiceOfferingCodes((previous) => previous.filter((code) => code !== offeringCode));
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Failed to remove offering.');
    } finally {
      setServiceOfferingBusyCode(null);
    }
  };

  const saveServiceExclusionEdit = async (exclusionCode: string) => {
    const name = normalizeExclusionName(editingServiceExclusionName);
    if (!name) {
      setError('Exclusion name is required.');
      return;
    }
    setServiceExclusionBusyCode(exclusionCode);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceExclusionsCatalog.map((entry) =>
        entry.code === exclusionCode
          ? {
              ...entry,
              name,
              description: editingServiceExclusionDescription.trim(),
            }
          : entry,
      );
      await saveServiceExclusionsCatalog(nextCatalog, 'Exclusion updated.');
      setEditingServiceExclusionCode(null);
      setEditingServiceExclusionName('');
      setEditingServiceExclusionDescription('');
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update exclusion.');
    } finally {
      setServiceExclusionBusyCode(null);
    }
  };

  const deleteServiceExclusion = async (exclusionCode: string) => {
    setServiceExclusionBusyCode(exclusionCode);
    setError('');
    setMessage('');
    try {
      const nextCatalog = serviceExclusionsCatalog.filter((entry) => entry.code !== exclusionCode);
      await saveServiceExclusionsCatalog(nextCatalog, 'Exclusion removed.');
      if (editingServiceExclusionCode === exclusionCode) {
        setEditingServiceExclusionCode(null);
        setEditingServiceExclusionName('');
        setEditingServiceExclusionDescription('');
      }
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Failed to remove exclusion.');
    } finally {
      setServiceExclusionBusyCode(null);
    }
  };

  const reorderServiceOfferings = async (sourceCode: string, targetCode: string) => {
    if (!activeService) return;
    if (!sourceCode || !targetCode || sourceCode === targetCode) return;
    const sourceIndex = serviceOfferingsCatalog.findIndex((entry) => entry.code === sourceCode);
    const targetIndex = serviceOfferingsCatalog.findIndex((entry) => entry.code === targetCode);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return;

    const nextCatalog = [...serviceOfferingsCatalog];
    const [moved] = nextCatalog.splice(sourceIndex, 1);
    if (!moved) return;
    nextCatalog.splice(targetIndex, 0, moved);

    setServiceOfferingReordering(true);
    setError('');
    setMessage('');
    try {
      await saveServiceOfferingsCatalog(nextCatalog, 'Offering order updated.');
    } catch (reorderError: any) {
      setError(reorderError?.message ?? 'Failed to reorder offerings.');
    } finally {
      setServiceOfferingReordering(false);
      setDraggingOfferingCode(null);
      setDropTargetOfferingCode(null);
    }
  };

  const saveTierOfferingState = async (
    nextIncludedCodes: string[],
    nextOverrides: Record<string, TierOfferingOverride>,
    successMessage = 'Tier offerings updated.',
  ) => {
    if (!activeTier) return;
    const includedSet = new Set(nextIncludedCodes);
    const validCodeSet = new Set(serviceOfferingsCatalog.map((entry) => entry.code));
    const currentSlaOverrides = normalizeTierOfferingSlaOverrides(
      activeTier.metadata?.tierOfferingSlaOverrides,
      validCodeSet,
    );
    const nextSlaOverrides = Object.fromEntries(
      Object.entries(currentSlaOverrides).filter(([code]) => includedSet.has(code)),
    );
    const metadata: Record<string, unknown> = {
      ...(activeTier.metadata ?? {}),
      includedServiceOfferingCodes: nextIncludedCodes,
      tierOfferingOverrides: nextOverrides,
      tierOfferingSlaOverrides: nextSlaOverrides,
    };
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        metadata,
      }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const saveTierOfferingSlaOverrides = async (
    nextOverrides: Record<string, TierOfferingSlaOverride>,
    successMessage = 'Tier offering SLA updated.',
    metadataPatch: Record<string, unknown> = {},
  ) => {
    if (!activeTier) return;
    const validCodes = new Set(serviceOfferingsCatalog.map((entry) => entry.code));
    const includedSet = new Set(tierIncludedOfferingCodes);
    const normalized = normalizeTierOfferingSlaOverrides(nextOverrides, validCodes);
    const filtered = Object.fromEntries(
      Object.entries(normalized).filter(([code]) => includedSet.has(code)),
    );
    const metadata: Record<string, unknown> = {
      ...(activeTier.metadata ?? {}),
      tierOfferingSlaOverrides: filtered,
      ...metadataPatch,
    };
    const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ metadata }),
    });
    setServices(response.services ?? []);
    setMessage(successMessage);
  };

  const toggleTierExclusionInclusion = async (exclusionCode: string) => {
    if (!activeTier) return;
    setTierExclusionBusyCode(exclusionCode);
    setError('');
    setMessage('');
    try {
      const selectedCodes = new Set(tierIncludedExclusionCodes);
      if (selectedCodes.has(exclusionCode)) {
        selectedCodes.delete(exclusionCode);
      } else {
        selectedCodes.add(exclusionCode);
      }
      await saveTierMetadataPatch(
        {
          includedExclusionCodes: Array.from(selectedCodes),
          exclusions: serviceExclusionsCatalog
            .filter((entry) => selectedCodes.has(entry.code))
            .map((entry) => entry.name),
        },
        'Tier exclusions updated.',
      );
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update tier exclusions.');
    } finally {
      setTierExclusionBusyCode(null);
    }
  };

  const updateTierOfferingOverride = async (
    offeringCode: string,
    patch: Partial<TierOfferingOverride>,
    successMessage = 'Tier offering rule updated.',
  ) => {
    if (!activeTier) return;
    setTierOfferingBusyCode(offeringCode);
    setError('');
    setMessage('');
    try {
      const currentOverride = tierOfferingOverrides[offeringCode] ?? {
        incentive: '',
      };
      const nextOverride: TierOfferingOverride = {
        ...currentOverride,
        ...patch,
      };
      const orderedCodes = serviceOfferingsCatalog
        .map((entry) => entry.code)
        .filter((code) => tierIncludedOfferingCodes.includes(code));
      const nextOverrides: Record<string, TierOfferingOverride> = {
        ...tierOfferingOverrides,
        [offeringCode]: nextOverride,
      };
      await saveTierOfferingState(orderedCodes, nextOverrides, successMessage);
    } catch (updateError: any) {
      setError(updateError?.message ?? 'Failed to update tier offering rule.');
    } finally {
      setTierOfferingBusyCode(null);
    }
  };

  const saveTierAddOnSettings = async (
    nextSettings: TierAddOnSettings,
    successMessage: string,
    busyCode: string | null = 'all',
  ) => {
    if (!activeTier) return;
    setTierOfferingBusyCode(busyCode);
    setError('');
    setMessage('');
    try {
      const metadata: Record<string, unknown> = {
        ...(activeTier.metadata ?? {}),
        tierAddOnSettings: toTierAddOnSettingsMetadata(nextSettings),
      };
      const response = await apiFetch<AdminCatalogResponse>(`/api/admin/services/tiers/${activeTier.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ metadata }),
      });
      setServices(response.services ?? []);
      setMessage(successMessage);
    } catch (saveError: any) {
      setError(saveError?.message ?? 'Failed to update tier add-on settings.');
    } finally {
      setTierOfferingBusyCode(null);
    }
  };

  const startEditTierCustomAddOn = (addOn: TierCustomAddOn) => {
    setEditingTierCustomAddOnCode(addOn.code);
    setEditingTierCustomAddOnName(addOn.name);
    setEditingTierCustomAddOnDescription(addOn.description ?? '');
    setEditingTierCustomAddOnPrice(
      addOn.unitPriceCents === null ? '' : formatUsdFromCents(addOn.unitPriceCents),
    );
  };

  const createTierCustomAddOn = async () => {
    if (!activeTier) return;
    const name = normalizeOfferingName(newTierCustomAddOnName);
    if (!name) {
      setError('Add-on name is required.');
      return;
    }
    const unitPriceCents = parseUsdToCents(newTierCustomAddOnPrice);
    if (unitPriceCents === null) {
      setError('Add-on price must use currency format like $250.00');
      return;
    }
    const existingCodes = new Set(tierAddOnSettings.customAddOns.map((entry) => entry.code));
    const nextAddOn: TierCustomAddOn = {
      code: createTierCustomAddOnCode(existingCodes, name),
      name,
      description: newTierCustomAddOnDescription.trim(),
      unitPriceCents,
      sortOrder: tierAddOnSettings.customAddOns.length,
    };
    const nextSettings: TierAddOnSettings = {
      maxAddOnsPerTier: tierAddOnSettings.maxAddOnsPerTier,
      presets: {
        ...tierAddOnSettings.presets,
      },
      customAddOns: [...tierAddOnSettings.customAddOns, nextAddOn],
    };
    await saveTierAddOnSettings(nextSettings, 'Tier-specific add-on created.', 'tier-custom-new');
    setNewTierCustomAddOnName('');
    setNewTierCustomAddOnDescription('');
    setNewTierCustomAddOnPrice('');
  };

  const saveTierCustomAddOnEdit = async (code: string) => {
    if (!activeTier) return;
    const name = normalizeOfferingName(editingTierCustomAddOnName);
    if (!name) {
      setError('Add-on name is required.');
      return;
    }
    const unitPriceCents = parseUsdToCents(editingTierCustomAddOnPrice);
    if (unitPriceCents === null) {
      setError('Add-on price must use currency format like $250.00');
      return;
    }
    const nextSettings: TierAddOnSettings = {
      maxAddOnsPerTier: tierAddOnSettings.maxAddOnsPerTier,
      presets: {
        ...tierAddOnSettings.presets,
      },
      customAddOns: tierAddOnSettings.customAddOns.map((entry, index) =>
        entry.code === code
          ? {
              ...entry,
              name,
              description: editingTierCustomAddOnDescription.trim(),
              unitPriceCents,
              sortOrder: index,
            }
          : entry,
      ),
    };
    await saveTierAddOnSettings(nextSettings, 'Tier-specific add-on updated.', `tier-custom-${code}`);
    setEditingTierCustomAddOnCode(null);
    setEditingTierCustomAddOnName('');
    setEditingTierCustomAddOnDescription('');
    setEditingTierCustomAddOnPrice('');
  };

  const deleteTierCustomAddOn = async (code: string) => {
    if (!activeTier) return;
    const nextSettings: TierAddOnSettings = {
      maxAddOnsPerTier: tierAddOnSettings.maxAddOnsPerTier,
      presets: {
        ...tierAddOnSettings.presets,
      },
      customAddOns: tierAddOnSettings.customAddOns
        .filter((entry) => entry.code !== code)
        .map((entry, index) => ({
          ...entry,
          sortOrder: index,
        })),
    };
    await saveTierAddOnSettings(nextSettings, 'Tier-specific add-on removed.', `tier-custom-${code}`);
    if (editingTierCustomAddOnCode === code) {
      setEditingTierCustomAddOnCode(null);
      setEditingTierCustomAddOnName('');
      setEditingTierCustomAddOnDescription('');
      setEditingTierCustomAddOnPrice('');
    }
  };

  const saveTierSlaBlocks = async () => {
    if (!activeTier) return;
    setTierSlaBusyCode('all');
    setError('');
    setMessage('');
    try {
      const validCodes = new Set(serviceOfferingsCatalog.map((entry) => entry.code));
      const nextOverrides = {
        ...normalizeTierOfferingSlaOverrides(activeTier.metadata?.tierOfferingSlaOverrides, validCodes),
      };

      const applyOverrideForCodes = (codes: string[], draft: TierOfferingSlaFormState) => {
        const normalizedCodes = codes.filter((code) => validCodes.has(code));
        if (!normalizedCodes.length) return;
        const nextOverride = toTierSlaOverrideFromDraft(draft);
        if (hasTierSlaOverrideValues(nextOverride)) {
          normalizedCodes.forEach((code) => {
            nextOverrides[code] = nextOverride;
          });
          return;
        }
        normalizedCodes.forEach((code) => {
          delete nextOverrides[code];
        });
      };

      const standardCodes = tierStandardOfferingCodes.length
        ? tierStandardOfferingCodes
        : tierIncludedOfferingCodes;
      applyOverrideForCodes(standardCodes, tierSlaBlockDrafts.offerings);
      applyOverrideForCodes(tierAddOnOfferingCodes, tierSlaBlockDrafts.add_ons);

      const ticketingOverride = toTierSlaOverrideFromDraft(tierSlaBlockDrafts.ticketing);
      const metadataPatch: Record<string, unknown> = {
        ticketingSla: hasTierSlaOverrideValues(ticketingOverride) ? ticketingOverride : null,
        ticketingWorkflow: {
          startTrigger: 'on_assignment',
          activeStatuses: ['open', 'in_progress'],
          pausedStatuses: ['blocked'],
          completeStatuses: ['resolved'],
        },
      };

      await saveTierOfferingSlaOverrides(nextOverrides, 'Tier SLA rules updated.', metadataPatch);
      setTierSlaEditing(false);
    } catch (saveError: any) {
      setError(saveError?.message ?? 'Failed to update tier SLA rules.');
    } finally {
      setTierSlaBusyCode(null);
    }
  };

  const updateServiceSlaBlockDraft = (
    block: TierSlaBlockKey,
    patch: Partial<TierOfferingSlaFormState>,
  ) => {
    setServiceSlaBlockDrafts((previous) => ({
      ...previous,
      [block]: {
        ...previous[block],
        ...patch,
      },
    }));
  };

  const renderServiceSlaBlockCard = (
    block: TierSlaBlockKey,
    title: string,
    linkedLabel: string,
    hint?: string,
  ) => {
    const draft = serviceSlaBlockDrafts[block];
    return (
      <OfferingCard key={`service-sla-block-${block}`}>
        <OfferingTopRow>
          <OfferingTitle>{title}</OfferingTitle>
          <OfferingMetaRow>
            <OfferingMetaChip>{linkedLabel}</OfferingMetaChip>
          </OfferingMetaRow>
        </OfferingTopRow>
        {hint ? <SubsectionHint>{hint}</SubsectionHint> : null}
        {block === 'ticketing' ? (
          <SubsectionHint>
            SLA starts when a ticket is assigned to an agent. SLA tracks while ticket is in Planning/In Progress, pauses in Blockers, and stops when Complete.
          </SubsectionHint>
        ) : null}
        {serviceSlaEditing ? (
          <MetaGrid>
            <MetaField>
              Response target (hours)
              <FieldHint>Maximum hours allowed to send first response after SLA start.</FieldHint>
              <MetaInput
                type="number"
                min={0.25}
                step={0.25}
                value={draft.responseTargetMinutes}
                onChange={(event) =>
                  updateServiceSlaBlockDraft(block, {
                    responseTargetMinutes: event.target.value,
                  })
                }
                disabled={serviceSlaBusy}
              />
            </MetaField>
            <MetaField>
              Resolution target (hours)
              <FieldHint>Maximum hours allowed to resolve the ticket after SLA start.</FieldHint>
              <MetaInput
                type="number"
                min={0.25}
                step={0.25}
                value={draft.resolutionTargetMinutes}
                onChange={(event) =>
                  updateServiceSlaBlockDraft(block, {
                    resolutionTargetMinutes: event.target.value,
                  })
                }
                disabled={serviceSlaBusy}
              />
            </MetaField>
            <MetaField>
              Goal completion (hours)
              <FieldHint>Internal delivery goal for this request category. Used for capacity and performance analytics.</FieldHint>
              <MetaInput
                type="number"
                min={0.25}
                step={0.25}
                value={draft.targetCompletionMinutes}
                onChange={(event) =>
                  updateServiceSlaBlockDraft(block, {
                    targetCompletionMinutes: event.target.value,
                  })
                }
                disabled={serviceSlaBusy}
              />
            </MetaField>
            <MetaField>
              Escalation after (hours)
              <FieldHint>Maximum hours before escalation should automatically trigger.</FieldHint>
              <MetaInput
                type="number"
                min={0.25}
                step={0.25}
                value={draft.escalationAfterMinutes}
                onChange={(event) =>
                  updateServiceSlaBlockDraft(block, {
                    escalationAfterMinutes: event.target.value,
                  })
                }
                disabled={serviceSlaBusy}
              />
            </MetaField>
            <MetaField>
              Escalation scope
              <FieldHint>Choose whether escalation applies to first response, resolution, or both.</FieldHint>
              <GlassPicklist<EscalationScope>
                value={draft.escalationScope}
                options={ESCALATION_SCOPE_OPTIONS}
                onChange={(nextValue) =>
                  updateServiceSlaBlockDraft(block, {
                    escalationScope: nextValue,
                  })
                }
                ariaLabel={`${title} escalation scope`}
                disabled={serviceSlaBusy}
              />
            </MetaField>
            <MetaField>
              Queue weight
              <FieldHint>Priority bucket for queue ordering. Higher priority is processed first.</FieldHint>
              <GlassPicklist<string>
                value={draft.queueWeight}
                options={QUEUE_WEIGHT_OPTIONS}
                onChange={(nextValue) =>
                  updateServiceSlaBlockDraft(block, {
                    queueWeight: nextValue,
                  })
                }
                ariaLabel={`${title} queue weight`}
                disabled={serviceSlaBusy}
              />
            </MetaField>
          </MetaGrid>
        ) : (
          <OfferingMetaRow>
            <OfferingMetaChip>Response {draft.responseTargetMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Resolution {draft.resolutionTargetMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Goal {draft.targetCompletionMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Escalation {draft.escalationAfterMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Scope {formatTokenLabel(draft.escalationScope)}</OfferingMetaChip>
            <OfferingMetaChip>Queue {queueWeightLabel(draft.queueWeight)}</OfferingMetaChip>
          </OfferingMetaRow>
        )}
      </OfferingCard>
    );
  };

  const renderTierSlaBlockCard = (
    block: TierSlaBlockKey,
    title: string,
    linkedLabel: string,
    hint?: string,
  ) => {
    const draft = tierSlaBlockDrafts[block];
    return (
      <OfferingCard key={`tier-sla-block-${block}`}>
        <OfferingTopRow>
          <OfferingTitle>{title}</OfferingTitle>
          <OfferingMetaRow>
            <OfferingMetaChip>{linkedLabel}</OfferingMetaChip>
          </OfferingMetaRow>
        </OfferingTopRow>
        {hint ? <SubsectionHint>{hint}</SubsectionHint> : null}
        {block === 'ticketing' ? (
          <SubsectionHint>
            SLA starts when a ticket is assigned to an agent. SLA tracks while ticket is in Planning/In Progress, pauses in Blockers, and stops when Complete.
          </SubsectionHint>
        ) : null}
        {tierSlaEditing ? (
          <>
            <MetaGrid>
              <MetaField>
                Response target (hours)
                <FieldHint>Maximum hours allowed to send first response after SLA start.</FieldHint>
                <MetaInput
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={draft.responseTargetMinutes}
                  onChange={(event) =>
                    updateTierSlaBlockDraft(block, {
                      responseTargetMinutes: event.target.value,
                    })
                  }
                  disabled={Boolean(tierSlaBusyCode)}
                />
              </MetaField>
              <MetaField>
                Resolution target (hours)
                <FieldHint>Maximum hours allowed to resolve the ticket after SLA start.</FieldHint>
                <MetaInput
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={draft.resolutionTargetMinutes}
                  onChange={(event) =>
                    updateTierSlaBlockDraft(block, {
                      resolutionTargetMinutes: event.target.value,
                    })
                  }
                  disabled={Boolean(tierSlaBusyCode)}
                />
              </MetaField>
              <MetaField>
                Goal completion (hours)
                <FieldHint>Internal delivery goal for this request category. Used for capacity and performance analytics.</FieldHint>
                <MetaInput
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={draft.targetCompletionMinutes}
                  onChange={(event) =>
                    updateTierSlaBlockDraft(block, {
                      targetCompletionMinutes: event.target.value,
                    })
                  }
                  disabled={Boolean(tierSlaBusyCode)}
                />
              </MetaField>
              <MetaField>
                Escalation after (hours)
                <FieldHint>Maximum hours before escalation should automatically trigger.</FieldHint>
                <MetaInput
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={draft.escalationAfterMinutes}
                  onChange={(event) =>
                    updateTierSlaBlockDraft(block, {
                      escalationAfterMinutes: event.target.value,
                    })
                  }
                  disabled={Boolean(tierSlaBusyCode)}
                />
              </MetaField>
              <MetaField>
                Escalation scope
                <FieldHint>Choose whether escalation applies to first response, resolution, or both.</FieldHint>
                <GlassPicklist<EscalationScope>
                  value={draft.escalationScope}
                  options={ESCALATION_SCOPE_OPTIONS}
                  onChange={(nextValue) =>
                    updateTierSlaBlockDraft(block, {
                      escalationScope: nextValue,
                    })
                  }
                  ariaLabel={`${title} escalation scope`}
                  disabled={Boolean(tierSlaBusyCode)}
                />
              </MetaField>
              <MetaField>
                Queue weight
                <FieldHint>Priority bucket for queue ordering. Higher priority is processed first.</FieldHint>
                <GlassPicklist<string>
                  value={draft.queueWeight}
                  options={QUEUE_WEIGHT_OPTIONS}
                  onChange={(nextValue) =>
                    updateTierSlaBlockDraft(block, {
                      queueWeight: nextValue,
                    })
                  }
                  ariaLabel={`${title} queue weight`}
                  disabled={Boolean(tierSlaBusyCode)}
                />
              </MetaField>
            </MetaGrid>
          </>
        ) : (
          <OfferingMetaRow>
            <OfferingMetaChip>Response {draft.responseTargetMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Resolution {draft.resolutionTargetMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Goal {draft.targetCompletionMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Escalation {draft.escalationAfterMinutes || '--'}h</OfferingMetaChip>
            <OfferingMetaChip>Scope {formatTokenLabel(draft.escalationScope)}</OfferingMetaChip>
            <OfferingMetaChip>Queue {queueWeightLabel(draft.queueWeight)}</OfferingMetaChip>
          </OfferingMetaRow>
        )}
      </OfferingCard>
    );
  };

  return (
    <Page>
      <ToastStack>
        {loading ? <Toast>Loading services...</Toast> : null}
        {message ? <Toast>{message}</Toast> : null}
        {error ? <Toast $error>{error}</Toast> : null}
      </ToastStack>

      <Shell>
        <ShellHeader>
          <HeaderText>
            <Title>Services</Title>
            <Subtitle>
              Service parent bins, tier structures, and offering visibility.
            </Subtitle>
          </HeaderText>
          <HeaderButtons>
            <Button
              type="button"
              $ghost
              onClick={() => {
                void loadCatalog();
                void loadBrainNodes();
              }}
              disabled={loading || saving || brainLoading}
            >
              Refresh
            </Button>
          </HeaderButtons>
        </ShellHeader>
        {!loading && showInitialCards ? (
          <InitialStage>
            <CardsGrid $centered>
              {serviceCards.map((service) => {
                const active = service.id === activeServiceId;
                return (
                  <ServiceCard
                    key={service.id}
                    type="button"
                    $active={active}
                    onClick={() => {
                      setShowCreateComposer(false);
                      setActiveServiceId(service.id);
                    }}
                  >
                    <ServiceCode>{service.code}</ServiceCode>
                    <ServiceTitle>{service.title}</ServiceTitle>
                    <ServiceTeaser>
                      {service.teaser || service.subtitle || service.description || 'Service details'}
                    </ServiceTeaser>
                    <CardMeta>
                      <MetaChip>{service.status}</MetaChip>
                      <MetaChip>{countVisibleTiers(service)} tiers</MetaChip>
                      <MetaChip>
                        {countVisibleOfferings(service)} offerings
                      </MetaChip>
                    </CardMeta>
                  </ServiceCard>
                );
              })}

              {!serviceCards.length ? (
                <EmptyState>No services found yet. Create your first service parent bin.</EmptyState>
              ) : null}

              <AddServiceCard
                type="button"
                onClick={() => {
                  setCreateServiceForm({
                    code: '',
                    slug: '',
                    title: '',
                    teaser: '',
                    status: 'draft',
                  });
                  setShowCreateComposer(true);
                  setActiveServiceId(null);
                }}
              >
                <AddCircle>
                  <Plus size={18} />
                </AddCircle>
                <AddLabel>Add service</AddLabel>
                <AddSub>Create another service parent bin</AddSub>
              </AddServiceCard>
            </CardsGrid>

            {showCreateComposer ? (
              <Composer onSubmit={onCreateService}>
                <FormGrid>
                  <Label>
                    Code
                    <Input
                      value={createServiceForm.code}
                      onChange={(event) =>
                        setCreateServiceForm((previous) => ({ ...previous, code: event.target.value }))
                      }
                      placeholder="PLATFORM_DEV"
                    />
                  </Label>
                  <Label>
                    Slug
                    <Input
                      value={createServiceForm.slug}
                      onChange={(event) =>
                        setCreateServiceForm((previous) => ({ ...previous, slug: event.target.value }))
                      }
                      placeholder="platform-development"
                    />
                  </Label>
                  <Label>
                    Title
                    <Input
                      value={createServiceForm.title}
                      onChange={(event) =>
                        setCreateServiceForm((previous) => ({ ...previous, title: event.target.value }))
                      }
                      placeholder="Service title"
                    />
                  </Label>
                  <Label>
                    Teaser
                    <Input
                      value={createServiceForm.teaser}
                      onChange={(event) =>
                        setCreateServiceForm((previous) => ({ ...previous, teaser: event.target.value }))
                      }
                      placeholder="One-line service summary"
                    />
                  </Label>
                  <Label>
                    Status
                    <Select
                      value={createServiceForm.status}
                      onChange={(event) =>
                        setCreateServiceForm((previous) => ({
                          ...previous,
                          status: event.target.value as CatalogStatus,
                        }))
                      }
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Select>
                  </Label>
                </FormGrid>
                <HeaderButtons>
                  <Button type="submit" disabled={saving}>
                    Create service
                  </Button>
                  <Button
                    type="button"
                    $ghost
                    onClick={() => setShowCreateComposer(false)}
                    disabled={saving}
                  >
                    Close
                  </Button>
                </HeaderButtons>
              </Composer>
            ) : null}
          </InitialStage>
        ) : null}

        {!loading && activeService ? (
          <Workspace>
            <LeftRail>
              <BackButton
                type="button"
                onClick={() => {
                  setActiveServiceId(null);
                  setActiveTierId(null);
                }}
              >
                <ChevronLeft size={14} />
                View All Services
              </BackButton>
              <SelectedCardShell>
                <SummaryPanel>
                  <SummaryHeader>
                    <SummaryEyebrow>{activeTier ? 'Tier summary' : 'Service summary'}</SummaryEyebrow>
                    <SummaryHeading>{activeTier ? activeTierSummary.name : activeService.title}</SummaryHeading>
                    <SummarySub>
                      {activeTier
                        ? activeTierSummary.description ||
                          `Tier in ${activeService.title}`
                        : activeService.teaser ||
                          activeService.subtitle ||
                          activeService.description ||
                          'Service details'}
                    </SummarySub>
                  </SummaryHeader>

                  {!activeTier ? (
                    <>
                      <SummarySection>
                        <SummarySectionTitle>Overview</SummarySectionTitle>
                        <SummaryRow>
                          <SummaryLabel>Service code</SummaryLabel>
                          <SummaryValue>{activeService.code}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Status</SummaryLabel>
                          <SummaryValue>{activeService.status}</SummaryValue>
                        </SummaryRow>
                      </SummarySection>

                      <SummarySection>
                        <SummarySectionTitle>Catalog</SummarySectionTitle>
                        <SummaryRow>
                          <SummaryLabel>Total tiers</SummaryLabel>
                          <SummaryValue>{countVisibleTiers(activeService)}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Total offerings</SummaryLabel>
                          <SummaryValue>{countVisibleOfferings(activeService)}</SummaryValue>
                        </SummaryRow>
                      </SummarySection>

                      {activeServiceSharedFeatures.length ? (
                        <SummarySection>
                          <SummarySectionTitle>Shared features</SummarySectionTitle>
                          <SummaryList>
                            {activeServiceSharedFeatures.map((feature) => (
                              <SummaryListItem key={feature}>{feature}</SummaryListItem>
                            ))}
                          </SummaryList>
                        </SummarySection>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <SummarySection>
                        <SummarySectionTitle>Overview</SummarySectionTitle>
                        <SummaryRow>
                          <SummaryLabel>SKU ID</SummaryLabel>
                          <SummaryValue>{activeTierSummary.skuId || '--'}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Status</SummaryLabel>
                          <SummaryValue>{activeTierSummary.status}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Service</SummaryLabel>
                          <SummaryValue>{activeService.title}</SummaryValue>
                        </SummaryRow>
                      </SummarySection>

                      <SummarySection>
                        <SummarySectionTitle>Commercial</SummarySectionTitle>
                        <SummaryRow>
                          <SummaryLabel>Price</SummaryLabel>
                          <SummaryValue>{activeTierSummary.amountUsd || '$0.00'}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Billing model</SummaryLabel>
                          <SummaryValue>
                            {formatTokenLabel(activeTierSummary.billingType)}
                            {activeTierSummary.billingInterval
                              ? ` / ${formatTokenLabel(activeTierSummary.billingInterval)}`
                              : ''}
                          </SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Minimum term</SummaryLabel>
                          <SummaryValue>
                            {activeTierSummary.minTermMonths.trim()
                              ? `${activeTierSummary.minTermMonths.trim()} months`
                              : '--'}
                          </SummaryValue>
                        </SummaryRow>
                      </SummarySection>

                      <SummarySection>
                        <SummarySectionTitle>Delivery</SummarySectionTitle>
                        <SummaryRow>
                          <SummaryLabel>Monthly delivery hours</SummaryLabel>
                          <SummaryValue>
                            {activeTierSummary.deliveryHoursMin.trim() || activeTierSummary.deliveryHoursMax.trim()
                              ? `${activeTierSummary.deliveryHoursMin.trim() || '--'} - ${
                                  activeTierSummary.deliveryHoursMax.trim() || '--'
                                }`
                              : '--'}
                          </SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Active lanes included</SummaryLabel>
                          <SummaryValue>{activeTierSummary.activeLanesIncluded.trim() || '--'}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Advanced lanes access</SummaryLabel>
                          <SummaryValue>{activeTierSummary.advancedLanesAccess.trim() || '--'}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Specialty lanes access</SummaryLabel>
                          <SummaryValue>{activeTierSummary.specialtyLanesAccess.trim() || '--'}</SummaryValue>
                        </SummaryRow>
                      </SummarySection>

                      <SummarySection>
                        <SummarySectionTitle>Client success</SummarySectionTitle>
                        <SummaryRow>
                          <SummaryLabel>Cadence</SummaryLabel>
                          <SummaryValue>{formatTokenLabel(activeTierSummary.clientSuccessCadenceType)}</SummaryValue>
                        </SummaryRow>
                        <SummaryRow>
                          <SummaryLabel>Meetings / month</SummaryLabel>
                          <SummaryValue>{activeTierSummary.clientSuccessMeetingsPerMonth.trim() || '--'}</SummaryValue>
                        </SummaryRow>
                      </SummarySection>
                    </>
                  )}
                </SummaryPanel>
              </SelectedCardShell>
            </LeftRail>

            <MainPane>
              <SplitColumns>
                <Panel>
                  <PanelHeader>
                    <PanelTitleSentence>{activeService.title} attributes across all tiers</PanelTitleSentence>
                      <PanelTabs>
                        <PanelTabButton
                          type="button"
                          $active={servicePanelTab === 'meta'}
                        onClick={() => setServicePanelTab('meta')}
                      >
                        Meta data
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={servicePanelTab === 'offerings'}
                        onClick={() => setServicePanelTab('offerings')}
                      >
                        Offerings
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={servicePanelTab === 'add_ons'}
                        onClick={() => setServicePanelTab('add_ons')}
                      >
                        Add-ons
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={servicePanelTab === 'exclusions'}
                        onClick={() => setServicePanelTab('exclusions')}
                      >
                        Exclusions
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={servicePanelTab === 'discounts'}
                        onClick={() => setServicePanelTab('discounts')}
                      >
                        Discounts
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={servicePanelTab === 'sla'}
                        onClick={() => setServicePanelTab('sla')}
                      >
                        SLA
                      </PanelTabButton>
                        <PanelTabButton
                          type="button"
                          $active={servicePanelTab === 'service_team'}
                          onClick={() => setServicePanelTab('service_team')}
                        >
                          Service team
                        </PanelTabButton>
                        <PanelTabButton
                          type="button"
                          $active={servicePanelTab === 'settings'}
                          onClick={() => setServicePanelTab('settings')}
                        >
                          Settings
                        </PanelTabButton>
                      </PanelTabs>
                    </PanelHeader>
                  <PanelBody>
                    {servicePanelTab === 'meta' ? (
                      <MetaForm
                        onSubmit={(event) => {
                          event.preventDefault();
                          if (!serviceMetaEditing) {
                            setServiceMetaEditing(true);
                            return;
                          }
                          void saveServiceMeta();
                        }}
                      >
                        <MetaActionRow>
                          <Button type="submit" disabled={serviceMetaBusy}>
                            {serviceMetaEditing ? 'Save service' : 'Edit service'}
                          </Button>
                          {serviceMetaEditing ? (
                            <Button
                              type="button"
                              $ghost
                              onClick={() => {
                                setServiceMetaEditing(false);
                                setServiceMetaForm({
                                  title: activeService.title ?? '',
                                  slug: activeService.slug ?? '',
                                  status: activeService.status ?? 'draft',
                                  checkoutEnabled: Boolean(activeService.checkoutEnabled),
                                  teaser: activeService.teaser ?? '',
                                  subtitle: activeService.subtitle ?? '',
                                  description: activeService.description ?? '',
                                  sharedFeatures: readServiceSharedFeaturesText(activeService.metadata),
                                });
                              }}
                              disabled={serviceMetaBusy}
                            >
                              Cancel
                            </Button>
                          ) : null}
                        </MetaActionRow>

                        <MetaGrid>
                          <MetaField>
                            Service title
                            <MetaInput
                              value={serviceMetaForm.title}
                              onChange={(event) =>
                                setServiceMetaForm((previous) => ({
                                  ...previous,
                                  title: event.target.value,
                                }))
                              }
                              disabled={!serviceMetaEditing || serviceMetaBusy}
                            />
                          </MetaField>
                          <MetaField>
                            Service code
                            <MetaInput value={activeService.code || ''} disabled />
                          </MetaField>
                          <MetaField>
                            Service slug
                            <MetaInput
                              value={serviceMetaForm.slug}
                              onChange={(event) =>
                                setServiceMetaForm((previous) => ({
                                  ...previous,
                                  slug: event.target.value,
                                }))
                              }
                              disabled={!serviceMetaEditing || serviceMetaBusy}
                            />
                          </MetaField>
                          <MetaField>
                            Status
                            <MetaSelect
                              value={serviceMetaForm.status}
                              onChange={(event) =>
                                setServiceMetaForm((previous) => ({
                                  ...previous,
                                  status: event.target.value as CatalogStatus,
                                }))
                              }
                              disabled={!serviceMetaEditing || serviceMetaBusy}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </MetaSelect>
                          </MetaField>
                        </MetaGrid>

                        <CheckboxRow>
                          <input
                            type="checkbox"
                            checked={serviceMetaForm.checkoutEnabled}
                            onChange={(event) =>
                              setServiceMetaForm((previous) => ({
                                ...previous,
                                checkoutEnabled: event.target.checked,
                              }))
                            }
                            disabled={!serviceMetaEditing || serviceMetaBusy}
                          />
                          Checkout enabled
                        </CheckboxRow>

                        <MetaField>
                          Teaser
                          <MetaInput
                            value={serviceMetaForm.teaser}
                            onChange={(event) =>
                              setServiceMetaForm((previous) => ({
                                ...previous,
                                teaser: event.target.value,
                              }))
                            }
                            disabled={!serviceMetaEditing || serviceMetaBusy}
                          />
                        </MetaField>
                        <MetaField>
                          Subtitle
                          <MetaInput
                            value={serviceMetaForm.subtitle}
                            onChange={(event) =>
                              setServiceMetaForm((previous) => ({
                                ...previous,
                                subtitle: event.target.value,
                              }))
                            }
                            disabled={!serviceMetaEditing || serviceMetaBusy}
                          />
                        </MetaField>
                        <MetaField>
                          Description
                          <MetaTextarea
                            value={serviceMetaForm.description}
                            onChange={(event) =>
                              setServiceMetaForm((previous) => ({
                                ...previous,
                                description: event.target.value,
                              }))
                            }
                            disabled={!serviceMetaEditing || serviceMetaBusy}
                          />
                        </MetaField>
                        <MetaField>
                          Shared features across all tiers (one per line)
                          <MetaTextarea
                            value={serviceMetaForm.sharedFeatures}
                            onChange={(event) =>
                              setServiceMetaForm((previous) => ({
                                ...previous,
                                sharedFeatures: event.target.value,
                              }))
                            }
                            placeholder="Examples: Monthly performance reporting; Dedicated client success owner"
                            disabled={!serviceMetaEditing || serviceMetaBusy}
                          />
                        </MetaField>

                        <TierDetails>
                          <DetailRow>
                            <DetailLabel>Total tiers</DetailLabel>
                            <DetailValue>{countVisibleTiers(activeService)}</DetailValue>
                          </DetailRow>
                          <DetailRow>
                            <DetailLabel>Total offerings</DetailLabel>
                            <DetailValue>{countVisibleOfferings(activeService)}</DetailValue>
                          </DetailRow>
                        </TierDetails>
                      </MetaForm>
                    ) : null}

                    {servicePanelTab === 'offerings' ? (
                      <>
                        <OfferingList>
                          {serviceOfferingsCatalog
                            .filter((offering) => offering.status !== 'archived')
                            .map((offering) => {
                              const includedInActiveTier = tierIncludedOfferingCodes.includes(offering.code);
                              const isEditingOffering = editingServiceOfferingCode === offering.code;
                              const isOfferingExpanded =
                                isEditingOffering || expandedServiceOfferingCodes.includes(offering.code);
                              return (
                              <OfferingCard
                                key={offering.code}
                                $selectable={!isEditingOffering}
                                $selected={includedInActiveTier}
                                $dragging={draggingOfferingCode === offering.code}
                                $dropTarget={dropTargetOfferingCode === offering.code}
                                draggable={
                                  servicePanelTab === 'offerings' &&
                                  !serviceOfferingReordering &&
                                  !serviceOfferingBusyCode &&
                                  !isEditingOffering
                                }
                                onDragStart={(event) => {
                                  if (isEditingOffering || serviceOfferingReordering) {
                                    event.preventDefault();
                                    return;
                                  }
                                  setDraggingOfferingCode(offering.code);
                                  setDropTargetOfferingCode(null);
                                  event.dataTransfer.effectAllowed = 'move';
                                  event.dataTransfer.setData('text/plain', offering.code);
                                }}
                                onDragOver={(event) => {
                                  if (!draggingOfferingCode || draggingOfferingCode === offering.code) return;
                                  event.preventDefault();
                                  setDropTargetOfferingCode(offering.code);
                                }}
                                onDragLeave={() => {
                                  if (dropTargetOfferingCode === offering.code) {
                                    setDropTargetOfferingCode(null);
                                  }
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  const sourceCode =
                                    draggingOfferingCode || event.dataTransfer.getData('text/plain');
                                  if (!sourceCode || sourceCode === offering.code) {
                                    setDropTargetOfferingCode(null);
                                    return;
                                  }
                                  void reorderServiceOfferings(sourceCode, offering.code);
                                }}
                                onDragEnd={() => {
                                  setDraggingOfferingCode(null);
                                  setDropTargetOfferingCode(null);
                                }}
                                onClick={() => {
                                  if (isEditingOffering) return;
                                  toggleServiceOfferingExpansion(offering.code);
                                }}
                              >
                                <OfferingTopRow>
                                  <OfferingTitleGroup>
                                    {!isEditingOffering ? (
                                      <DragHandleSlot>
                                        <DragHandleButton
                                          type="button"
                                          title="Drag to reorder"
                                          onMouseDown={(event) => event.stopPropagation()}
                                          onClick={(event) => event.stopPropagation()}
                                          disabled={
                                            serviceOfferingReordering ||
                                            Boolean(serviceOfferingBusyCode) ||
                                            isEditingOffering
                                          }
                                        >
                                          <GripVertical size={14} />
                                        </DragHandleButton>
                                      </DragHandleSlot>
                                    ) : null}
                                    {isEditingOffering ? (
                                      <OfferingInput
                                        value={editingServiceOfferingName}
                                        onChange={(event) => setEditingServiceOfferingName(event.target.value)}
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                            event.preventDefault();
                                            void saveServiceOfferingEdit(offering.code);
                                          }
                                        }}
                                      />
                                    ) : (
                                      <OfferingTitle>{offering.name}</OfferingTitle>
                                    )}
                                  </OfferingTitleGroup>
                                  <RowActions>
                                    <TypeQuickPick
                                      onMouseDown={(event) => event.stopPropagation()}
                                      onClick={(event) => event.stopPropagation()}
                                    >
                                      <GlassPicklist<ServiceOfferingType>
                                        value={
                                          editingServiceOfferingCode === offering.code
                                            ? editingServiceOfferingType
                                            : normalizeServiceOfferingType(offering.type)
                                        }
                                        options={OFFERING_TYPE_PICKLIST_OPTIONS}
                                        onChange={(nextValue) => {
                                          const normalizedType = normalizeServiceOfferingType(nextValue);
                                          if (editingServiceOfferingCode === offering.code) {
                                            setEditingServiceOfferingType(normalizedType);
                                            return;
                                          }
                                          void quickUpdateServiceOfferingType(offering.code, normalizedType);
                                        }}
                                        ariaLabel={`Quick offering type for ${offering.name}`}
                                        placeholder="Type"
                                        disabled={
                                          serviceOfferingBusyCode === offering.code || serviceOfferingReordering
                                        }
                                      />
                                    </TypeQuickPick>
                                    <IconActionButton
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (isEditingOffering) {
                                          void saveServiceOfferingEdit(offering.code);
                                          return;
                                        }
                                        startEditServiceOffering(offering);
                                      }}
                                      title={
                                        isEditingOffering
                                          ? 'Save offering'
                                          : 'Edit offering'
                                      }
                                      disabled={serviceOfferingBusyCode === offering.code}
                                    >
                                      {isEditingOffering ? (
                                        <Check size={14} />
                                      ) : (
                                        <Pencil size={14} />
                                      )}
                                    </IconActionButton>
                                    <IconActionButton
                                      type="button"
                                      $danger
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void deleteServiceOffering(offering.code);
                                      }}
                                      title="Delete offering"
                                      disabled={serviceOfferingBusyCode === offering.code}
                                    >
                                      <Trash2 size={14} />
                                    </IconActionButton>
                                  </RowActions>
                                </OfferingTopRow>
                                {isEditingOffering ? (
                                  <>
                                    <OfferingInput
                                      value={editingServiceOfferingDescription}
                                      onChange={(event) => setEditingServiceOfferingDescription(event.target.value)}
                                      onClick={(event) => event.stopPropagation()}
                                      placeholder="Description (optional)"
                                    />
                                    <SubcategorySection>
                                      <SubcategoryToolbar>
                                        <FieldHint>Subcategories</FieldHint>
                                        <Button
                                          type="button"
                                          $ghost
                                          onMouseDown={(event) => event.stopPropagation()}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            addEditingServiceOfferingSubcategory();
                                          }}
                                          disabled={serviceOfferingBusyCode === offering.code}
                                        >
                                          Add subcategory
                                        </Button>
                                      </SubcategoryToolbar>
                                      {editingServiceOfferingSubcategories.length ? (
                                        <SubcategoryList>
                                          {editingServiceOfferingSubcategories.map((subcategory) => (
                                            <NestedOfferingCard key={subcategory.id}>
                                              <SubcategoryFields>
                                                <OfferingInput
                                                  value={subcategory.name}
                                                  onChange={(event) =>
                                                    updateEditingServiceOfferingSubcategory(
                                                      subcategory.id,
                                                      'name',
                                                      event.target.value,
                                                    )
                                                  }
                                                  onClick={(event) => event.stopPropagation()}
                                                  placeholder="Subcategory name"
                                                />
                                                <OfferingInput
                                                  value={subcategory.description}
                                                  onChange={(event) =>
                                                    updateEditingServiceOfferingSubcategory(
                                                      subcategory.id,
                                                      'description',
                                                      event.target.value,
                                                    )
                                                  }
                                                  onClick={(event) => event.stopPropagation()}
                                                  placeholder="Short description (optional)"
                                                />
                                                <SubcategoryEstimateInput
                                                  value={
                                                    typeof subcategory.estimatedMinutesOverride === 'number'
                                                      ? String(subcategory.estimatedMinutesOverride)
                                                      : ''
                                                  }
                                                  onChange={(event) =>
                                                    updateEditingServiceOfferingSubcategory(
                                                      subcategory.id,
                                                      'estimatedMinutesOverride',
                                                      event.target.value,
                                                    )
                                                  }
                                                  onClick={(event) => event.stopPropagation()}
                                                  placeholder="Reset estimate mins"
                                                  aria-label="Reset aggregate estimate minutes"
                                                />
                                                <IconActionButton
                                                  type="button"
                                                  $danger
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    removeEditingServiceOfferingSubcategory(subcategory.id);
                                                  }}
                                                  title="Remove subcategory"
                                                  disabled={serviceOfferingBusyCode === offering.code}
                                                >
                                                  <Trash2 size={14} />
                                                </IconActionButton>
                                              </SubcategoryFields>
                                            </NestedOfferingCard>
                                          ))}
                                        </SubcategoryList>
                                      ) : (
                                        <SubcategoryEmptyState>
                                          No subcategories yet. Add one directly to this offering.
                                        </SubcategoryEmptyState>
                                      )}
                                    </SubcategorySection>
                                  </>
                                ) : (
                                  <>
                                    <OfferingDescription>{offering.description || '--'}</OfferingDescription>
                                    {isOfferingExpanded && offering.subcategories.length ? (
                                      <SubcategoryList>
                                        {offering.subcategories.map((subcategory) => (
                                          <ReadOnlySubcategoryCard key={subcategory.id}>
                                            <SubcategoryReadOnlyHeader>
                                              <OfferingTitle>{subcategory.name}</OfferingTitle>
                                              <OfferingDescription>
                                              {subcategory.description || 'No description'}
                                              </OfferingDescription>
                                            </SubcategoryReadOnlyHeader>
                                            <SubcategoryEstimateSummary>
                                              <SubcategoryEstimateLabel>
                                                Estimated Time{' '}
                                                {formatEstimatedTimeLabel(
                                                  subcategory.estimatedMinutes,
                                                )}
                                              </SubcategoryEstimateLabel>
                                              {subcategory.estimateSource === 'seed' ? (
                                                <SubcategoryEstimateMeta>
                                                  Reset baseline active. New completed tickets will refine
                                                  this estimate.
                                                </SubcategoryEstimateMeta>
                                              ) : typeof subcategory.estimateSampleCount === 'number' &&
                                                subcategory.estimateSampleCount > 0 ? (
                                                <SubcategoryEstimateMeta>
                                                  Based on {subcategory.estimateSampleCount} completed ticket
                                                  {subcategory.estimateSampleCount === 1 ? '' : 's'}
                                                </SubcategoryEstimateMeta>
                                              ) : null}
                                            </SubcategoryEstimateSummary>
                                          </ReadOnlySubcategoryCard>
                                        ))}
                                      </SubcategoryList>
                                    ) : null}
                                  </>
                                )}
                              </OfferingCard>
                            );
                            })}
                          <OfferingComposerRow>
                            <OfferingInput
                              value={newServiceOfferingName}
                              onChange={(event) => setNewServiceOfferingName(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  void createServiceOffering();
                                }
                              }}
                              placeholder="Add an offering for this service"
                              disabled={serviceOfferingBusyCode === 'new'}
                            />
                            <RowActions>
                              <IconActionButton
                                type="button"
                                onClick={() => void createServiceOffering()}
                                title="Create offering"
                                disabled={serviceOfferingBusyCode === 'new' || !newServiceOfferingName.trim()}
                              >
                                <Plus size={14} />
                              </IconActionButton>
                            </RowActions>
                          </OfferingComposerRow>
                        </OfferingList>
                      </>
                    ) : null}

                    {servicePanelTab === 'add_ons' ? (
                      <>
                        {serviceAddOnsCatalog.length ? (
                          <OfferingList>
                            {serviceAddOnsCatalog.map((addOn) => {
                              const isEditingParent = editingServiceAddOnCode === addOn.code;
                              const isCreatingVariation = newServiceAddOnVariationDraft.parentCode === addOn.code;
                              return (
                                <OfferingCard key={`service-addon-${addOn.code}`}>
                                  <OfferingTopRow>
                                    <OfferingTitleGroup>
                                      {isEditingParent ? (
                                        <OfferingInput
                                          value={editingServiceAddOnName}
                                          onChange={(event) => setEditingServiceAddOnName(event.target.value)}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                              event.preventDefault();
                                              void saveServiceAddOnEdit(addOn.code);
                                            }
                                          }}
                                        />
                                      ) : (
                                        <OfferingTitle>{addOn.name}</OfferingTitle>
                                      )}
                                    </OfferingTitleGroup>
                                    <OfferingMetaChip>
                                      {addOn.variations.length} variation{addOn.variations.length === 1 ? '' : 's'}
                                    </OfferingMetaChip>
                                    <RowActions>
                                      <IconActionButton
                                        type="button"
                                        onClick={() => {
                                          if (isEditingParent) {
                                            void saveServiceAddOnEdit(addOn.code);
                                            return;
                                          }
                                          startEditServiceAddOn(addOn);
                                        }}
                                        title={isEditingParent ? 'Save add-on' : 'Edit add-on'}
                                        disabled={serviceOfferingBusyCode === `service-addon-${addOn.code}`}
                                      >
                                        {isEditingParent ? <Check size={14} /> : <Pencil size={14} />}
                                      </IconActionButton>
                                      <IconActionButton
                                        type="button"
                                        $danger
                                        onClick={() => void deleteServiceAddOn(addOn.code)}
                                        title="Delete add-on"
                                        disabled={serviceOfferingBusyCode === `service-addon-${addOn.code}`}
                                      >
                                        <Trash2 size={14} />
                                      </IconActionButton>
                                    </RowActions>
                                  </OfferingTopRow>
                                  {isEditingParent ? (
                                    <OfferingInput
                                      value={editingServiceAddOnDescription}
                                      onChange={(event) => setEditingServiceAddOnDescription(event.target.value)}
                                      placeholder="Description (optional)"
                                    />
                                  ) : (
                                    <OfferingDescription>{addOn.description || '--'}</OfferingDescription>
                                  )}
                                  <FieldHint>
                                    Parent add-ons define the upsell family. Child variations hold the priced options
                                    customers can mix and match with any service tier.
                                  </FieldHint>
                                  <NestedOfferingList>
                                    {addOn.variations.map((variation) => {
                                      const isEditingVariation =
                                        editingServiceAddOnVariationDraft.parentCode === addOn.code &&
                                        editingServiceAddOnVariationDraft.code === variation.code;
                                      return (
                                        <NestedOfferingCard key={`${addOn.code}-${variation.code}`}>
                                          <OfferingTopRow>
                                            <OfferingTitleGroup>
                                              {isEditingVariation ? (
                                                <OfferingInput
                                                  value={editingServiceAddOnVariationDraft.name}
                                                  onChange={(event) =>
                                                    setEditingServiceAddOnVariationDraft((previous) => ({
                                                      ...previous,
                                                      name: event.target.value,
                                                    }))
                                                  }
                                                  onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                      event.preventDefault();
                                                      void saveServiceAddOnVariationEdit(addOn.code, variation.code);
                                                    }
                                                  }}
                                                />
                                              ) : (
                                                <OfferingTitle>{variation.name}</OfferingTitle>
                                              )}
                                            </OfferingTitleGroup>
                                            <OfferingMetaChip>
                                              {variation.unitPriceCents === null
                                                ? 'Price unset'
                                                : formatUsdFromCents(variation.unitPriceCents)}
                                            </OfferingMetaChip>
                                            <RowActions>
                                              <IconActionButton
                                                type="button"
                                                onClick={() => {
                                                  if (isEditingVariation) {
                                                    void saveServiceAddOnVariationEdit(addOn.code, variation.code);
                                                    return;
                                                  }
                                                  startEditServiceAddOnVariation(addOn.code, variation);
                                                }}
                                                title={isEditingVariation ? 'Save variation' : 'Edit variation'}
                                                disabled={serviceOfferingBusyCode === `service-addon-variation-${variation.code}`}
                                              >
                                                {isEditingVariation ? <Check size={14} /> : <Pencil size={14} />}
                                              </IconActionButton>
                                              <IconActionButton
                                                type="button"
                                                $danger
                                                onClick={() => void deleteServiceAddOnVariation(addOn.code, variation.code)}
                                                title="Delete variation"
                                                disabled={serviceOfferingBusyCode === `service-addon-variation-${variation.code}`}
                                              >
                                                <Trash2 size={14} />
                                              </IconActionButton>
                                            </RowActions>
                                          </OfferingTopRow>
                                          {isEditingVariation ? (
                                            <>
                                              <OfferingInput
                                                value={editingServiceAddOnVariationDraft.description}
                                                onChange={(event) =>
                                                  setEditingServiceAddOnVariationDraft((previous) => ({
                                                    ...previous,
                                                    description: event.target.value,
                                                  }))
                                                }
                                                placeholder="Description (optional)"
                                              />
                                              <OfferingInput
                                                value={editingServiceAddOnVariationDraft.price}
                                                onChange={(event) =>
                                                  setEditingServiceAddOnVariationDraft((previous) => ({
                                                    ...previous,
                                                    price: event.target.value,
                                                  }))
                                                }
                                                placeholder="Price (USD)"
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <OfferingDescription>{variation.description || '--'}</OfferingDescription>
                                              <FieldHint>
                                                {variation.unitPriceCents === null
                                                  ? 'Price unset'
                                                  : `Checkout price ${formatUsdFromCents(variation.unitPriceCents)}`}
                                              </FieldHint>
                                            </>
                                          )}
                                        </NestedOfferingCard>
                                      );
                                    })}
                                    {isCreatingVariation ? (
                                      <NestedOfferingCard>
                                        <OfferingTopRow>
                                          <OfferingTitle>Create variation</OfferingTitle>
                                          <OfferingMetaChip>Priced entry</OfferingMetaChip>
                                          <RowActions>
                                            <IconActionButton
                                              type="button"
                                              onClick={() => void createServiceAddOnVariation(addOn.code)}
                                              title="Create variation"
                                              disabled={
                                                serviceOfferingBusyCode === `service-addon-variation-new-${addOn.code}` ||
                                                !newServiceAddOnVariationDraft.name.trim() ||
                                                !newServiceAddOnVariationDraft.price.trim()
                                              }
                                            >
                                              <Plus size={14} />
                                            </IconActionButton>
                                          </RowActions>
                                        </OfferingTopRow>
                                        <OfferingInput
                                          value={newServiceAddOnVariationDraft.name}
                                          onChange={(event) =>
                                            setNewServiceAddOnVariationDraft((previous) => ({
                                              ...previous,
                                              name: event.target.value,
                                            }))
                                          }
                                          placeholder="Variation name"
                                        />
                                        <OfferingInput
                                          value={newServiceAddOnVariationDraft.description}
                                          onChange={(event) =>
                                            setNewServiceAddOnVariationDraft((previous) => ({
                                              ...previous,
                                              description: event.target.value,
                                            }))
                                          }
                                          placeholder="Description (optional)"
                                        />
                                        <OfferingInput
                                          value={newServiceAddOnVariationDraft.price}
                                          onChange={(event) =>
                                            setNewServiceAddOnVariationDraft((previous) => ({
                                              ...previous,
                                              price: event.target.value,
                                            }))
                                          }
                                          placeholder="Price (USD)"
                                        />
                                      </NestedOfferingCard>
                                    ) : (
                                      <NestedOfferingCard>
                                        <OfferingTopRow>
                                          <OfferingTitle>Add variation</OfferingTitle>
                                          <OfferingMetaChip>Child option</OfferingMetaChip>
                                          <RowActions>
                                            <IconActionButton
                                              type="button"
                                              onClick={() => beginCreateServiceAddOnVariation(addOn.code)}
                                              title="Add variation"
                                            >
                                              <Plus size={14} />
                                            </IconActionButton>
                                          </RowActions>
                                        </OfferingTopRow>
                                        <FieldHint>
                                          Add a priced child option under this add-on family.
                                        </FieldHint>
                                      </NestedOfferingCard>
                                    )}
                                  </NestedOfferingList>
                                </OfferingCard>
                              );
                            })}
                          </OfferingList>
                        ) : (
                          <EmptyState>
                            No add-ons configured yet. Create a parent add-on below, then attach priced child
                            variations customers can choose during checkout.
                          </EmptyState>
                        )}
                        <OfferingCard>
                          <OfferingTopRow>
                            <OfferingTitleGroup>
                              <OfferingTitle>Create add-on parent</OfferingTitle>
                            </OfferingTitleGroup>
                            <OfferingMetaChip>Parent item</OfferingMetaChip>
                            <RowActions>
                              <IconActionButton
                                type="button"
                                onClick={() => void createServiceAddOn()}
                                title="Create add-on parent"
                                disabled={serviceOfferingBusyCode === 'service-addon-new' || !newServiceAddOnName.trim()}
                              >
                                <Plus size={14} />
                              </IconActionButton>
                            </RowActions>
                          </OfferingTopRow>
                          <OfferingInput
                            value={newServiceAddOnName}
                            onChange={(event) => setNewServiceAddOnName(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                void createServiceAddOn();
                              }
                            }}
                            placeholder="Add-on parent name"
                            disabled={serviceOfferingBusyCode === 'service-addon-new'}
                          />
                          <OfferingInput
                            value={newServiceAddOnDescription}
                            onChange={(event) => setNewServiceAddOnDescription(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                void createServiceAddOn();
                              }
                            }}
                            placeholder="Description (optional)"
                            disabled={serviceOfferingBusyCode === 'service-addon-new'}
                          />
                        </OfferingCard>
                      </>
                    ) : null}
                    {servicePanelTab === 'exclusions' ? (
                      <>
                        {serviceExclusionsCatalog.length ? (
                          <OfferingList>
                            {serviceExclusionsCatalog.map((exclusion) => {
                              const includedInActiveTier = tierIncludedExclusionCodes.includes(exclusion.code);
                              return (
                                <OfferingCard
                                  key={`service-exclusion-${exclusion.code}`}
                                  $selectable={Boolean(activeTier) && tierPanelTab === 'exclusions'}
                                  $selected={includedInActiveTier}
                                  onClick={() => {
                                    if (!activeTier || tierPanelTab !== 'exclusions') return;
                                    if (editingServiceExclusionCode === exclusion.code) return;
                                    void toggleTierExclusionInclusion(exclusion.code);
                                  }}
                                >
                                  <OfferingTopRow>
                                    <OfferingTitleGroup>
                                      {editingServiceExclusionCode === exclusion.code ? (
                                        <OfferingInput
                                          value={editingServiceExclusionName}
                                          onChange={(event) => setEditingServiceExclusionName(event.target.value)}
                                          onClick={(event) => event.stopPropagation()}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                              event.preventDefault();
                                              void saveServiceExclusionEdit(exclusion.code);
                                            }
                                          }}
                                        />
                                      ) : (
                                        <OfferingTitle>{exclusion.name}</OfferingTitle>
                                      )}
                                    </OfferingTitleGroup>
                                    <OfferingMetaChip>
                                      {includedInActiveTier ? 'Applied to tier' : 'Service rule'}
                                    </OfferingMetaChip>
                                    <RowActions>
                                      <IconActionButton
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          if (editingServiceExclusionCode === exclusion.code) {
                                            void saveServiceExclusionEdit(exclusion.code);
                                            return;
                                          }
                                          startEditServiceExclusion(exclusion);
                                        }}
                                        title={
                                          editingServiceExclusionCode === exclusion.code
                                            ? 'Save exclusion'
                                            : 'Edit exclusion'
                                        }
                                        disabled={serviceExclusionBusyCode === exclusion.code}
                                      >
                                        {editingServiceExclusionCode === exclusion.code ? (
                                          <Check size={14} />
                                        ) : (
                                          <Pencil size={14} />
                                        )}
                                      </IconActionButton>
                                      <IconActionButton
                                        type="button"
                                        $danger
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void deleteServiceExclusion(exclusion.code);
                                        }}
                                        title="Delete exclusion"
                                        disabled={serviceExclusionBusyCode === exclusion.code}
                                      >
                                        <Trash2 size={14} />
                                      </IconActionButton>
                                    </RowActions>
                                  </OfferingTopRow>
                                  {editingServiceExclusionCode === exclusion.code ? (
                                    <OfferingInput
                                      value={editingServiceExclusionDescription}
                                      onChange={(event) =>
                                        setEditingServiceExclusionDescription(event.target.value)
                                      }
                                      onClick={(event) => event.stopPropagation()}
                                      placeholder="Description (optional)"
                                    />
                                  ) : (
                                    <>
                                      <OfferingDescription>{exclusion.description || '--'}</OfferingDescription>
                                      <OfferingTypeReadOnly>
                                        {includedInActiveTier
                                          ? 'Included in the active tier exclusions.'
                                          : activeTier && tierPanelTab === 'exclusions'
                                            ? 'Click to include this exclusion in the active tier.'
                                            : 'Service-level exclusion rule.'}
                                      </OfferingTypeReadOnly>
                                    </>
                                  )}
                                </OfferingCard>
                              );
                            })}
                          </OfferingList>
                        ) : (
                          <EmptyState>
                            No exclusions configured yet. Add exclusion rules here, then include them inside a
                            tier from this same panel.
                          </EmptyState>
                        )}
                        <OfferingCard>
                          <OfferingTopRow>
                            <OfferingTitleGroup>
                              <OfferingTitle>Create exclusion</OfferingTitle>
                            </OfferingTitleGroup>
                            <OfferingMetaChip>Service rule</OfferingMetaChip>
                            <RowActions>
                              <IconActionButton
                                type="button"
                                onClick={() => void createServiceExclusion()}
                                title="Create exclusion"
                                disabled={serviceExclusionBusyCode === 'new' || !newServiceExclusionName.trim()}
                              >
                                <Plus size={14} />
                              </IconActionButton>
                            </RowActions>
                          </OfferingTopRow>
                          <OfferingInput
                            value={newServiceExclusionName}
                            onChange={(event) => setNewServiceExclusionName(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                void createServiceExclusion();
                              }
                            }}
                            placeholder="Exclusion name"
                            disabled={serviceExclusionBusyCode === 'new'}
                          />
                          <OfferingInput
                            value={newServiceExclusionDescription}
                            onChange={(event) => setNewServiceExclusionDescription(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                void createServiceExclusion();
                              }
                            }}
                            placeholder="Description (optional)"
                            disabled={serviceExclusionBusyCode === 'new'}
                          />
                        </OfferingCard>
                      </>
                    ) : null}

                    {servicePanelTab === 'discounts' ? (
                      <MetaForm
                        onSubmit={(event) => {
                          event.preventDefault();
                          void saveServiceDiscounts();
                        }}
                      >
                        <MetaActionRow>
                          <Button type="button" $ghost onClick={addServiceDiscount} disabled={serviceDiscountsBusy}>
                            + Create discount
                          </Button>
                          <Button type="submit" disabled={serviceDiscountsBusy}>
                            Save discounts
                          </Button>
                        </MetaActionRow>

                        {serviceDiscountsForm.length ? (
                          <OfferingList>
                            {serviceDiscountsForm.map((discount, index) => (
                              <OfferingCard key={`service-discount-${discount.id}`}>
                                <OfferingTopRow>
                                  <OfferingTitle>{discount.name || `Discount ${index + 1}`}</OfferingTitle>
                                  <RowActions>
                                    <IconActionButton
                                      type="button"
                                      $danger
                                      onClick={() => removeServiceDiscount(discount.id)}
                                      title="Remove discount"
                                      disabled={serviceDiscountsBusy}
                                    >
                                      <Trash2 size={14} />
                                    </IconActionButton>
                                  </RowActions>
                                </OfferingTopRow>
                                <MetaGrid>
                                  <MetaField>
                                    Name
                                    <MetaInput
                                      value={discount.name}
                                      onChange={(event) =>
                                        updateServiceDiscount(discount.id, (previous) => ({
                                          ...previous,
                                          name: event.target.value,
                                          code:
                                            normalizeDiscountCode(previous.code).length > 0
                                              ? previous.code
                                              : normalizeDiscountCode(event.target.value),
                                        }))
                                      }
                                      placeholder="Spring launch"
                                      disabled={serviceDiscountsBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Discount code
                                    <MetaInput
                                      value={discount.code}
                                      onChange={(event) =>
                                        updateServiceDiscount(discount.id, (previous) => ({
                                          ...previous,
                                          code: normalizeDiscountCode(event.target.value),
                                        }))
                                      }
                                      placeholder="SPRING25"
                                      disabled={serviceDiscountsBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Value mode
                                    <MetaPicklistWrap>
                                      <GlassPicklist<DiscountMode>
                                        value={discount.mode}
                                        options={DISCOUNT_MODE_PICKLIST_OPTIONS}
                                        onChange={(nextValue) =>
                                          updateServiceDiscount(discount.id, (previous) => ({
                                            ...previous,
                                            mode: nextValue,
                                          }))
                                        }
                                        ariaLabel={`Discount value mode for ${discount.name || discount.code}`}
                                        placeholder="Select value mode"
                                        disabled={serviceDiscountsBusy}
                                      />
                                    </MetaPicklistWrap>
                                  </MetaField>
                                  {discount.mode === 'amount' ? (
                                    <MetaField>
                                      Discount value (USD)
                                      <MetaInput
                                        value={discount.amountUsd}
                                        onChange={(event) =>
                                          updateServiceDiscount(discount.id, (previous) => ({
                                            ...previous,
                                            amountUsd: event.target.value,
                                          }))
                                        }
                                        placeholder="100.00"
                                        disabled={serviceDiscountsBusy}
                                      />
                                    </MetaField>
                                  ) : null}
                                  {discount.mode === 'percent' ? (
                                    <MetaField>
                                      Discount value (%)
                                      <MetaInput
                                        value={discount.percentOff}
                                        onChange={(event) =>
                                          updateServiceDiscount(discount.id, (previous) => ({
                                            ...previous,
                                            percentOff: event.target.value,
                                          }))
                                        }
                                        placeholder="25"
                                        disabled={serviceDiscountsBusy}
                                      />
                                    </MetaField>
                                  ) : null}
                                  {discount.mode === 'duration' ? (
                                    <>
                                      <MetaField>
                                        Duration value mode
                                        <MetaPicklistWrap>
                                          <GlassPicklist<DiscountDurationValueMode>
                                            value={discount.durationValueMode}
                                            options={DISCOUNT_DURATION_VALUE_PICKLIST_OPTIONS}
                                            onChange={(nextValue) =>
                                              updateServiceDiscount(discount.id, (previous) => ({
                                                ...previous,
                                                durationValueMode: nextValue,
                                              }))
                                            }
                                            ariaLabel={`Duration value mode for ${discount.name || discount.code}`}
                                            placeholder="Select duration value mode"
                                            disabled={serviceDiscountsBusy}
                                          />
                                        </MetaPicklistWrap>
                                      </MetaField>
                                      <MetaField>
                                        Duration (months)
                                        <MetaInput
                                          value={discount.durationMonths}
                                          onChange={(event) =>
                                            updateServiceDiscount(discount.id, (previous) => ({
                                              ...previous,
                                              durationMonths: event.target.value,
                                            }))
                                          }
                                          placeholder="1"
                                          disabled={serviceDiscountsBusy}
                                        />
                                      </MetaField>
                                      {discount.durationValueMode === 'amount' ? (
                                        <MetaField>
                                          Discount value (USD)
                                          <MetaInput
                                            value={discount.durationAmountUsd}
                                            onChange={(event) =>
                                              updateServiceDiscount(discount.id, (previous) => ({
                                                ...previous,
                                                durationAmountUsd: event.target.value,
                                              }))
                                            }
                                            placeholder="500.00"
                                            disabled={serviceDiscountsBusy}
                                          />
                                        </MetaField>
                                      ) : null}
                                      {discount.durationValueMode === 'percent' ? (
                                        <MetaField>
                                          Discount value (%)
                                          <MetaInput
                                            value={discount.durationPercentOff}
                                            onChange={(event) =>
                                              updateServiceDiscount(discount.id, (previous) => ({
                                                ...previous,
                                                durationPercentOff: event.target.value,
                                              }))
                                            }
                                            placeholder="25"
                                            disabled={serviceDiscountsBusy}
                                          />
                                        </MetaField>
                                      ) : null}
                                    </>
                                  ) : null}
                                  {!discount.noExpiration ? (
                                    <MetaField>
                                      Expiration date
                                      <MetaInput
                                        type="date"
                                        value={discount.expiresAt}
                                        onChange={(event) =>
                                          updateServiceDiscount(discount.id, (previous) => ({
                                            ...previous,
                                            expiresAt: event.target.value,
                                          }))
                                        }
                                        disabled={serviceDiscountsBusy}
                                      />
                                    </MetaField>
                                  ) : null}
                                  {!discount.unlimitedInventory ? (
                                    <MetaField>
                                      Inventory limit
                                      <MetaInput
                                        value={discount.inventoryLimit}
                                        onChange={(event) =>
                                          updateServiceDiscount(discount.id, (previous) => ({
                                            ...previous,
                                            inventoryLimit: event.target.value,
                                          }))
                                        }
                                        placeholder="100"
                                        disabled={serviceDiscountsBusy}
                                      />
                                    </MetaField>
                                  ) : null}
                                  <MetaField>
                                    Uses
                                    <MetaInput value={String(discount.usageCount)} disabled />
                                  </MetaField>
                                </MetaGrid>
                                <CheckboxRow>
                                  <input
                                    type="checkbox"
                                    checked={discount.noExpiration}
                                    onChange={(event) =>
                                      updateServiceDiscount(discount.id, (previous) => ({
                                        ...previous,
                                        noExpiration: event.target.checked,
                                        expiresAt: event.target.checked ? '' : previous.expiresAt,
                                      }))
                                    }
                                    disabled={serviceDiscountsBusy}
                                  />
                                  No expiration
                                </CheckboxRow>
                                <CheckboxRow>
                                  <input
                                    type="checkbox"
                                    checked={discount.unlimitedInventory}
                                    onChange={(event) =>
                                      updateServiceDiscount(discount.id, (previous) => ({
                                        ...previous,
                                        unlimitedInventory: event.target.checked,
                                        inventoryLimit: event.target.checked ? '' : previous.inventoryLimit,
                                      }))
                                    }
                                    disabled={serviceDiscountsBusy}
                                  />
                                  Unlimited inventory
                                </CheckboxRow>
                              </OfferingCard>
                            ))}
                          </OfferingList>
                        ) : (
                          <EmptyState>No service-level discounts configured.</EmptyState>
                        )}
                      </MetaForm>
                    ) : null}

                    {servicePanelTab === 'sla' ? (
                      <MetaForm
                        onSubmit={(event) => {
                          event.preventDefault();
                          if (!serviceSlaEditing) {
                            setServiceSlaEditing(true);
                            return;
                          }
                          void saveServiceSla();
                        }}
                      >
                        <MetaActionRow>
                          <Button type="submit" disabled={serviceSlaBusy}>
                            {serviceSlaEditing ? 'Save SLA rules' : 'Edit SLA rules'}
                          </Button>
                          {serviceSlaEditing ? (
                            <Button
                              type="button"
                              $ghost
                              onClick={() => {
                                setServiceSlaEditing(false);
                                setServiceSlaBlockDrafts(readServiceSlaBlockDrafts(activeService.metadata));
                              }}
                              disabled={serviceSlaBusy}
                            >
                              Cancel
                            </Button>
                          ) : null}
                        </MetaActionRow>
                        <SubsectionTitle>SLA rule blocks</SubsectionTitle>
                        <SubsectionHint>
                          Configure parent-level SLA defaults for this service. These values apply to all child tiers unless a tier overrides them.
                        </SubsectionHint>
                        <OfferingList>
                          {renderServiceSlaBlockCard(
                            'offerings',
                            'Offerings block',
                            `${serviceOfferingsCatalog.filter((offering) => !offering.addOnEnabled).length} linked`,
                            'Sets default SLA for all standard offerings in this service. Child tiers inherit these defaults until explicitly overridden.',
                          )}
                          {renderServiceSlaBlockCard(
                            'add_ons',
                            'Add-ons block',
                            `${serviceAddOnVariationCount} linked`,
                            'Sets default SLA for all add-on offerings in this service. Child tiers inherit these defaults for add-ons.',
                          )}
                          {renderServiceSlaBlockCard(
                            'ticketing',
                            'Ticketing block',
                            'Operations',
                            'Sets default queue and escalation handling for service-level operational tickets. Child tiers can override when needed.',
                          )}
                        </OfferingList>
                      </MetaForm>
                    ) : null}

                    {servicePanelTab === 'service_team' ? (
                      <>
                        <SubsectionTitle>Service team routing (Brain authority)</SubsectionTitle>
                        <SubsectionHint>
                          This panel is read-only. Team assignment is controlled in Brain: open a node, go to Routing,
                          and assign this service or a specific tier.
                        </SubsectionHint>
                        {invalidServiceRoutingTeams.length ? (
                          <AuditWarning>
                            Ignored {invalidServiceRoutingTeams.length} Brain route
                            {invalidServiceRoutingTeams.length === 1 ? '' : 's'} for this service because the
                            routed node is missing a Team label in Brain: {' '}
                            {invalidServiceRoutingTeams
                              .map((team) => team.nodeTitle || team.nodeId)
                              .join(', ')}.
                          </AuditWarning>
                        ) : null}
                        {serviceRoutingTeams.length ? (
                          <OfferingList>
                            {serviceRoutingTeams.map((team) => {
                              const ownerLabel = team.owner || '-- Unassigned in Brain --';
                              return (
                                <OfferingCard key={`service-routing-${team.nodeId}`}>
                                  <TeamSummaryLine>{team.nodeTitle || '--'}</TeamSummaryLine>
                                  <TeamSummaryLine>{team.teamLabel || team.designationType || '--'}</TeamSummaryLine>
                                  <OwnerInline>
                                    <OwnerAvatar
                                      uid={team.ownerUid || undefined}
                                      email={team.ownerEmail || undefined}
                                      name={ownerLabel}
                                      photoURL={team.ownerAvatarUrl || undefined}
                                      size={24}
                                    />
                                    <OwnerName>{ownerLabel}</OwnerName>
                                  </OwnerInline>
                                </OfferingCard>
                              );
                            })}
                          </OfferingList>
                        ) : (
                          <EmptyState>
                            No Brain routing node is assigned to this service yet.
                          </EmptyState>
                        )}
                      </>
                    ) : null}

                    {servicePanelTab === 'settings' ? (
                      <>
                        <EmptyState>Service settings are coming next step.</EmptyState>
                        <DeleteTierZone>
                          <DeleteTierButton
                            type="button"
                            onClick={() => void deleteService()}
                            disabled={serviceDeleteBusy}
                          >
                            Delete service
                          </DeleteTierButton>
                        </DeleteTierZone>
                      </>
                    ) : null}
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <TierCardRow>
                      {visibleTiers.map((tier) => (
                        <TierSelectCard
                          key={tier.id}
                          type="button"
                          $active={tier.id === activeTierId}
                          onClick={() => setActiveTierId(tier.id)}
                        >
                          <TierCardTitle>{tier.name}</TierCardTitle>
                          <TierCardMeta>{tier.status}</TierCardMeta>
                        </TierSelectCard>
                      ))}
                      <TierAddCard
                        type="button"
                        onClick={openCreateTierModal}
                        disabled={!activeService || saving}
                      >
                        + Add tier
                      </TierAddCard>
                    </TierCardRow>
                    <PanelTabs>
                      <PanelTabButton
                        type="button"
                        $active={tierPanelTab === 'meta'}
                        onClick={() => setTierPanelTab('meta')}
                      >
                        Meta data
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={tierPanelTab === 'offerings'}
                        onClick={() => setTierPanelTab('offerings')}
                      >
                        Offerings
                      </PanelTabButton>
                        <PanelTabButton
                          type="button"
                          $active={tierPanelTab === 'add_ons'}
                          onClick={() => setTierPanelTab('add_ons')}
                        >
                          Add-ons
                        </PanelTabButton>
                        <PanelTabButton
                          type="button"
                          $active={tierPanelTab === 'exclusions'}
                          onClick={() => setTierPanelTab('exclusions')}
                        >
                          Exclusions
                        </PanelTabButton>
                        <PanelTabButton
                          type="button"
                          $active={tierPanelTab === 'discounts'}
                          onClick={() => setTierPanelTab('discounts')}
                      >
                        Discounts
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={tierPanelTab === 'sla'}
                        onClick={() => setTierPanelTab('sla')}
                      >
                        SLA
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={tierPanelTab === 'service_team'}
                        onClick={() => setTierPanelTab('service_team')}
                      >
                        Tier team
                      </PanelTabButton>
                      <PanelTabButton
                        type="button"
                        $active={tierPanelTab === 'settings'}
                        onClick={() => setTierPanelTab('settings')}
                      >
                        Settings
                      </PanelTabButton>
                    </PanelTabs>
                  </PanelHeader>
                  <PanelBody>
                    {!visibleTiers.length ? (
                      <EmptyState>No tiers found for this service.</EmptyState>
                    ) : (
                      <>
                        {!activeTier ? (
                          <EmptyState>Select a tier to view tier-specific details.</EmptyState>
                        ) : (
                          <>
                            {tierPanelTab === 'meta' ? (
                              <MetaForm
                                onSubmit={(event) => {
                                  event.preventDefault();
                                  if (!tierMetaEditing) {
                                    setTierMetaEditing(true);
                                    return;
                                  }
                                  void saveTierMeta();
                                }}
                              >
                                <MetaActionRow>
                                  <Button
                                    type="submit"
                                    disabled={tierMetaBusy}
                                  >
                                    {tierMetaEditing ? 'Save tier' : 'Edit tier'}
                                  </Button>
                                  {tierMetaEditing ? (
                                    <Button
                                      type="button"
                                      $ghost
                                      onClick={() => {
                                        setTierMetaEditing(false);
                                        setTierMetaForm(readTierMetaForm(activeTier));
                                      }}
                                      disabled={tierMetaBusy}
                                    >
                                      Cancel
                                    </Button>
                                  ) : null}
                                </MetaActionRow>

                                <MetaGrid>
                                  <MetaField>
                                    Tier name
                                    <MetaInput
                                      value={tierMetaForm.name}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({ ...previous, name: event.target.value }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    SKU ID
                                    <MetaInput
                                      value={tierMetaForm.skuId}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({ ...previous, skuId: event.target.value }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Status
                                    <MetaSelect
                                      value={tierMetaForm.status}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          status: event.target.value as CatalogStatus,
                                        }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    >
                                      {STATUS_OPTIONS.filter((status) => status !== 'archived').map((status) => (
                                        <option key={status} value={status}>
                                          {status}
                                        </option>
                                      ))}
                                    </MetaSelect>
                                  </MetaField>
                                  <MetaField>
                                    Price (USD)
                                    <MetaInput
                                      value={tierMetaForm.amountUsd}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          amountUsd: normalizeUsdInput(event.target.value),
                                        }))
                                      }
                                      onBlur={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          amountUsd: formatUsdFromCents(
                                            parseUsdToCents(event.target.value) ?? 0,
                                          ),
                                        }))
                                      }
                                      placeholder="$2500.00"
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Billing type
                                    <MetaSelect
                                      value={tierMetaForm.billingType}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          billingType: event.target.value,
                                        }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    >
                                      <option value="">--</option>
                                      <option value="recurring">recurring</option>
                                      <option value="one_time">one_time</option>
                                    </MetaSelect>
                                  </MetaField>
                                  <MetaField>
                                    Billing interval
                                    <MetaSelect
                                      value={tierMetaForm.billingInterval}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          billingInterval: event.target.value,
                                        }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    >
                                      <option value="">--</option>
                                      <option value="month">month</option>
                                    </MetaSelect>
                                  </MetaField>
                                  <MetaField>
                                    Currency
                                    <MetaInput
                                      value={tierMetaForm.currency}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({ ...previous, currency: event.target.value }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Display order
                                    <MetaInput
                                      type="number"
                                      min={0}
                                      value={tierMetaForm.sortOrder}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({ ...previous, sortOrder: event.target.value }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                </MetaGrid>

                                <MetaGrid>
                                  <MetaField>
                                    Minimum term (months)
                                    <MetaInput
                                      type="number"
                                      min={0}
                                      value={tierMetaForm.minTermMonths}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          minTermMonths: event.target.value,
                                        }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Delivery hours min
                                    <MetaInput
                                      type="number"
                                      min={0}
                                      value={tierMetaForm.deliveryHoursMin}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          deliveryHoursMin: event.target.value,
                                        }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Delivery hours max
                                    <MetaInput
                                      type="number"
                                      min={0}
                                      value={tierMetaForm.deliveryHoursMax}
                                      onChange={(event) =>
                                        setTierMetaForm((previous) => ({
                                          ...previous,
                                          deliveryHoursMax: event.target.value,
                                        }))
                                      }
                                      disabled={!tierMetaEditing || tierMetaBusy}
                                    />
                                  </MetaField>
                                  <MetaField>
                                    Active lanes included
                                    <MetaFieldInputRow>
                                      <MetaInput
                                        value={tierMetaForm.activeLanesIncluded}
                                        onChange={(event) =>
                                          setTierMetaForm((previous) => ({
                                            ...previous,
                                            activeLanesIncluded: event.target.value,
                                          }))
                                        }
                                        placeholder="Example: 3 or All"
                                        disabled={!tierMetaEditing || tierMetaBusy}
                                      />
                                      <MetaQuickSetButton
                                        type="button"
                                        onClick={() =>
                                          setTierMetaForm((previous) => ({
                                            ...previous,
                                            activeLanesIncluded: 'all',
                                          }))
                                        }
                                        disabled={!tierMetaEditing || tierMetaBusy}
                                      >
                                        All
                                      </MetaQuickSetButton>
                                    </MetaFieldInputRow>
                                  </MetaField>
                                  <MetaField>
                                    Advanced lanes access
                                    <MetaFieldInputRow>
                                      <MetaInput
                                        value={tierMetaForm.advancedLanesAccess}
                                        onChange={(event) =>
                                          setTierMetaForm((previous) => ({
                                            ...previous,
                                            advancedLanesAccess: event.target.value,
                                          }))
                                        }
                                        disabled={!tierMetaEditing || tierMetaBusy}
                                      />
                                      <MetaQuickSetButton
                                        type="button"
                                        onClick={() =>
                                          setTierMetaForm((previous) => ({
                                            ...previous,
                                            advancedLanesAccess: 'all',
                                          }))
                                        }
                                        disabled={!tierMetaEditing || tierMetaBusy}
                                      >
                                        All
                                      </MetaQuickSetButton>
                                    </MetaFieldInputRow>
                                  </MetaField>
                                  <MetaField>
                                    Specialty lanes access
                                    <MetaFieldInputRow>
                                      <MetaInput
                                        value={tierMetaForm.specialtyLanesAccess}
                                        onChange={(event) =>
                                          setTierMetaForm((previous) => ({
                                            ...previous,
                                            specialtyLanesAccess: event.target.value,
                                          }))
                                        }
                                        disabled={!tierMetaEditing || tierMetaBusy}
                                      />
                                      <MetaQuickSetButton
                                        type="button"
                                        onClick={() =>
                                          setTierMetaForm((previous) => ({
                                            ...previous,
                                            specialtyLanesAccess: 'all',
                                          }))
                                        }
                                        disabled={!tierMetaEditing || tierMetaBusy}
                                      >
                                        All
                                      </MetaQuickSetButton>
                                    </MetaFieldInputRow>
                                  </MetaField>
                                </MetaGrid>

                                <CheckboxRow>
                                  <input
                                    type="checkbox"
                                    checked={tierMetaForm.isPrimaryService}
                                    onChange={(event) =>
                                      setTierMetaForm((previous) => ({
                                        ...previous,
                                        isPrimaryService: event.target.checked,
                                      }))
                                    }
                                    disabled={!tierMetaEditing || tierMetaBusy}
                                  />
                                  Primary service tier
                                </CheckboxRow>

                                <MetaField>
                                  Description
                                  <MetaTextarea
                                    value={tierMetaForm.description}
                                    onChange={(event) =>
                                      setTierMetaForm((previous) => ({
                                        ...previous,
                                        description: event.target.value,
                                      }))
                                    }
                                    disabled={!tierMetaEditing || tierMetaBusy}
                                  />
                                </MetaField>
                                <MetaField>
                                  Client success cadence
                                  <FieldHint>
                                    Sets the rhythm of client-success check-ins for this tier.
                                  </FieldHint>
                                  <MetaSelect
                                    value={tierMetaForm.clientSuccessCadenceType}
                                    onChange={(event) =>
                                      setTierMetaForm((previous) => ({
                                        ...previous,
                                        clientSuccessCadenceType:
                                          event.target.value as ClientSuccessCadenceType,
                                      }))
                                    }
                                    disabled={!tierMetaEditing || tierMetaBusy}
                                  >
                                    {CLIENT_SUCCESS_CADENCE_OPTIONS.map((cadence) => (
                                      <option key={cadence} value={cadence}>
                                        {cadence}
                                      </option>
                                    ))}
                                  </MetaSelect>
                                </MetaField>
                                <MetaField>
                                  Client success meetings / month
                                  <FieldHint>
                                    Sets the numeric monthly meeting commitment tracked for this tier.
                                  </FieldHint>
                                  <MetaInput
                                    type="number"
                                    min={0}
                                    value={tierMetaForm.clientSuccessMeetingsPerMonth}
                                    onChange={(event) =>
                                      setTierMetaForm((previous) => ({
                                        ...previous,
                                        clientSuccessMeetingsPerMonth: event.target.value,
                                      }))
                                    }
                                    placeholder="2"
                                    disabled={!tierMetaEditing || tierMetaBusy}
                                  />
                                </MetaField>
                              </MetaForm>
                            ) : null}
                            {tierPanelTab === 'offerings' ? (
                              serviceOfferingsCatalog.some(
                                (offering) =>
                                  offering.status !== 'archived' &&
                                  tierIncludedOfferingCodes.includes(offering.code),
                              ) ? (
                                <>
                                  <SubsectionTitle>Tier offerings</SubsectionTitle>
                                  <SubsectionHint>
                                    Showing offerings currently included in this tier.
                                  </SubsectionHint>
                                  <OfferingList>
                                    {serviceOfferingsCatalog
                                      .filter(
                                        (offering) =>
                                          offering.status !== 'archived' &&
                                          tierIncludedOfferingCodes.includes(offering.code),
                                      )
                                      .map((offering) => {
                                        const tierOfferingEditing =
                                          editingTierOfferingCode === offering.code;
                                        return (
                                          <OfferingCard key={`${activeTier.id}-${offering.code}`}>
                                            <OfferingTopRow>
                                              <OfferingTitle>{offering.name}</OfferingTitle>
                                              <RowActions>
                                                <IconActionButton
                                                  type="button"
                                                  onClick={() =>
                                                    setEditingTierOfferingCode((previous) =>
                                                      previous === offering.code ? null : offering.code,
                                                    )
                                                  }
                                                  title={tierOfferingEditing ? 'Collapse offering' : 'Edit offering'}
                                                  disabled={tierOfferingBusyCode === offering.code}
                                                >
                                                  {tierOfferingEditing ? <Check size={14} /> : <Pencil size={14} />}
                                                </IconActionButton>
                                              </RowActions>
                                            </OfferingTopRow>
                                            <OfferingDescription>{offering.description || '--'}</OfferingDescription>
                                            <OfferingTypeReadOnly>
                                              Offering type:{' '}
                                              {formatTokenLabel(
                                                normalizeServiceOfferingType(offering.type),
                                              )}
                                            </OfferingTypeReadOnly>
                                            {tierOfferingEditing ? (
                                              <>
                                                <MetaField>
                                                  Tier incentive / notes
                                                  <MetaInput
                                                    defaultValue={tierOfferingOverrides[offering.code]?.incentive ?? ''}
                                                    placeholder="Optional tier-specific incentive or note"
                                                    disabled={tierOfferingBusyCode === offering.code}
                                                    onBlur={(event) =>
                                                      void updateTierOfferingOverride(
                                                        offering.code,
                                                        { incentive: event.target.value },
                                                        'Tier offering incentive updated.',
                                                      )
                                                    }
                                                  />
                                                </MetaField>
                                              </>
                                            ) : null}
                                          </OfferingCard>
                                        );
                                      })}
                                  </OfferingList>
                                </>
                              ) : (
                                <EmptyState>
                                  No offerings are included in this tier yet. Go to Service {`>`} Offerings and
                                  click an offering once to include it.
                                </EmptyState>
                              )
                            ) : null}
                            {tierPanelTab === 'add_ons' ? (
                              <>
                                <SubsectionTitle>Tier-specific add-ons</SubsectionTitle>
                                <SubsectionHint>
                                  Create add-ons that exist only on this tier. Use these when the add-on should not
                                  live in the shared service add-on catalog.
                                </SubsectionHint>
                                {tierAddOnSettings.customAddOns.length ? (
                                  <OfferingList>
                                    {tierAddOnSettings.customAddOns.map((addOn) => {
                                      const isEditing = editingTierCustomAddOnCode === addOn.code;
                                      const busyCode = `tier-custom-${addOn.code}`;
                                      return (
                                        <OfferingCard key={`${activeTier.id}-${addOn.code}`} $selected>
                                          <OfferingTopRow>
                                            <OfferingTitleGroup>
                                              {isEditing ? (
                                                <OfferingInput
                                                  value={editingTierCustomAddOnName}
                                                  onChange={(event) =>
                                                    setEditingTierCustomAddOnName(event.target.value)
                                                  }
                                                  onClick={(event) => event.stopPropagation()}
                                                  onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                      event.preventDefault();
                                                      void saveTierCustomAddOnEdit(addOn.code);
                                                    }
                                                  }}
                                                />
                                              ) : (
                                                <OfferingTitle>{addOn.name}</OfferingTitle>
                                              )}
                                            </OfferingTitleGroup>
                                            <OfferingMetaChip>
                                              {addOn.unitPriceCents === null
                                                ? 'Price unset'
                                                : formatUsdFromCents(addOn.unitPriceCents)}
                                            </OfferingMetaChip>
                                            <RowActions>
                                              <IconActionButton
                                                type="button"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  if (isEditing) {
                                                    void saveTierCustomAddOnEdit(addOn.code);
                                                    return;
                                                  }
                                                  startEditTierCustomAddOn(addOn);
                                                }}
                                                title={isEditing ? 'Save add-on' : 'Edit add-on'}
                                                disabled={tierOfferingBusyCode === busyCode}
                                              >
                                                {isEditing ? <Check size={14} /> : <Pencil size={14} />}
                                              </IconActionButton>
                                              <IconActionButton
                                                type="button"
                                                $danger
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  void deleteTierCustomAddOn(addOn.code);
                                                }}
                                                title="Delete add-on"
                                                disabled={tierOfferingBusyCode === busyCode}
                                              >
                                                <Trash2 size={14} />
                                              </IconActionButton>
                                            </RowActions>
                                          </OfferingTopRow>
                                          {isEditing ? (
                                            <>
                                              <OfferingInput
                                                value={editingTierCustomAddOnDescription}
                                                onChange={(event) =>
                                                  setEditingTierCustomAddOnDescription(event.target.value)
                                                }
                                                onClick={(event) => event.stopPropagation()}
                                                placeholder="Description (optional)"
                                              />
                                              <OfferingInput
                                                value={editingTierCustomAddOnPrice}
                                                onChange={(event) =>
                                                  setEditingTierCustomAddOnPrice(event.target.value)
                                                }
                                                onClick={(event) => event.stopPropagation()}
                                                placeholder="Add-on price (USD)"
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <OfferingDescription>{addOn.description || '--'}</OfferingDescription>
                                              <FieldHint>
                                                {addOn.unitPriceCents === null
                                                  ? 'Set a price before using this tier-specific add-on in checkout.'
                                                  : `Checkout price ${formatUsdFromCents(addOn.unitPriceCents)}`}
                                              </FieldHint>
                                            </>
                                          )}
                                        </OfferingCard>
                                      );
                                    })}
                                  </OfferingList>
                                ) : (
                                  <EmptyState>
                                    No tier-specific add-ons yet. Create one below if this tier needs custom upsells
                                    that should not be shared across the entire service.
                                  </EmptyState>
                                )}
                                <OfferingCard>
                                  <OfferingTopRow>
                                    <OfferingTitleGroup>
                                      <OfferingTitle>Create tier-specific add-on</OfferingTitle>
                                    </OfferingTitleGroup>
                                    <OfferingMetaChip>Priced entry</OfferingMetaChip>
                                    <RowActions>
                                      <IconActionButton
                                        type="button"
                                        onClick={() => void createTierCustomAddOn()}
                                        title="Create tier-specific add-on"
                                        disabled={
                                          tierOfferingBusyCode === 'tier-custom-new' ||
                                          !newTierCustomAddOnName.trim() ||
                                          !newTierCustomAddOnPrice.trim()
                                        }
                                      >
                                        <Plus size={14} />
                                      </IconActionButton>
                                    </RowActions>
                                  </OfferingTopRow>
                                  <OfferingInput
                                    value={newTierCustomAddOnName}
                                    onChange={(event) => setNewTierCustomAddOnName(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        void createTierCustomAddOn();
                                      }
                                    }}
                                    placeholder="Add-on name"
                                    disabled={tierOfferingBusyCode === 'tier-custom-new'}
                                  />
                                  <OfferingInput
                                    value={newTierCustomAddOnDescription}
                                    onChange={(event) => setNewTierCustomAddOnDescription(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        void createTierCustomAddOn();
                                      }
                                    }}
                                    placeholder="Description (optional)"
                                    disabled={tierOfferingBusyCode === 'tier-custom-new'}
                                  />
                                  <OfferingInput
                                    value={newTierCustomAddOnPrice}
                                    onChange={(event) => setNewTierCustomAddOnPrice(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        void createTierCustomAddOn();
                                      }
                                    }}
                                    placeholder="Price (USD)"
                                    disabled={tierOfferingBusyCode === 'tier-custom-new'}
                                  />
                                </OfferingCard>
                              </>
                            ) : null}
                            {tierPanelTab === 'exclusions' ? (
                              <>
                                <SubsectionTitle>Tier exclusions</SubsectionTitle>
                                <SubsectionHint>
                                  Select exclusions from the service catalog. Included exclusions appear as active
                                  cards here and in the service column.
                                </SubsectionHint>
                                {serviceExclusionsCatalog.filter((exclusion) =>
                                  tierIncludedExclusionCodes.includes(exclusion.code),
                                ).length ? (
                                  <OfferingList>
                                    {serviceExclusionsCatalog
                                      .filter((exclusion) => tierIncludedExclusionCodes.includes(exclusion.code))
                                      .map((exclusion) => (
                                        <OfferingCard key={`${activeTier.id}-${exclusion.code}`} $selected>
                                          <OfferingTopRow>
                                            <OfferingTitle>{exclusion.name}</OfferingTitle>
                                            <OfferingMetaChip>Applied</OfferingMetaChip>
                                            <RowActions>
                                              <IconActionButton
                                                type="button"
                                                $danger
                                                onClick={() => void toggleTierExclusionInclusion(exclusion.code)}
                                                title="Remove exclusion"
                                                disabled={tierExclusionBusyCode === exclusion.code}
                                              >
                                                <Trash2 size={14} />
                                              </IconActionButton>
                                            </RowActions>
                                          </OfferingTopRow>
                                          <OfferingDescription>{exclusion.description || '--'}</OfferingDescription>
                                          <OfferingTypeReadOnly>
                                            Exclusion rule applied to this tier.
                                          </OfferingTypeReadOnly>
                                        </OfferingCard>
                                      ))}
                                  </OfferingList>
                                ) : (
                                  <EmptyState>
                                    No exclusions are applied to this tier yet. Go to Service {`>`} Exclusions and
                                    click an exclusion once to include it.
                                  </EmptyState>
                                )}
                              </>
                            ) : null}
                            {tierPanelTab === 'discounts' ? (
                              <MetaForm
                                onSubmit={(event) => {
                                  event.preventDefault();
                                  void saveTierDiscounts();
                                }}
                              >
                                <MetaActionRow>
                                  <Button type="button" $ghost onClick={addTierDiscount} disabled={tierDiscountsBusy}>
                                    + Create discount
                                  </Button>
                                  <Button type="submit" disabled={tierDiscountsBusy}>
                                    Save discounts
                                  </Button>
                                </MetaActionRow>

                                {tierDiscountsForm.length ? (
                                  <OfferingList>
                                    {tierDiscountsForm.map((discount, index) => (
                                      <OfferingCard key={`tier-discount-${discount.id}`}>
                                        <OfferingTopRow>
                                          <OfferingTitle>{discount.name || `Discount ${index + 1}`}</OfferingTitle>
                                          <RowActions>
                                            <IconActionButton
                                              type="button"
                                              $danger
                                              onClick={() => removeTierDiscount(discount.id)}
                                              title="Remove discount"
                                              disabled={tierDiscountsBusy}
                                            >
                                              <Trash2 size={14} />
                                            </IconActionButton>
                                          </RowActions>
                                        </OfferingTopRow>
                                        <MetaGrid>
                                          <MetaField>
                                            Name
                                            <MetaInput
                                              value={discount.name}
                                              onChange={(event) =>
                                                updateTierDiscount(discount.id, (previous) => ({
                                                  ...previous,
                                                  name: event.target.value,
                                                  code:
                                                    normalizeDiscountCode(previous.code).length > 0
                                                      ? previous.code
                                                      : normalizeDiscountCode(event.target.value),
                                                }))
                                              }
                                              placeholder="Silver launch"
                                              disabled={tierDiscountsBusy}
                                            />
                                          </MetaField>
                                          <MetaField>
                                            Discount code
                                            <MetaInput
                                              value={discount.code}
                                              onChange={(event) =>
                                                updateTierDiscount(discount.id, (previous) => ({
                                                  ...previous,
                                                  code: normalizeDiscountCode(event.target.value),
                                                }))
                                              }
                                              placeholder="SILVER10"
                                              disabled={tierDiscountsBusy}
                                            />
                                          </MetaField>
                                          <MetaField>
                                            Value mode
                                            <MetaPicklistWrap>
                                              <GlassPicklist<DiscountMode>
                                                value={discount.mode}
                                                options={DISCOUNT_MODE_PICKLIST_OPTIONS}
                                                onChange={(nextValue) =>
                                                  updateTierDiscount(discount.id, (previous) => ({
                                                    ...previous,
                                                    mode: nextValue,
                                                  }))
                                                }
                                                ariaLabel={`Discount value mode for ${discount.name || discount.code}`}
                                                placeholder="Select value mode"
                                                disabled={tierDiscountsBusy}
                                              />
                                            </MetaPicklistWrap>
                                          </MetaField>
                                          {discount.mode === 'amount' ? (
                                            <MetaField>
                                              Discount value (USD)
                                              <MetaInput
                                                value={discount.amountUsd}
                                                onChange={(event) =>
                                                  updateTierDiscount(discount.id, (previous) => ({
                                                    ...previous,
                                                    amountUsd: event.target.value,
                                                  }))
                                                }
                                                placeholder="100.00"
                                                disabled={tierDiscountsBusy}
                                              />
                                            </MetaField>
                                          ) : null}
                                          {discount.mode === 'percent' ? (
                                            <MetaField>
                                              Discount value (%)
                                              <MetaInput
                                                value={discount.percentOff}
                                                onChange={(event) =>
                                                  updateTierDiscount(discount.id, (previous) => ({
                                                    ...previous,
                                                    percentOff: event.target.value,
                                                  }))
                                                }
                                                placeholder="20"
                                                disabled={tierDiscountsBusy}
                                              />
                                            </MetaField>
                                          ) : null}
                                          {discount.mode === 'duration' ? (
                                            <>
                                              <MetaField>
                                                Duration value mode
                                                <MetaPicklistWrap>
                                                  <GlassPicklist<DiscountDurationValueMode>
                                                    value={discount.durationValueMode}
                                                    options={DISCOUNT_DURATION_VALUE_PICKLIST_OPTIONS}
                                                    onChange={(nextValue) =>
                                                      updateTierDiscount(discount.id, (previous) => ({
                                                        ...previous,
                                                        durationValueMode: nextValue,
                                                      }))
                                                    }
                                                    ariaLabel={`Duration value mode for ${discount.name || discount.code}`}
                                                    placeholder="Select duration value mode"
                                                    disabled={tierDiscountsBusy}
                                                  />
                                                </MetaPicklistWrap>
                                              </MetaField>
                                              <MetaField>
                                                Duration (months)
                                                <MetaInput
                                                  value={discount.durationMonths}
                                                  onChange={(event) =>
                                                    updateTierDiscount(discount.id, (previous) => ({
                                                      ...previous,
                                                      durationMonths: event.target.value,
                                                    }))
                                                  }
                                                  placeholder="1"
                                                  disabled={tierDiscountsBusy}
                                                />
                                              </MetaField>
                                              {discount.durationValueMode === 'amount' ? (
                                                <MetaField>
                                                  Discount value (USD)
                                                  <MetaInput
                                                    value={discount.durationAmountUsd}
                                                    onChange={(event) =>
                                                      updateTierDiscount(discount.id, (previous) => ({
                                                        ...previous,
                                                        durationAmountUsd: event.target.value,
                                                      }))
                                                    }
                                                    placeholder="500.00"
                                                    disabled={tierDiscountsBusy}
                                                  />
                                                </MetaField>
                                              ) : null}
                                              {discount.durationValueMode === 'percent' ? (
                                                <MetaField>
                                                  Discount value (%)
                                                  <MetaInput
                                                    value={discount.durationPercentOff}
                                                    onChange={(event) =>
                                                      updateTierDiscount(discount.id, (previous) => ({
                                                        ...previous,
                                                        durationPercentOff: event.target.value,
                                                      }))
                                                    }
                                                    placeholder="25"
                                                    disabled={tierDiscountsBusy}
                                                  />
                                                </MetaField>
                                              ) : null}
                                            </>
                                          ) : null}
                                          {!discount.noExpiration ? (
                                            <MetaField>
                                              Expiration date
                                              <MetaInput
                                                type="date"
                                                value={discount.expiresAt}
                                                onChange={(event) =>
                                                  updateTierDiscount(discount.id, (previous) => ({
                                                    ...previous,
                                                    expiresAt: event.target.value,
                                                  }))
                                                }
                                                disabled={tierDiscountsBusy}
                                              />
                                            </MetaField>
                                          ) : null}
                                          {!discount.unlimitedInventory ? (
                                            <MetaField>
                                              Inventory limit
                                              <MetaInput
                                                value={discount.inventoryLimit}
                                                onChange={(event) =>
                                                  updateTierDiscount(discount.id, (previous) => ({
                                                    ...previous,
                                                    inventoryLimit: event.target.value,
                                                  }))
                                                }
                                                placeholder="100"
                                                disabled={tierDiscountsBusy}
                                              />
                                            </MetaField>
                                          ) : null}
                                          <MetaField>
                                            Uses
                                            <MetaInput value={String(discount.usageCount)} disabled />
                                          </MetaField>
                                        </MetaGrid>
                                        <CheckboxRow>
                                          <input
                                            type="checkbox"
                                            checked={discount.noExpiration}
                                            onChange={(event) =>
                                              updateTierDiscount(discount.id, (previous) => ({
                                                ...previous,
                                                noExpiration: event.target.checked,
                                                expiresAt: event.target.checked ? '' : previous.expiresAt,
                                              }))
                                            }
                                            disabled={tierDiscountsBusy}
                                          />
                                          No expiration
                                        </CheckboxRow>
                                        <CheckboxRow>
                                          <input
                                            type="checkbox"
                                            checked={discount.unlimitedInventory}
                                            onChange={(event) =>
                                              updateTierDiscount(discount.id, (previous) => ({
                                                ...previous,
                                                unlimitedInventory: event.target.checked,
                                                inventoryLimit: event.target.checked ? '' : previous.inventoryLimit,
                                              }))
                                            }
                                            disabled={tierDiscountsBusy}
                                          />
                                          Unlimited inventory
                                        </CheckboxRow>
                                      </OfferingCard>
                                    ))}
                                  </OfferingList>
                                ) : (
                                  <EmptyState>No tier-level discounts configured.</EmptyState>
                                )}
                              </MetaForm>
                            ) : null}
                            {tierPanelTab === 'sla' ? (
                              <MetaForm
                                onSubmit={(event) => {
                                  event.preventDefault();
                                  if (!tierSlaEditing) {
                                    setTierSlaEditing(true);
                                    return;
                                  }
                                  void saveTierSlaBlocks();
                                }}
                              >
                                <MetaActionRow>
                                  <Button type="submit" disabled={Boolean(tierSlaBusyCode)}>
                                    {tierSlaEditing ? 'Save SLA rules' : 'Edit SLA rules'}
                                  </Button>
                                  {tierSlaEditing ? (
                                    <Button
                                      type="button"
                                      $ghost
                                      onClick={() => {
                                        setTierSlaBlockDrafts(buildTierSlaBlockDrafts());
                                        setTierSlaEditing(false);
                                      }}
                                      disabled={Boolean(tierSlaBusyCode)}
                                    >
                                      Cancel
                                    </Button>
                                  ) : null}
                                </MetaActionRow>
                                <SubsectionTitle>SLA rule blocks</SubsectionTitle>
                                <SubsectionHint>
                                  Configure SLA rules by block. Offerings applies to standard included offerings; Add-ons applies to included add-on offerings; Ticketing covers queue-level handling.
                                </SubsectionHint>
                                <OfferingList>
                                  {renderTierSlaBlockCard(
                                    'offerings',
                                    'Offerings block',
                                    `${
                                      (
                                        tierStandardOfferingCodes.length
                                          ? tierStandardOfferingCodes
                                          : tierIncludedOfferingCodes
                                      ).length
                                    } linked`,
                                    'Sets default SLA for all standard offerings included in this tier. Saving applies these values to every linked offering in this block.',
                                  )}
                                  {renderTierSlaBlockCard(
                                    'add_ons',
                                    'Add-ons block',
                                    `${tierAddOnOfferingCodes.length} linked`,
                                    tierAddOnOfferingCodes.length
                                      ? 'Sets default SLA for included add-ons only. Use this when add-ons should have different response/resolution targets than core delivery lanes.'
                                      : 'Sets default SLA for included add-ons only. No add-on offerings are included yet. These rules will apply when add-ons are included.',
                                  )}
                                  {renderTierSlaBlockCard(
                                    'ticketing',
                                    'Ticketing block',
                                    'Operations',
                                    'Defines tier-level queue/ticket SLA behavior for operational requests. This is saved as ticketing policy for the tier.',
                                  )}
                                </OfferingList>
                              </MetaForm>
                            ) : null}

                            {tierPanelTab === 'service_team' ? (
                              <>
                                <SubsectionTitle>Tier team routing (Brain authority)</SubsectionTitle>
                                <SubsectionHint>
                                  This panel is read-only. Tier-specific routing is configured in Brain. If no tier
                                  route exists, this tier inherits the service-level route.
                                </SubsectionHint>
                                {tierSpecificRoutingTeams.length ? (
                                  <SubsectionHint>Source: tier-specific Brain routing.</SubsectionHint>
                                ) : (
                                  <SubsectionHint>Source: inherited service-level Brain routing.</SubsectionHint>
                                )}
                                {invalidTierRoutingTeams.length ? (
                                  <AuditWarning>
                                    Ignored {invalidTierRoutingTeams.length} tier-specific Brain route
                                    {invalidTierRoutingTeams.length === 1 ? '' : 's'} because the routed
                                    node is missing a Team label in Brain: {' '}
                                    {invalidTierRoutingTeams
                                      .map((team) => team.nodeTitle || team.nodeId)
                                      .join(', ')}.
                                  </AuditWarning>
                                ) : null}
                                {!tierSpecificRoutingTeams.length && invalidServiceRoutingTeams.length ? (
                                  <AuditWarning>
                                    Ignored {invalidServiceRoutingTeams.length} inherited service-level Brain route
                                    {invalidServiceRoutingTeams.length === 1 ? '' : 's'} because the routed
                                    node is missing a Team label in Brain: {' '}
                                    {invalidServiceRoutingTeams
                                      .map((team) => team.nodeTitle || team.nodeId)
                                      .join(', ')}.
                                  </AuditWarning>
                                ) : null}
                                {effectiveTierRoutingTeams.length ? (
                                  <OfferingList>
                                    {effectiveTierRoutingTeams.map((team) => {
                                      const ownerLabel = team.owner || '-- Unassigned in Brain --';
                                      return (
                                        <OfferingCard key={`tier-routing-${team.nodeId}`}>
                                          <TeamSummaryLine>{team.nodeTitle || '--'}</TeamSummaryLine>
                                          <TeamSummaryLine>{team.teamLabel || team.designationType || '--'}</TeamSummaryLine>
                                          <OwnerInline>
                                            <OwnerAvatar
                                              uid={team.ownerUid || undefined}
                                              email={team.ownerEmail || undefined}
                                              name={ownerLabel}
                                              photoURL={team.ownerAvatarUrl || undefined}
                                              size={24}
                                            />
                                            <OwnerName>{ownerLabel}</OwnerName>
                                          </OwnerInline>
                                        </OfferingCard>
                                      );
                                    })}
                                  </OfferingList>
                                ) : (
                                  <EmptyState>
                                    No Brain routing node is assigned for this tier or service yet.
                                  </EmptyState>
                                )}
                              </>
                            ) : null}
                            {tierPanelTab === 'settings' ? (
                              <>
                                <EmptyState>Tier settings are coming next step.</EmptyState>
                                <DeleteTierZone>
                                  <DeleteTierButton
                                    type="button"
                                    onClick={() => void deleteTier()}
                                    disabled={tierDeleteBusy}
                                  >
                                    Delete tier
                                  </DeleteTierButton>
                                </DeleteTierZone>
                              </>
                            ) : null}
                          </>
                        )}
                      </>
                    )}
                  </PanelBody>
                </Panel>
              </SplitColumns>
            </MainPane>
          </Workspace>
        ) : null}
      </Shell>
      {showCreateTierComposer ? (
        <>
          <TierModalBackdrop
            type="button"
            $open={showCreateTierComposer}
            aria-label="Close create tier modal"
            onClick={closeCreateTierModal}
          />
          <TierModal
            $open={showCreateTierComposer}
            role="dialog"
            aria-modal="true"
            aria-label="Create tier"
          >
            <TierModalHeader>
              <div>
                <TierModalTitle>Create tier</TierModalTitle>
                <TierModalSub>
                  {activeService ? `Add a tier to ${activeService.title}` : 'Add a tier'}
                </TierModalSub>
              </div>
              <Button type="button" $ghost onClick={closeCreateTierModal} disabled={saving}>
                Close
              </Button>
            </TierModalHeader>
            <TierModalBody>
              <Composer onSubmit={onCreateTier}>
                <FormGrid>
                  <Label>
                    Tier name
                    <Input
                      value={createTierForm.name}
                      onChange={(event) =>
                        setCreateTierForm((previous) => ({ ...previous, name: event.target.value }))
                      }
                      placeholder="Managed Marketing Basic"
                    />
                  </Label>
                  <Label>
                    SKU ID
                    <Input
                      value={createTierForm.skuId}
                      onChange={(event) =>
                        setCreateTierForm((previous) => ({ ...previous, skuId: event.target.value }))
                      }
                      placeholder="marketing-basic"
                    />
                  </Label>
                  <Label>
                    Status
                    <GlassPicklist<CatalogStatus>
                      value={createTierForm.status}
                      options={STATUS_OPTIONS.filter((status) => status !== 'archived').map((status) => ({
                        value: status,
                        label: status,
                      }))}
                      onChange={(nextValue) =>
                        setCreateTierForm((previous) => ({
                          ...previous,
                          status: nextValue,
                        }))
                      }
                      ariaLabel="Create tier status"
                      placeholder="Select status"
                    />
                  </Label>
                  <Label>
                    Billing type
                    <GlassPicklist<'recurring' | 'one_time'>
                      value={createTierForm.billingType}
                      options={[
                        { value: 'recurring', label: 'Recurring' },
                        { value: 'one_time', label: 'One time' },
                      ]}
                      onChange={(nextValue) =>
                        setCreateTierForm((previous) => ({
                          ...previous,
                          billingType: nextValue,
                        }))
                      }
                      ariaLabel="Create tier billing type"
                      placeholder="Select billing type"
                    />
                  </Label>
                </FormGrid>
                <HeaderButtons>
                  <Button type="submit" disabled={saving || !activeService}>
                    Create tier
                  </Button>
                  <Button type="button" $ghost onClick={closeCreateTierModal} disabled={saving}>
                    Cancel
                  </Button>
                </HeaderButtons>
              </Composer>
            </TierModalBody>
          </TierModal>
        </>
      ) : null}
    </Page>
  );
}

